import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Tooltip, useMediaQuery } from "@mui/material";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import { addToCart, openCart, openSubscriptionCheckout } from "@store/reducers/cart";

import { bannerSurface } from "@utils/bannerGradient";
import { recordMediaEvent } from "@utils/recordMediaEvent";
import styled, { keyframes } from "styled-components";

import { AnimatedBannerList, type BannerPresenceItem } from "@/components/AnimatedBannerList";
import { ButtonLabel } from "@/components/ButtonGlyph";
import { ScrollChevron, ScrollHint, useScrollAffordance } from "@/components/CartModal/phoneTemplate";
import LicenseModal from "@/components/LicenseModal";
import ModalScrollArea from "@/components/LicenseModal/ModalScrollArea";
import {
  AnnualTotal,
  BillingLabel,
  BillingSwitch,
  BillingToggleWrap,
  CheckIcon as LicenseCheckIcon,
  CloseButton as LicenseCloseButton,
  ModalContainer as LicenseModalContainer,
  ModalContent as LicenseModalContent,
  ModalHead as LicenseModalHead,
  ModalHeading,
  Overlay as LicenseOverlay,
  PlanButton,
  PlanColumn,
  PlanIcon,
  PlanPrice,
  PlansGrid,
  PlansMainRow,
  PlanTitle,
  PriceLead,
  PriceUnit,
  SubFeature,
  SubFeatureList,
  WarningIcon,
  WhitelistingBtn,
  WhitelistingStepper,
  WhitelistingText,
} from "@/components/LicenseModal/styles";
import { AccountType, useAccountType } from "@/contexts/AccountTypeContext";
import { setActionRequiredCount } from "@/hooks/useActionRequiredCount";
import { useModalAnimation } from "@/hooks/useModalAnimation";
import { useSubscriptionConfig } from "@/hooks/useSubscriptionConfig";
import LicenceCertificateModal from "@/pages/AccountLicensesPage/LicenceCertificateModal";
import { ManageAddonsModal } from "@/pages/AccountSubscription";
import {
  getMyClaims,
  hideClaim,
  hidePendingSubmission,
  markClaimsViewed,
  type MyClaimDto,
  parseClaimSubmissionError,
  queueClaimForNextCycle,
  removePendingSubmission,
  resolveClaimVideo,
  type ResolvedVideoDto,
  resubmitPending,
  submitClaimUrl,
  unhideClaim,
  unhidePendingSubmission,
} from "@/services/claimSubmissions.service";
import {
  type ChannelSlot,
  connectUserChannel,
  disconnectUserChannel,
  getMyChannelCredits,
  parseChannelError,
  type ResolvedChannelDto,
  resolveUserChannel,
} from "@/services/userChannels.service";
import * as S from "@/styles/account-purchases";
import { colors, components, fonts, fontSizes, radius } from "@/styles/design-system";
import { CREATOR_CREDIT_TEXT } from "@/utils/creatorCreditText";
import { paidCreditLine } from "@/utils/creditText";
import { computeSubscriptionPricing, singleLicensePriceForTier } from "@/utils/pricing";

import MusicUseInviteBanner from "./MusicUseInviteBanner";
import {
  ActionBtn,
  CreditCopyBtn,
  CreditTextBox,
  ErrorIdLabel,
  FreeHintBox,
  SearchInput,
  SectionHeading,
  SubmissionCard,
  SubmissionInfo,
  SubmissionMeta,
  SubmissionStatusBadge,
  SubmissionTitle,
  VideoThumb,
} from "./styles";
import { useClaimsRefreshBanner } from "./useClaimsRefreshBanner";

/* ══════════════════════════════════════
   AUTO-RESOLVE CONFIRMATION MODAL
   ══════════════════════════════════════ */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(24px) scale(0.97); }
`;

// Spinning indicator shown inside the Resolve button while the resolve
// transition is animating (after the brief "Claim resolved!" success
// state). Same recipe used in checkout buttons — consistent across the app.
/* When a card's verdict is being recomputed, refresh the list on this schedule
   rather than once. The runner is a few seconds on a good day and longer when
   the GPT music-use call runs, so any single fetch is a guess about when it
   finished — and guessing wrong leaves the card stuck on "Under Review" until
   the customer reloads. Front-loaded, then spreading out, so a fast verdict
   appears at once without polling for half a minute in the common case. */
const RECHECK_POLL_MS = [0, 2500, 5000, 9000, 14000, 20000, 28000];

const spinAnim = keyframes`to { transform: rotate(360deg); }`;
const ResolveSpinner = styled.span`
  display: inline-block; width: 12px; height: 12px;
  border: 2px solid currentColor; border-top-color: transparent;
  border-radius: 50%;
  animation: ${spinAnim} 0.7s linear infinite;
  flex-shrink: 0;
`;

/** Inline YouTube glyph — used as a fallback avatar when a channel row
    has no thumbnail URL. Kept high in the file so both modals and the
    ChannelsPanel dropdown can render it without ESLint
    `no-use-before-define` complaints. */
function YtIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FF0000" style={{ flexShrink: 0 }}>
      <path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

/** Trash glyph — the per-channel disconnect action on a connected slot. */
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: "translateX(0px)" }}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

/** EyeOff icon — used on cards to move them into the Hidden tab. */
function HiddenIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

/** Eye (no slash) — used on Hidden-tab cards to bring them back. */
function UnhideIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

const AutoResolveModalContainer = styled.div<{ $closing?: boolean }>`
  position: relative;
  width: 92%;
  max-width: 480px;
  /* Static glass snapshot — the same gradient the cart / license modals use
     (LicenseModal/styles.ts). Replaces the old transparent blur so these
     small confirm modals match the rest of the modal family. */
  background:
    radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
    radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
    radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
    linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%);
  border: none;
  border-radius: ${radius["4xl"]};
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;
`;

export const AutoResolveModal: React.FC<{ open: boolean; onConfirm: () => void; onCancel: () => void }> = ({ open, onConfirm, onCancel }) => {
  const { mounted, closing } = useModalAnimation(open);
  if (!mounted) return null;

  return createPortal(
    <div
      data-claims-modal=""
      style={{ position: "fixed", inset: 0, zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onCancel}
    >
      <AutoResolveModalContainer $closing={closing} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onCancel}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(235, 194, 142, 0.35)"; e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.5)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(235, 194, 142, 0.2)"; e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.3)"; }}
          style={{
            position: "absolute", top: 16, right: 16, zIndex: 10, width: 36, height: 36, borderRadius: "50%",
            background: "rgba(235, 194, 142, 0.2)", border: "1px solid rgba(235, 194, 142, 0.3)", color: "#fff",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s ease",
            fontSize: "1.1rem",
          }}
        >
          &#x2715;
        </button>

        {/* Header */}
        <div style={{ padding: "40px 32px 0" }}>
          <h2 style={{ fontFamily: fonts.body, fontSize: fontSizes.xl, fontWeight: 600, color: colors.text.heading, margin: 0 }}>
            Enable Auto-Resolve?
          </h2>
        </div>
        <div style={{ height: 1, background: colors.border.light, marginTop: 16 }} />

        {/* Body */}
        <div style={{ padding: "20px 32px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
          <p style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: colors.text.body, lineHeight: 1.6, margin: 0 }}>
            This feature will immediately start to auto-resolve all your existing claims, and will continue to auto-resolve future ready claims until your monthly channel limits are reached.
          </p>

          {/* Button */}
          <div style={{ marginTop: 10 }}>
            <ActionBtn $primary onClick={onConfirm} style={{ width: "100%", justifyContent: "center", height: 44, fontWeight: 700 }}>
              <ButtonLabel glyph="check" iconFirst>
                Turn on auto-resolve
              </ButtonLabel>
            </ActionBtn>
          </div>
        </div>
      </AutoResolveModalContainer>
    </div>,
    document.body,
  );
};

/** Confirmation before disconnecting a channel from a slot. */
export const DisconnectChannelModal: React.FC<{ open: boolean; channelName: string | null; submitting?: boolean; onConfirm: () => void; onCancel: () => void }> = ({ open, channelName, submitting, onConfirm, onCancel }) => {
  const { mounted, closing } = useModalAnimation(open);
  if (!mounted) return null;

  return createPortal(
    <div
      data-claims-modal=""
      style={{ position: "fixed", inset: 0, zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onCancel}
    >
      <AutoResolveModalContainer $closing={closing} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onCancel}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(235, 194, 142, 0.35)"; e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.5)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(235, 194, 142, 0.2)"; e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.3)"; }}
          style={{
            position: "absolute", top: 16, right: 16, zIndex: 10, width: 36, height: 36, borderRadius: "50%",
            background: "rgba(235, 194, 142, 0.2)", border: "1px solid rgba(235, 194, 142, 0.3)", color: "#fff",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s ease",
            fontSize: "1.1rem",
          }}
        >
          &#x2715;
        </button>

        <div style={{ padding: "24px 32px 0" }}>
          {/* paddingRight clears the absolute X (right 16 + 36 wide) so a long
              channel name wraps before it instead of running underneath. */}
          <h2 style={{ fontFamily: fonts.body, fontSize: fontSizes.xl, fontWeight: 600, color: colors.text.heading, margin: 0, paddingRight: 32 }}>
            Disconnect {channelName || "this channel"}?
          </h2>
        </div>
        <div style={{ height: 1, background: colors.border.light, marginTop: 16 }} />

        <div style={{ padding: "20px 32px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
          <p style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: colors.text.body, lineHeight: 1.6, margin: 0 }}>
            We&apos;ll stop scanning this channel for Audio Sanctum claims, and any unresolved claims on it will no longer be processed.
          </p>

          <div style={{ marginTop: 10 }}>
            <ActionBtn
              onClick={onConfirm}
              disabled={submitting}
              onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = "#c0342a"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#d83c2e"; }}
              style={{ width: "100%", justifyContent: "center", height: 44, background: "#d83c2e", border: "none", color: "#fff", transition: "background 0.15s ease" }}
            >
              {submitting ? "Disconnecting…" : "Disconnect Channel"}
            </ActionBtn>
          </div>
        </div>
      </AutoResolveModalContainer>
    </div>,
    document.body,
  );
};

/* ══════════════════════════════════════
   DOWNGRADE CHANNEL PICKER MODAL
   ══════════════════════════════════════
   Shown when a Subscription user has a pending downgrade that reduces their
   channel count below their currently-connected channel count. The user
   must pick which channels to remove before the downgrade transition can
   fire at period end. See docu/whitelisting.md §20.11 for the spec.

   In production this is mandatory (no close button) — the caller reopens
   it on every visit to /account/claims until the selection is
   persisted. The `onCancel` prop is optional so dev/testing can still
   dismiss it without wiring the full subscription backend.
   ══════════════════════════════════════ */

const DowngradeChannelPickerContainer = styled.div<{ $closing?: boolean }>`
  position: relative;
  width: 92%;
  max-width: 520px;
  /* dvh (dynamic viewport) so the modal tracks the VISIBLE height as the iOS
     Safari bar shows/hides — vh used the large viewport and clipped the modal
     against the top/bottom edges (and broke the inner scroll bound) when the
     bottom bar appeared. */
  max-height: 85dvh;
  display: flex;
  flex-direction: column;

  /* Phone: full width within the outer 16px padding (the 92% desktop centering
     would otherwise stack with that padding into a big side gap). */
  @media (max-width: 600px) {
    width: 100%;
    max-height: 90dvh;
  }
  /* Canonical modal background — static multi-radial gradient defined in
     design-system.ts components.modal. Replaces the legacy frosted-glass
     recipe so this picker reads consistently with every other modal. */
  background: ${components.modal.background};
  border: ${components.modal.border};
  border-radius: ${components.modal.borderRadius};
  box-shadow: ${components.modal.boxShadow};
  overflow: hidden;
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;
`;

const DowngradeChannelScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px 28px 8px 40px;
  scrollbar-gutter: stable;

  /* Phone: symmetric, tighter side inset. */
  @media (max-width: 600px) {
    padding: 8px 18px;
  }

  &::-webkit-scrollbar { width: 12px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.5);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.7);
    background-clip: padding-box;
  }
`;

/* Fixed header — phone padding tightened (desktop unchanged). */
const PickerHeader = styled.div`
  flex-shrink: 0;
  padding: 40px 40px 16px;
  @media (max-width: 600px) {
    padding: 28px 22px 14px;
  }
`;

const PickerTitle = styled.h2`
  font-family: ${fonts.heading};
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 1.15;
  color: ${colors.text.heading};
  margin: 0 0 12px;
  padding-right: 44px; /* clear the optional ✕ */
  @media (max-width: 600px) {
    font-size: 1.4rem;
  }
`;

/* Fixed footer — buttons always visible. Phone padding tightened. */
const PickerFooter = styled.div`
  flex-shrink: 0;
  position: relative;
  padding: 4px 40px 44px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media (max-width: 600px) {
    padding: 4px 22px 26px;
    gap: 14px;
  }
`;

/* Action buttons — side-by-side, compact, at every width so the footer stays
   short and the channel list keeps the full height to scroll. The buttons
   wrap their label to a 2nd line (min-height, not fixed height) rather than
   squishing. */
const PickerFooterBtns = styled.div`
  display: flex;
  gap: 10px;
  @media (max-width: 600px) {
    gap: 8px;
  }
`;

/* Scroll affordance — shows while the channel list overflows above the footer
   (phone/tablet; desktop has the scrollbar). Anchored to the footer top. */
const PickerScrollFade = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 48px;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 4px;
  background: linear-gradient(to bottom, rgba(111, 83, 60, 0) 0%, rgba(111, 83, 60, 0.82) 55%, rgba(111, 83, 60, 1) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s ease;
  @media (min-width: 1150px) {
    display: none;
  }
`;

const DowngradeChannelList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DowngradeChannelRow = styled.button<{ $selected?: boolean }>`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${({ $selected }) => ($selected ? "rgba(228, 122, 106, 0.12)" : "rgba(255, 255, 255, 0.03)")};
  border: 1px solid ${({ $selected }) => ($selected ? "rgba(228, 122, 106, 0.45)" : "rgba(235, 194, 142, 0.12)")};
  transition: all 0.15s ease;
  &:hover {
    background: ${({ $selected }) => ($selected ? "rgba(228, 122, 106, 0.18)" : "rgba(255, 255, 255, 0.06)")};
    border-color: ${({ $selected }) => ($selected ? "rgba(228, 122, 106, 0.6)" : "rgba(235, 194, 142, 0.25)")};
  }
`;

export interface DowngradeChannelItem {
  id: string;
  channelName: string | null;
  channelHandle: string | null;
  channelThumbnailUrl: string | null;
}

export const DowngradeChannelPickerModal: React.FC<{
  open: boolean;
  channels: DowngradeChannelItem[];
  newLimit: number;
  /** The date the downgrade will actually take effect — typically the next
      billing cycle start. Channels selected for removal stay fully active
      until this date. Format: localized date string (e.g. "May 10, 2026"). */
  effectiveDate: string;
  onConfirm: (keepChannelIds: string[]) => void;
  /** Called when the user clicks "Keep Current Plan" to back out of the
      downgrade entirely. Same action as the existing pending-downgrade
      banner button — POST /stripe/subscriptions/cancel-downgrade.
      Used in the PRE-billing state only (effectiveDate in the future). */
  onCancelDowngrade?: () => void;
  /** Called when the user clicks "Restore Plan" in the POST-billing state
      (effectiveDate already passed). The downgrade has taken effect on
      Stripe, so reverting requires a real upgrade — parent closes this
      modal and opens ManageAddonsModal pre-filled with the OLD config
      values so the user can confirm/adjust and pay the pro-rated upgrade. */
  onRestorePlan?: () => void;
  /** Called when the user clicks the X close button or hits ESC. Optional
      — when omitted, the modal stays non-closeable (legacy callers that
      want the mandatory behavior). When provided, the X renders top-right
      and ESC closes the modal. The companion banner
      (DisconnectChannelsReminderBanner) stays visible site-wide so the
      user always has a one-click path back to reopen. */
  onClose?: () => void;
  /** Set when the downgrade has already been applied (POST-rollover
      over-quota state surfaced by `PendingDowngradeGlobalModal`). Hides
      the "Keep Current Plan" / "Schedule Disconnect" affordances since
      there's nothing left to schedule — the user just needs to disconnect
      channels now. Confirm button label switches to "Disconnect
      Channel(s)" accordingly. */
  alreadyApplied?: boolean;
}> = ({ open, channels, newLimit, effectiveDate, onConfirm, onCancelDowngrade, onRestorePlan, onClose, alreadyApplied }) => {
  const { mounted, closing } = useModalAnimation(open);
  const [selectedToRemove, setSelectedToRemove] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);
  // [mounted] so the affordance re-attaches once the (initially unmounted)
  // scroll element renders — the modal returns null until `mounted` is true.
  const canScrollDown = useScrollAffordance(listRef, [mounted]);

  // Reset selection every time the modal opens so the user starts fresh.
  useEffect(() => {
    if (open) setSelectedToRemove(new Set());
  }, [open]);

  // ESC closes the modal — only when onClose is provided (callers without
  // a close handler get the legacy mandatory behavior).
  useEffect(() => {
    if (!open || !onClose) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const mustRemove = Math.max(0, channels.length - newLimit);
  const canConfirm = selectedToRemove.size === mustRemove && mustRemove > 0;

  const toggleChannel = (id: string) => {
    setSelectedToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < mustRemove) {
        next.add(id);
      }
      return next;
    });
  };

  const isPastDate = new Date(effectiveDate) <= new Date();

  const handleConfirm = () => {
    if (!canConfirm) return;
    const keepIds = channels.filter((c) => !selectedToRemove.has(c.id)).map((c) => c.id);
    onConfirm(keepIds);
  };

  return createPortal(
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, height: "100dvh", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box" }}
    >
      <DowngradeChannelPickerContainer $closing={closing}>
        {/* Close button — only rendered when `onClose` is passed by the
            parent. Reuses the canonical LicenseCloseButton styling
            (gold-tinted circle, same as ManageAddonsModal and friends)
            for visual consistency across the modal family. The companion
            DisconnectChannelsReminderBanner stays visible site-wide so
            the user always has a one-click path back to reopen the
            modal. Legacy mandatory behavior (no X) is preserved when
            callers omit `onClose`. */}
        {onClose && (
          <LicenseCloseButton onClick={onClose} aria-label="Close">
            &#x2715;
          </LicenseCloseButton>
        )}

        {/* Header — fixed */}
        <PickerHeader>
          <PickerTitle>
            {mustRemove === 1 ? "Choose channel to disconnect" : "Choose channels to disconnect"}
          </PickerTitle>
          <p style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: colors.text.body, lineHeight: 1.55, margin: 0 }}>
            You downgraded your subscription to a lower channel limit. To continue, please select <strong style={{ fontFamily: fonts.number, color: colors.text.primary }}>{mustRemove}</strong> {mustRemove === 1 ? "channel" : "channels"} that will be disconnected from your account.
          </p>
        </PickerHeader>

        {/* Scrollable content — channel list */}
        <DowngradeChannelScrollArea ref={listRef}>
          <DowngradeChannelList>
            {channels.map((c) => {
              const selected = selectedToRemove.has(c.id);
              const displayName = c.channelName || c.channelHandle || c.id;
              return (
                <DowngradeChannelRow
                  key={c.id}
                  $selected={selected}
                  onClick={() => toggleChannel(c.id)}
                >
                  {/* Thumbnail — renders img if URL present, with onError
                      fallback that swaps to the placeholder circle + inline
                      YtIcon. Same pattern as ChannelsPanel to stay
                      consistent. */}
                  {c.channelThumbnailUrl ? (
                    <img
                      src={c.channelThumbnailUrl}
                      alt=""
                      onError={(e) => {
                        const img = e.currentTarget;
                        img.style.display = "none";
                        const fallback = img.nextElementSibling as HTMLElement | null;
                        if (fallback) fallback.style.display = "flex";
                      }}
                      style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0, objectFit: "cover", background: "rgba(235, 194, 142, 0.1)", opacity: selected ? 0.3 : 1, transition: "opacity 0.15s ease" }}
                    />
                  ) : null}
                  <div style={{
                    display: c.channelThumbnailUrl ? "none" : "flex",
                    alignItems: "center", justifyContent: "center",
                    width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                    background: "rgba(235, 194, 142, 0.1)",
                    opacity: selected ? 0.3 : 1, transition: "opacity 0.15s ease",
                  }}>
                    <YtIcon />
                  </div>
                  {/* Name + handle */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 600, color: selected ? "rgba(255,255,255,0.4)" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: selected ? "line-through" : "none", transition: "all 0.15s ease" }}>
                      {displayName}
                    </div>
                    {c.channelHandle && c.channelName && (
                      <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: selected ? "rgba(251, 244, 236, 0.3)" : "rgba(251, 244, 236, 0.55)", textDecoration: selected ? "line-through" : "none", transition: "all 0.15s ease" }}>
                        {c.channelHandle}
                      </div>
                    )}
                  </div>
                  {/* Checkbox */}
                  <div style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    background: selected ? "#e47a6a" : "transparent",
                    border: `2px solid ${selected ? "#e47a6a" : "rgba(235, 194, 142, 0.4)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s ease",
                  }}>
                    {selected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </DowngradeChannelRow>
              );
            })}
          </DowngradeChannelList>
        </DowngradeChannelScrollArea>

        {/* Footer — fixed, always visible. Counter on top, then the action
            buttons (side-by-side on desktop, stacked on phone). The scroll
            fade hints at more channels below when the list overflows. */}
        <PickerFooter>
          <PickerScrollFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></PickerScrollFade>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div />
            <span style={{
              fontFamily: fonts.number,
              fontSize: fontSizes.sm,
              color: "rgba(251, 244, 236, 0.6)",
            }}>
              {selectedToRemove.size} / {mustRemove} selected
            </span>
          </div>
          <PickerFooterBtns>
            {/* Back-out buttons (Keep Current Plan / Restore Plan) only
                render in the pre-rollover / past-date legacy paths. When
                `alreadyApplied` is set, the downgrade has already landed
                and there's no schedule to back out of — only the primary
                disconnect action makes sense. */}
            {!alreadyApplied && isPastDate ? (
              onRestorePlan && (
                <ActionBtn
                  onClick={onRestorePlan}
                  style={{ flex: 1, justifyContent: "center", textAlign: "center", minHeight: 44, padding: "8px 12px", lineHeight: 1.2 }}
                >
                  Restore Plan
                </ActionBtn>
              )
            ) : !alreadyApplied ? (
              onCancelDowngrade && (
                <ActionBtn
                  onClick={onCancelDowngrade}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(78, 224, 170, 0.95)";
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(78, 224, 170, 0.95)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = "rgba(52, 211, 153, 0.85)";
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(52, 211, 153, 0.85)";
                  }}
                  style={{
                    flex: 1,
                    justifyContent: "center",
                    textAlign: "center",
                    minHeight: 44,
                    padding: "8px 12px",
                    lineHeight: 1.2,
                    background: "rgba(52, 211, 153, 0.85)",
                    borderColor: "rgba(52, 211, 153, 0.85)",
                    color: "#fff",
                  }}
                >
                  Keep Current Plan
                </ActionBtn>
              )
            ) : null}
            <ActionBtn
              $primary
              onClick={handleConfirm}
              disabled={!canConfirm}
              style={{ flex: 1, justifyContent: "center", textAlign: "center", minHeight: 44, padding: "8px 14px", lineHeight: 1.2, ...(canConfirm ? {} : { opacity: 0.4, cursor: "not-allowed" }) }}
            >
              {alreadyApplied
                ? (mustRemove === 1 ? "Disconnect Channel" : "Disconnect Channels")
                : (isPastDate ? "Disconnect" : "Schedule Disconnect")}
            </ActionBtn>
          </PickerFooterBtns>
        </PickerFooter>
      </DowngradeChannelPickerContainer>
    </div>,
    document.body,
  );
};

/* ══════════════════════════════════════
   PLAN UPGRADE MODAL (no track context)
   ══════════════════════════════════════ */

const PlanUpgradeModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { mounted, closing } = useModalAnimation(open);
  const dispatch = useAppDispatch();
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const subConfig = useSubscriptionConfig();
  const [premiumChannels, setPremiumChannels] = useState(subConfig.includedChannels);
  const [premiumWhitelistings, setPremiumWhitelistings] = useState(subConfig.includedClaims);

  const isAnnual = billing === "annual";
  // Centralized pricing (claims billed per channel) — mirrors the backend.
  const subPricing = computeSubscriptionPricing(subConfig, { channels: premiumChannels, claims: premiumWhitelistings, isAnnual });
  const premiumPrice = subPricing.totalMonthly;

  const handleSubscription = useCallback(() => {
    onClose();
    dispatch(openSubscriptionCheckout({ channels: premiumChannels, claims: premiumWhitelistings, billingPeriod: billing }));
  }, [onClose, dispatch, premiumChannels, premiumWhitelistings, billing]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return createPortal(
    <LicenseOverlay>
      {/* Mirrors the "Choose Your Option" (LicenseModal) phone template:
          ✕ on the container, the fixed head holds ONLY the title, and the
          billing toggle + plan scroll inside ScrollArea (variant b — no
          bottom button). Keeps this modal a separate subscription-only upsell
          but with the same phone behaviour. */}
      <LicenseModalContainer $closing={closing} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <LicenseCloseButton onClick={onClose} aria-label="Close">
          &#x2715;
        </LicenseCloseButton>
        <LicenseModalHead>
          <ModalHeading>Choose Your Plan</ModalHeading>
        </LicenseModalHead>

        <ModalScrollArea>
        <LicenseModalContent>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", flexWrap: "wrap", gap: "8px", paddingBottom: "20px" }}>
          <BillingToggleWrap>
            <BillingSwitch $annual={billing === "annual"} onClick={() => setBilling(billing === "monthly" ? "annual" : "monthly")} />
            <BillingLabel $active={billing === "annual"} onClick={() => setBilling("annual")}>
              Annual (Save {subConfig.annualDiscountPercent}%)
            </BillingLabel>
          </BillingToggleWrap>
        </div>
        <PlansGrid>
          <PlansMainRow style={{ display: "flex", justifyContent: "center" }}>
            <PlanColumn $highlighted style={{ maxWidth: 400 }}>
              <PlanIcon>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </PlanIcon>
              <PlanTitle>Subscription</PlanTitle>
              <PlanPrice>
                <PriceLead>${premiumPrice.toFixed(2)}<PriceUnit>month</PriceUnit></PriceLead>
                <WhitelistingStepper>
                  <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumChannels(Math.max(1, premiumChannels - 1)); }} style={{ visibility: premiumChannels <= 1 ? "hidden" : "visible" }}>−</WhitelistingBtn>
                  <WhitelistingText>{premiumChannels} YouTube {premiumChannels === 1 ? "channel" : "channels"}</WhitelistingText>
                  <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumChannels(premiumChannels + 1); }}>+</WhitelistingBtn>
                </WhitelistingStepper>
                <WhitelistingStepper>
                  <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumWhitelistings(Math.max(subConfig.includedClaims, premiumWhitelistings - subConfig.claimIncrement)); }} style={{ visibility: premiumWhitelistings <= subConfig.includedClaims ? "hidden" : "visible" }}>−</WhitelistingBtn>
                  <WhitelistingText>{premiumWhitelistings} claims per channel</WhitelistingText>
                  <WhitelistingBtn onClick={(e) => { e.stopPropagation(); setPremiumWhitelistings(Math.min(200, premiumWhitelistings + subConfig.claimIncrement)); }}>+</WhitelistingBtn>
                </WhitelistingStepper>
                {isAnnual && <AnnualTotal style={{ width: "100%", textAlign: "center" }}>${(premiumPrice * 12).toFixed(2)} charged upfront ({subConfig.annualDiscountPercent}% saved)</AnnualTotal>}
              </PlanPrice>
              <SubFeatureList>
                <SubFeature><LicenseCheckIcon>&#10003;</LicenseCheckIcon>Full library access as MP3 + WAV</SubFeature>
                <SubFeature><LicenseCheckIcon>&#10003;</LicenseCheckIcon>Covers {premiumChannels} YouTube {premiumChannels === 1 ? "channel" : "channels"}</SubFeature>
                <SubFeature><LicenseCheckIcon>&#10003;</LicenseCheckIcon>{premiumWhitelistings} monthly claims per channel{premiumChannels > 1 ? ` (${premiumWhitelistings * premiumChannels} claims total)` : ""}</SubFeature>
                <SubFeature><LicenseCheckIcon>&#10003;</LicenseCheckIcon>Monetization allowed</SubFeature>
                <SubFeature><LicenseCheckIcon>&#10003;</LicenseCheckIcon>1-line credit text</SubFeature>
                <SubFeature><LicenseCheckIcon>&#10003;</LicenseCheckIcon>Auto-resolve claims</SubFeature>
                <SubFeature><LicenseCheckIcon>&#10003;</LicenseCheckIcon>Cancel anytime</SubFeature>
                <SubFeature><WarningIcon>&#10007;</WarningIcon>No music or ambient content</SubFeature>
                <SubFeature><WarningIcon>&#10007;</WarningIcon>No use in livestreams</SubFeature>
              </SubFeatureList>
              <PlanButton $gold onClick={handleSubscription}>Get Started</PlanButton>
            </PlanColumn>
          </PlansMainRow>
        </PlansGrid>
        </LicenseModalContent>
        </ModalScrollArea>
      </LicenseModalContainer>
    </LicenseOverlay>,
    document.body,
  );
};

/* ══════════════════════════════════════
   TYPES
   ══════════════════════════════════════ */

type VideoStatus =
  | "Ready to Release"
  | "Credit Text"
  | "Credit Missing"
  | "Music Use"
  | "Not Covered"
  | "Private Video"
  | "No Claim Detected"
  | "Claim Limit Reached"
  | "Analyzing"
  | "Searching"
  | "Whitelisted";

type SortKey = "status" | "title" | "date";

interface VideoItem {
  id: string;
  /** Real backend id of the `pending_url_submissions` row — present only for
      cards that came from the real GET /api/claims/my read. Drives the
      Submit again / Remove buttons to call real endpoints instead of
      mutating local mock state. */
  submissionId?: string;
  videoTitle: string;
  videoUrl: string;
  trackTitle: string;
  trackArtist: string;
  status: VideoStatus;
  /** ISO string of the runner's last verdict, null if it has never run. Drives
      the release of the Under-Review hold after a Submit again — see
      `submittedAtCheckedAt`. */
  checkedAt?: string | null;
  /** The runner has judged this claim more than once. Switches error copy from
      first-time instructions to "we looked again and it is still wrong". */
  reChecked?: boolean;
  /** Matched track's licence tier — decides the single-licence price. */
  licenseTier?: string;
  /** Matched catalogue track name. The cart names this rather than the
      provider's asset title, because it is what the licence is for. */
  matchedTrackName?: string;
  date: string;
  channelName?: string;
  /** YouTube channel URL (built from channelId backend-side). Used to
      render the channel name as a link on each card. */
  channelUrl?: string;
  /** Raw YouTube channel id — drives the channel filter + per-channel usage. */
  channelId?: string;
  /** Which plan tier is required for this track */
  requiredTier?: "Premium";
  /** Global-monotonic `#N` id of the latest status event for this card.
      Rendered in the card footer. Undefined when no event has been logged
      yet (e.g. locally-constructed mock rows, or very fresh Analyzing
      cards from the real API before the runner's first pass). */
  eventSequentialId?: number;
  /** The YouTube Licence number for a resolved claim, rendered beside the
      License Text button. */
  licenceCode?: string;
  licenceVoidReason?: string;
  /** ISO date the Content ID claim was released (removed from the video) —
      shown as "Claim removed on <date>" on the Licensed card. */
  releasedAt?: string;
  /** The licence is active but the request that removes the claim from the
      video has not gone out yet, because the day's provider allowance is
      spent. The Licensed card says the removal follows within 24 hours
      instead of claiming it has already happened. */
  releasePending?: boolean;
  /** The track's real Credit Text from the backend — the exact string the
      claims runner checks for. Absent when the claim matched no track. */
  creditText?: string;
  /** Released video whose credit a re-check found missing (status "Credit
      Missing"); and whether this claim is held while whitelisting is suspended. */
  creditMissing?: boolean;
  paused?: boolean;
  accountSuspended?: boolean;
  lastCreditCheckAt?: string;
  /**
   * True when a Subscription user has manually submitted a free-track video URL
   * on a channel that isn't connected to their account. Triggers the "Free Use"
   * variant of the Credit Text error copy — see ERROR_MESSAGES["credit text free use"].
   */
  isFreeUseSubmission?: boolean;
  /** Set by the API once a waiting card has waited past its threshold — the
      hint line switches to SLOW_HINTS. Never changes the status itself. */
  takingLonger?: boolean;
  /** Backend flag: user clicked Hide on this card. Lives in the Hidden
      tab regardless of its underlying status. Seeded into the
      ThreeSectionList's hiddenIds set on first render. */
  isHidden?: boolean;
  /** Backend flag: user queued this Claim Limit Reached card for next
      cycle. Lives in the Queued tab. */
  isQueuedForNextCycle?: boolean;
  /** Backend flag: action-required matched claim the user has never had on
      screen (`claimViewedAt IS NULL`). Drives the green "New" badge + the
      per-channel new counts on the channel picker. The page freezes the set
      of new ids per page-load so badges persist while scrolling / switching
      channels and only drop the viewed ones on the next load. */
  isNew?: boolean;
  /** Backend flag: video was deleted from YouTube. Card renders greyed
      out with no action buttons — only a dismiss option. */
  isVideoDeleted?: boolean;
  /** Backend flag: free-tier user has at least one unconnected active
      license matching this card's track. Swaps the License Now button
      for "License Now (uses 1 License)". Backend route: POST /claims/my/:id/use-license. */
  useLicenseAvailable?: boolean;
  /** Backend flag: this card's channel is at its monthly credit limit
      right now. Subscription users only. While true, Ready-to-Release
      cards render as Claim Limit Reached (same buttons, same copy) — the
      stored runner verdict may still say Ready-to-Release until the
      cycle resets, but the credit bucket is empty, so the user can't
      release the card anyway. */
  channelAtLimit?: boolean;
  /** Resolved catalog track id (when matcher could identify the track).
      Powers the Buy Single License button — addToCart needs the real
      trackId so the cart row maps to a real catalog entry. */
  trackId?: string;
  trackSlug?: string;
  coverUrl?: string;
}

/** Convert a backend MyClaimDto to the frontend's VideoItem shape.
    Mostly a pass-through — the backend `VideoStatus` literals already
    match the frontend. `date` comes over as an ISO string; slice to
    YYYY-MM-DD to match the mock-data format. */
const dtoToVideoItem = (d: MyClaimDto): VideoItem => ({
  id: d.id,
  submissionId: d.submissionId,
  videoTitle: d.videoTitle,
  videoUrl: d.videoUrl,
  trackTitle: d.trackTitle,
  trackArtist: d.trackArtist,
  status: d.status as VideoStatus,
  // Carries the runner's last-verdict time through to the card. This mapper
  // picks fields one by one, so anything not named here is silently dropped —
  // which is exactly what happened to `checkedAt` on the first attempt at this
  // fix: the backend sent it, the card never saw it, and the Under-Review hold
  // had nothing to release it with.
  checkedAt: d.checkedAt,
  reChecked: d.reChecked,
  licenseTier: d.licenseTier,
  matchedTrackName: d.matchedTrackName,
  date: d.date.slice(0, 10),
  channelName: d.channelName,
  channelUrl: d.channelUrl,
  channelId: d.channelId,
  requiredTier: d.requiredTier,
  isHidden: d.isHidden,
  isQueuedForNextCycle: d.isQueuedForNextCycle,
  isNew: d.isNew,
  isVideoDeleted: d.isVideoDeleted,
  takingLonger: d.takingLonger,
  useLicenseAvailable: d.useLicenseAvailable,
  eventSequentialId: d.eventSequentialId,
  licenceCode: d.licenceCode,
  licenceVoidReason: d.licenceVoidReason,
  releasedAt: d.releasedAt,
  releasePending: d.releasePending,
  creditText: d.creditText,
  creditMissing: d.creditMissing,
  paused: d.paused,
  accountSuspended: d.accountSuspended,
  lastCreditCheckAt: d.lastCreditCheckAt,
  channelAtLimit: d.channelAtLimit,
  trackId: d.trackId,
  trackSlug: d.trackSlug,
  coverUrl: d.coverUrl,
});

/* ══════════════════════════════════════
   MOCK DATA
   ══════════════════════════════════════ */

const MOCK_FREE_VIDEOS: VideoItem[] = [
  { id: "f-1", videoTitle: "Travel Vlog - Japan 2026", videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", trackTitle: "Invisible", trackArtist: "Patrick Lenk", status: "Credit Text", date: "2026-03-28", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
  { id: "f-2", videoTitle: "Cinematic Nature Film", videoUrl: "https://www.youtube.com/watch?v=abc123", trackTitle: "Sacred Dawn", trackArtist: "Simon Daum", status: "Not Covered", date: "2026-03-27", requiredTier: "Premium", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
  { id: "f-3", videoTitle: "Relaxation Music Stream", videoUrl: "https://www.youtube.com/watch?v=xyz789", trackTitle: "Eternal", trackArtist: "Patrick Lenk", status: "Music Use", date: "2026-03-26", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
  { id: "f-8", videoTitle: "My Holiday Compilation", videoUrl: "https://www.youtube.com/watch?v=hol01", trackTitle: "—", trackArtist: "—", status: "No Claim Detected", date: "2026-03-30", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
  { id: "f-7", videoTitle: "Private Yoga Session", videoUrl: "https://www.youtube.com/watch?v=yoga01", trackTitle: "Orbis Factor Kyrie", trackArtist: "Patrick Lenk", status: "Private Video", date: "2026-03-29", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
  { id: "f-4", videoTitle: "Cooking Tutorial #42", videoUrl: "https://www.youtube.com/watch?v=cook42", trackTitle: "Silent Heavens", trackArtist: "Patrick Lenk", status: "Analyzing", date: "2026-03-30", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
  { id: "f-4b", videoTitle: "Sunset Skateboarding Edit", videoUrl: "https://www.youtube.com/watch?v=skate8", trackTitle: "—", trackArtist: "—", status: "Searching", date: "2026-03-30" },
  { id: "f-5", videoTitle: "Morning Meditation Session", videoUrl: "https://www.youtube.com/watch?v=med01", trackTitle: "Another Dimension", trackArtist: "Patrick Lenk", status: "Whitelisted", date: "2026-03-15", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
  { id: "f-6", videoTitle: "Study With Me — 3 Hours", videoUrl: "https://www.youtube.com/watch?v=study3", trackTitle: "Godly Grace", trackArtist: "Patrick Lenk", status: "Whitelisted", date: "2026-03-10", channelName: "Wanderer Diaries", channelUrl: "https://www.youtube.com/@wanderer-diaries" },
];

const MOCK_PREMIUM_VIDEOS: VideoItem[] = [
  { id: "p-cl", videoTitle: "Deep Focus Ambient Mix", videoUrl: "https://www.youtube.com/watch?v=focus02", trackTitle: "Silent Heavens", trackArtist: "Patrick Lenk", status: "Claim Limit Reached", date: "2026-04-03", channelName: "Ambient Worlds", channelUrl: "https://www.youtube.com/@ambient-worlds" },
  { id: "p-0", videoTitle: "Cinematic Drone Footage", videoUrl: "https://www.youtube.com/watch?v=drone1", trackTitle: "Sacred Dawn", trackArtist: "Simon Daum", status: "Ready to Release", date: "2026-04-02", channelName: "Sleep Therapy", channelUrl: "https://www.youtube.com/@sleep-therapy" },
  { id: "p-nc", videoTitle: "Cinematic Travel Montage", videoUrl: "https://www.youtube.com/watch?v=travel01", trackTitle: "Godly Grace", trackArtist: "Patrick Lenk", status: "Not Covered", date: "2026-03-29", channelName: "Adventure Daily", channelUrl: "https://www.youtube.com/@adventure-daily" },
  { id: "p-m", videoTitle: "Relaxation Music Stream", videoUrl: "https://www.youtube.com/watch?v=relmus2", trackTitle: "Eternal", trackArtist: "Patrick Lenk", status: "Music Use", date: "2026-03-26", channelName: "Sleep Therapy", channelUrl: "https://www.youtube.com/@sleep-therapy" },
  // Free-use / credit-text card removed: subscriptions don't distinguish free
  // vs premium tracks — every claim is one credit, no credit-text path.
  { id: "p-8", videoTitle: "My Holiday Compilation", videoUrl: "https://www.youtube.com/watch?v=hol02", trackTitle: "—", trackArtist: "—", status: "No Claim Detected", date: "2026-03-30", channelName: "Adventure Daily", channelUrl: "https://www.youtube.com/@adventure-daily" },
  { id: "p-3", videoTitle: "Private Meditation Video", videoUrl: "https://www.youtube.com/watch?v=priv01", trackTitle: "Orbis Factor Kyrie", trackArtist: "Patrick Lenk", status: "Private Video", date: "2026-03-28", channelName: "Sleep Therapy", channelUrl: "https://www.youtube.com/@sleep-therapy" },
  { id: "p-4", videoTitle: "Healing Session — 2 Hours", videoUrl: "https://www.youtube.com/watch?v=heal02", trackTitle: "Eternal", trackArtist: "Patrick Lenk", status: "Analyzing", date: "2026-03-31", channelName: "Sleep Therapy", channelUrl: "https://www.youtube.com/@sleep-therapy" },
  { id: "p-4b", videoTitle: "Sunrise Time-lapse Vlog", videoUrl: "https://www.youtube.com/watch?v=sunr9", trackTitle: "—", trackArtist: "—", status: "Searching", date: "2026-03-31", channelName: "Adventure Daily", channelUrl: "https://www.youtube.com/@adventure-daily" },
  { id: "p-5", videoTitle: "Study Music Collection", videoUrl: "https://www.youtube.com/watch?v=studym", trackTitle: "Godly Grace", trackArtist: "Patrick Lenk", status: "Whitelisted", date: "2026-03-22", channelName: "Ambient Worlds", channelUrl: "https://www.youtube.com/@ambient-worlds" },
  { id: "p-6", videoTitle: "Meditation Guide for Beginners", videoUrl: "https://www.youtube.com/watch?v=medgd", trackTitle: "Silent Heavens", trackArtist: "Patrick Lenk", status: "Whitelisted", date: "2026-03-18", channelName: "Sleep Therapy", channelUrl: "https://www.youtube.com/@sleep-therapy" },
  { id: "p-7", videoTitle: "Sleep Therapy Night Mix", videoUrl: "https://www.youtube.com/watch?v=slpnm", trackTitle: "Another Dimension", trackArtist: "Patrick Lenk", status: "Whitelisted", date: "2026-03-12", channelName: "Sleep Therapy", channelUrl: "https://www.youtube.com/@sleep-therapy" },
];

/* ── Creator Mock Data ── */

interface CreatorSubmission {
  id: string;
  videoTitle: string;
  videoUrl: string;
  submittedAt: string;
  status: string;
  /** Same purpose as `VideoItem.eventSequentialId`. Mock submissions leave
      it undefined — the `#N` label and copy button simply don't render. */
  eventSequentialId?: number;
}

const MOCK_CREATOR_SUBMISSIONS: CreatorSubmission[] = [
  { id: "mc-4", videoTitle: "Peaceful Piano Session", videoUrl: "https://www.youtube.com/watch?v=mc4", submittedAt: "2026-03-25", status: "Credit Text" },
  { id: "mc-5", videoTitle: "Cinematic Orchestra Mix", videoUrl: "https://www.youtube.com/watch?v=mc5", submittedAt: "2026-03-22", status: "Timestamps" },
  { id: "mc-7", videoTitle: "Healing Frequencies 432Hz", videoUrl: "https://www.youtube.com/watch?v=mc7", submittedAt: "2026-03-18", status: "Private Video" },
];

const MOCK_CREATOR_SPLITS: CreatorSubmission[] = [
  { id: "sp-1", videoTitle: "Ambient Music Mix Vol. 3", videoUrl: "https://www.youtube.com/watch?v=mc1", submittedAt: "2026-03-28", status: "Split" },
  { id: "sp-2", videoTitle: "Gregorian Chants Collection", videoUrl: "https://www.youtube.com/watch?v=sp2", submittedAt: "2026-02-15", status: "Split" },
  { id: "sp-3", videoTitle: "Sacred Music for Study", videoUrl: "https://www.youtube.com/watch?v=sp3", submittedAt: "2026-01-20", status: "Split" },
  { id: "sp-4", videoTitle: "Peaceful Monastery Sounds", videoUrl: "https://www.youtube.com/watch?v=sp4", submittedAt: "2025-12-10", status: "Split" },
];

/* ══════════════════════════════════════
   CONSTANTS
   ══════════════════════════════════════ */

const STATUS_ORDER: Record<string, number> = {
  "ready to release": 0,
  "credit text": 0,
  "music use": 0,
  "not covered": 0,
  "private video": 0,
  "no claim detected": 0,
  "analyzing": 1,
  "searching": 1,
  "whitelisted": 2,
};

const ERROR_STATUSES: VideoStatus[] = ["Ready to Release", "Credit Text", "Credit Missing", "Music Use", "Not Covered", "Private Video", "No Claim Detected", "Claim Limit Reached"];

/** True while the account's whitelisting is suspended (ToU §17.4). Provided at
    the LicensesTab root and consumed by the channel-management UI at any depth
    (dropdown "Edit Channels", connect panel) to block connect/remove/replace —
    avoids threading the flag through every intermediate component. */
const AccountSuspendedContext = React.createContext(false);

/** Tab key → human label. Used by the Hidden-tab unhide tooltip to show
    where each card will move when unhidden ("Move to Action Required",
    "Move to Resolved", etc). */
const TAB_LABELS: Record<string, string> = {
  action: "Action Required",
  review: "Under Review",
  queued: "Queued",
  licensed: "Licensed",
  hidden: "Hidden",
};

// Pending-submission timeout — fetched from GET /api/claims/sync/config on mount.
// Default 7 so the copy is sensible before the fetch resolves.
const ClaimsConfigContext = React.createContext<{ pendingTimeoutDays: number }>({ pendingTimeoutDays: 7 });

const ERROR_MESSAGES_STATIC: Record<string, string> = {
  "ready to release": "All checks passed. Ready to resolve claim.",
  "credit text": "Free tracks require credit text in your video description. Add and submit again.",
  "credit text free use": "Free tracks used outside your connected channels are still free to use - but they need credit text. Add it to your video description and submit again.",
  "music use": "Claim can't be removed because it contains music and/or ambient content. Become a Music Creator Partner to resolve.",
  "not covered": "Video contains a Premium track which is not covered by your current plan. Start a subscription or buy a license to resolve.",
  "private video": "This video appears to be private or deleted. Either change its visibility and submit again or confirm it as deleted.",
  "claim limit reached": "",
};

/** The Credit Text card's message.
 *
 *  Two dimensions, and both were previously collapsed into the free-user
 *  sentence: WHICH credit is owed, and whether we have already looked once.
 *
 *  A subscriber was told "Free tracks require credit text" — wrong twice over.
 *  There is no free/premium distinction for a subscription at all (every track
 *  is covered the same way), and what they actually owe is the one-line credit.
 *  The "Show Credit Text" button beside it handed them that one-liner, so the
 *  message and the button contradicted each other on the same card. */
const creditTextMessage = (oneLiner: boolean, reChecked: boolean): string => {
  if (reChecked) {
    return oneLiner
      ? "Your video was reviewed again and the one-line credit is still missing or incomplete. Add it and submit again."
      : "Your video was reviewed again and its credit text is still missing or incomplete. Add it and submit again.";
  }
  return oneLiner
    ? "Your video needs the one-line credit in its description. Add it and submit again."
    : ERROR_MESSAGES_STATIC["credit text"];
};

const buildNoClaimDetectedMessage = (days: number) =>
  `No claim was detected for this video in the past ${days} days. Did you use our music in this video? Re-submit to wait another ${days} days for an incoming claim, or remove this video.`;

// Hint copy shown under the title for the two "Under Review" sub-statuses
const REVIEW_HINTS: Record<string, string> = {
  "analyzing": "Music detected — analyzing your video...",
  "searching": "No claim detected yet — we keep checking. YouTube can take a few days to flag a video, this is normal.",
};

/** What a card says once it has been waiting two days.
 *
 *  "Analyzing your video..." is true for the first few minutes and a lie by
 *  the second day: the work has stopped and is waiting for one of us. The
 *  card cannot say that, but it must stop implying that something is
 *  happening right now. So it names the wait, promises nothing it cannot
 *  keep, asks for nothing the reader can act on, and thanks them. */
const SLOW_HINTS: Record<string, string> = {
  "analyzing": "Music detected — this one is taking longer than usual. Nothing is needed from you, we are on it. Thank you for your patience.",
  "searching": "No claim detected yet — this one is taking longer than usual. We keep checking. Thank you for your patience.",
};

// Section hint shown above the list on the "Under Review" tab. The 16px bottom
// margin matches the gap between claim cards (SubmissionCard margin-bottom).
const UnderReviewHint = (
  <FreeHintBox style={{ marginBottom: 16 }}>
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
    The following video claims are under review (up to 48h). We email you if your action is required.
  </FreeHintBox>
);

const getCreditText = (artist: string, title: string) =>
  `Music: ${artist} - ${title}\n© Provided by Audio Sanctum\nWatch: [Paste link to Audio Sanctum YT video]\nListen: https://spotify.audiosanctum.com\nFree Use → https://audiosanctum.com/free-youtube-music`;

const getVideoId = (url: string) => url.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1] || "";

const getChannelName = (url: string): string => {
  const match = url.match(/@([^/?]+)/);
  return match ? match[1] : url;
};

/* ══════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════ */

/** Animated credit text panel */
const CreditTextPanel: React.FC<{ open: boolean; artist: string; title: string; creditText?: string }> = ({ open, artist, title, creditText }) => {
  // The real credit text for this track when we know it; the placeholder shape
  // only when the claim never matched a track.
  const text = creditText || getCreditText(artist, title);
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setHeight(open ? ref.current.scrollHeight : 0);
    }
  }, [open]);

  return (
    <div style={{ maxHeight: `${height}px`, opacity: open ? 1 : 0, overflow: "hidden", transition: "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease" }}>
      {/* Gap lives as PADDING on the measured wrapper (not margin on the box)
          so the box's top margin can't collapse through it and make
          scrollHeight under-measure — which clipped the box's bottom
          (button spacing + border) under the panel's overflow:hidden. */}
      <div ref={ref} style={{ paddingTop: 14 }}>
        <CreditTextBox style={{ width: "100%", marginTop: 0 }}>
          {text}
          <div>
            <CreditCopyBtn
              $copied={copied}
              onClick={() => {
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {copied ? (
                  <polyline points="20 6 9 17 4 12" />
                ) : (
                  <>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </>
                )}
              </svg>
              {copied ? "Copied!" : "Copy"}
            </CreditCopyBtn>
          </div>
        </CreditTextBox>
      </div>
    </div>
  );
};

/** Fade-in wrapper for items appearing in Under Review — expands height then fades in */
const FadeIn: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Measure the content height after mount
    requestAnimationFrame(() => {
      if (innerRef.current) {
        setHeight(innerRef.current.offsetHeight);
      }
      // Start fade-in after height animation
      setTimeout(() => setShow(true), 350);
    });
  }, []);

  return (
    <div
      ref={ref}
      style={{
        maxHeight: height > 0 ? `${height}px` : "0px",
        overflow: "hidden",
        transition: "max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        ref={innerRef}
        style={{
          opacity: show ? 0.5 : 0,
          transition: "opacity 0.3s ease",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/** Smooth expand/collapse wrapper */
const SmoothExpand: React.FC<{ open: boolean; children: React.ReactNode }> = ({ open, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      setHeight(open ? ref.current.scrollHeight : 0);
    }
  }, [open]);

  return (
    <div style={{ maxHeight: `${height}px`, opacity: open ? 1 : 0, overflow: "hidden", transition: "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease" }}>
      <div ref={ref}>{children}</div>
    </div>
  );
};

/** Clickable `#N` pill with clipboard copy + "Copied" flash. Used inline
    inside the card action row (both matched-claim cards and creator
    submission cards). No tooltip — the hover state itself signals
    interactivity. */
const EventIdPill: React.FC<{ id: number }> = ({ id }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(`#${id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <ErrorIdLabel onClick={handleCopy}>
      {copied ? (
        <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
      ) : (
        <svg viewBox="0 0 24 24">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
      {copied ? "Copied" : `#${id}`}
    </ErrorIdLabel>
  );
};

/** The licence number on a resolved card, click-to-copy. A licence code is
    something people quote into a support form — it should never have to be
    retyped off the screen. Same copy/flash idiom as the `#N` pill. */
const LicenceNumber: React.FC<{ code: string; voidReason?: string }> = ({ code, voidReason }) => {
  const [copied, setCopied] = useState(false);
  // Phone/tablet stack the action row, so "stretch" gives width, not height —
  // there the box has to carry the button's own padding and type to match it.
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  const handleCopy = (): void => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <ErrorIdLabel
      onClick={handleCopy}
      style={{
        // Desktop: stretching to the action row's height matches the License
        // Text button exactly, and keeps matching it if that button's padding
        // ever changes. Matching by a hand-set height would drift.
        alignSelf: "stretch",
        justifyContent: "center",
        borderRadius: 10,
        // Keep the same monospace-number family + size as the #N claim pill
        // (ErrorIdLabel's base). Only the padding differs by breakpoint: on
        // phone the row stacks, so it carries button-sized padding for height.
        ...(isCompact ? { padding: "8px 16px" } : { padding: "0 14px" }),
        // A voided licence reads red and says so on the face of the card. The
        // certificate and its PDF already say it; the card is where the
        // customer actually looks, and staying silent here left the document
        // and the page disagreeing.
        color: copied ? "#9ad3a8" : voidReason ? "#e47a6a" : "rgba(251, 244, 236, 0.6)",
        ...(voidReason ? { borderColor: "rgba(228, 122, 106, 0.4)" } : {}),
      }}
    >
      {copied ? "Copied" : `License Nr. ${code}`}
      {copied ? (
        <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
      ) : (
        <svg viewBox="0 0 24 24">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </ErrorIdLabel>
  );
};


/** Video card — shared across Free/Premium */
/** Site-themed "paused" hint for the past-due claim buttons. The native
 *  `title` tooltip looks off-brand (and never fires on a disabled element),
 *  so use the MUI Tooltip — themed cream/brown in `theme/components.ts`. The
 *  paused buttons are aria-disabled (not `disabled`) with pointer events
 *  intact, so MUI attaches directly to the button with no wrapper / layout
 *  shift. `enterTouchDelay={0}` so a tap on phone reveals it immediately. */
const PausedTip: React.FC<{ on: boolean; children: React.ReactElement }> = ({ on, children }) =>
  on ? (
    <Tooltip
      title="Paused while your payment is overdue"
      placement="top"
      enterTouchDelay={0}
      componentsProps={{ tooltip: { sx: { backgroundColor: "#3c2d23", color: "#f5e4cc" } } }}
    >
      {children}
    </Tooltip>
  ) : children;

/**
 * A refusal, said where the action was taken — under the card's buttons, or
 * inside the submit box beside Cancel and Submit. Amber like the rest of the
 * page's warnings, and at body size: this is the sentence that explains why
 * nothing happened, not a caption.
 */
const RefusalNotice: React.FC<{ message: string; style?: React.CSSProperties }> = ({ message, style }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 6,
      background: "rgba(221, 122, 51, 0.14)",
      border: "1px solid rgba(221, 122, 51, 0.45)",
      fontFamily: fonts.body,
      fontSize: fontSizes.body,
      lineHeight: 1.45,
      color: "#f0c39a",
      ...style,
    }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dd7a33" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 2 }}>
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.5" />
    </svg>
    <span>{message}</span>
  </div>
);

const VideoCard: React.FC<{
  item: VideoItem;
  showCreditText?: boolean;
  showUpgrade?: boolean;
  onOpenLicense?: (item: VideoItem, preferMode?: "subscription" | "single") => void;
  onHide?: (id: string) => void;
  /** When provided, the top-right icon becomes Unhide (Eye) instead of
      Hide (EyeOff). Used by the Hidden tab to bring cards back. */
  onUnhide?: (id: string) => void;
  /** Label for the unhide tooltip — tells the user which tab the card
      will move to when unhidden. E.g. "Move to Action Required". Only
      read when `onUnhide` is set. */
  unhideTargetLabel?: string;
  onSubmitAgain?: (id: string) => void;
  /** Open the YouTube Licence certificate for a resolved claim. Only wired on
      cards backed by a real claim row — a mock card has nothing to certify. */
  onOpenCertificate?: (id: string) => void;
  onQueue?: (id: string) => void;
  onManageSub?: () => void;
  onResolve?: (id: string) => void;
  /** Why the last resolve on THIS card was refused. Sits under the buttons,
      where the click was, rather than above the card. */
  resolveError?: string | null;
  onDismissDeleted?: (id: string) => void;
  onConfirmDeleted?: (id: string) => void;
  /** Drives the Resolve button's transient post-click states.
   *    "success" — green button reads "Claim resolved!" (just confirmed).
   *    "moving"  — greyed button + spinner reads "Moving to "Licensed"…".
   *    undefined — default Resolve Claim button (clickable). */
  resolveStage?: "success" | "moving";
  /** Same two-stage pattern for the Submit-again button. Stage 1 reads
   *  "Submitted!", stage 2 reads "Moving to "Under Review"…". */
  submitStage?: "success" | "moving";
  /** Same two-stage pattern for the Queue-for-Next-Month button. Stage 1
   *  reads "Queued!", stage 2 reads "Moving to "Queued"…". */
  queueStage?: "success" | "moving";
  /** Subscription is PAST_DUE — freeze the claim-state actions
   *  (Resolve / Submit again / Queue). Credit-text, view-license, and
   *  Apply-for-Music-Creator stay enabled (those aren't claim mutations). */
  isPastDue?: boolean;
  /** The card sits in the Queued tab — read-only until cycle reset, so ALL
   *  inline action buttons are suppressed (the event-id pill still renders).
   *  The Hidden tab does NOT use this — it keeps its inline buttons and only
   *  drops "Move to Hidden" (gated on `onHide`, which it doesn't pass). */
  parked?: boolean;
  /** Show the green "New" badge — an action-needed claim the user hasn't had
   *  on screen yet (drives the unread system on /account/claims). */
  isNew?: boolean;
  /** Fired (once, after the card has been ~half-visible for ~1s) so the parent
   *  can record it server-side as "viewed". */
  onVisible?: () => void;
}> = ({ item: rawItem, showCreditText = true, showUpgrade: _showUpgrade = true, onHide, onUnhide, unhideTargetLabel, onSubmitAgain, onOpenCertificate, onQueue, onManageSub, onOpenLicense, onResolve, resolveError, onDismissDeleted, onConfirmDeleted, resolveStage, submitStage, queueStage, isPastDue, parked, isNew, onVisible }) => {
  const navigate = useNavigate();
  // Common greyed-out style for sibling buttons while their card's
  // primary action is animating. Keeps them visible (so the layout
  // doesn't shift) but visibly disabled.
  const disabledSiblingStyle: React.CSSProperties = {
    opacity: 0.35,
    cursor: "not-allowed",
    pointerEvents: "none",
    transition: "opacity 0.3s ease",
  };
  // Same greyed look as above, but WITHOUT pointer-events:none — a `disabled`
  // or pointer-events:none element never fires its native `title`, so the
  // "Paused while your payment is overdue" hint would be invisible. Paused
  // buttons render with no onClick + aria-disabled, so they stay inert while
  // remaining hoverable (so the hint shows).
  const pausedStyle: React.CSSProperties = {
    opacity: 0.35,
    cursor: "not-allowed",
    transition: "opacity 0.3s ease",
  };
  // Submit-again button rendered as a function so the three sites that
  // include it (Credit Text / Private Video / No Claim Detected) all
  // share the same three-state transition: default → "Submitted!" →
  // greyed spinner. Defined as a function so the latest item.id and
  // submitStage are captured per render.
  const renderSubmitAgain = (id: string): React.ReactNode => {
    if (submitStage === "success") {
      return (
        <ActionBtn $primary disabled style={{ cursor: "default", transition: "background 0.3s ease, opacity 0.3s ease" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Submitted!
        </ActionBtn>
      );
    }
    if (submitStage === "moving") {
      return (
        <ActionBtn $primary disabled style={{ background: "rgba(255, 255, 255, 0.08)", borderColor: "rgba(255, 255, 255, 0.15)", color: "rgba(255, 255, 255, 0.65)", cursor: "default", opacity: 0.85, transition: "background 0.3s ease, opacity 0.3s ease" }}>
          <ResolveSpinner />
          Moving to &ldquo;Under Review&rdquo;…
        </ActionBtn>
      );
    }
    return (
      <PausedTip on={!!isPastDue}>
      <ActionBtn
        $primary
        onClick={isPastDue ? undefined : () => onSubmitAgain?.(id)}
        aria-disabled={isPastDue || undefined}
        style={isPastDue ? pausedStyle : undefined}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13" />
          <path d="M22 2L15 22L11 13L2 9L22 2Z" />
        </svg>
        Submit again
      </ActionBtn>
      </PausedTip>
    );
  };
  // Live channel atLimit overrides Ready to Release → Claim Limit Reached.
  // The card's stored runner verdict may still be Ready to Release because
  // the runner hasn't re-evaluated since the cycle filled up, but the credit
  // bucket is empty and the release path would fail. Treat the card as Claim
  // Limit Reached visually so the user sees Queue + Increase buttons.
  const item = (rawItem.status === "Ready to Release" && rawItem.channelAtLimit)
    ? { ...rawItem, status: "Claim Limit Reached" as VideoStatus }
    : rawItem;
  const [openCredit, setOpenCredit] = useState(false);
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  const isPhone = useMediaQuery("(max-width: 600px)");
  // "New" card on-screen detector — once a new card has been ~half visible for
  // ~1s, tell the parent to record it server-side as viewed (so it stops being
  // New on the NEXT page load). The badge itself stays put this session.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;
  useEffect(() => {
    if (!isNew) return;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let fired = false;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e && e.isIntersecting && e.intersectionRatio >= 0.5 && !fired && !timer) {
        timer = setTimeout(() => { fired = true; timer = null; onVisibleRef.current?.(); io.disconnect(); }, 1000);
      } else if ((!e || !e.isIntersecting) && timer) {
        clearTimeout(timer); timer = null;
      }
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => { if (timer) clearTimeout(timer); io.disconnect(); };
  }, [isNew]);
  const { pendingTimeoutDays } = React.useContext(ClaimsConfigContext);
  const { accountType } = useAccountType();
  // A paid-plan user (subscription / music creator) only sees "Not Covered" when
  // the claim's channel isn't connected — a sub covers CONNECTED channels. So the
  // "start a subscription" copy/CTA is wrong for them; they need to connect the
  // channel (or license the track) instead.
  const isPaidPlan = accountType !== AccountType.FREE;
  /* Which credit does this claim actually owe?
     
     Not "is this a subscriber" — a FREE user covering a premium track with a
     Single Licence owes the one-liner too. The backend has already decided
     (`usesPaidCredit` → PAID_CREDIT_LINE vs the full block) and hands the exact
     text down on the card, so compare against that rather than re-deriving a
     rule that would drift from it. */
  const owesOneLineCredit = (item.creditText || "").trim() === paidCreditLine;
  const isDeleted = !!item.isVideoDeleted;
  const isError = isDeleted || ERROR_STATUSES.includes(item.status);
  // While suspended, actionable cards show a single Paused/Fix button instead of
  // their normal actions — EXCEPT already-Licensed (Whitelisted) cards, which
  // are done and unaffected by the pause, so they keep their normal state.
  const showPausedButton = !!item.accountSuspended && item.status !== "Whitelisted";
  const isReviewSubStatus = item.status === "Analyzing" || item.status === "Searching";
  const reviewHint = isReviewSubStatus
    ? (item.takingLonger ? SLOW_HINTS : REVIEW_HINTS)[item.status.toLowerCase()]
    : null;
  const errorMsg = item.status === "Claim Limit Reached"
    ? `Your monthly claim limit for "${item.channelName || "this channel"}" has been reached. Queue this video for next month, or increase your monthly claim amount now.`
    : item.status === "No Claim Detected"
      ? buildNoClaimDetectedMessage(pendingTimeoutDays)
      : item.status === "Credit Text" && !item.isFreeUseSubmission
        ? creditTextMessage(owesOneLineCredit, !!item.reChecked)
      : item.status === "Credit Text" && item.isFreeUseSubmission
        ? ERROR_MESSAGES_STATIC["credit text free use"]
        : item.status === "Not Covered" && isPaidPlan
          ? "Your subscription covers this track. To resolve, connect this video's channel to your account to cover the claim or buy a YouTube video license."
          : ERROR_MESSAGES_STATIC[item.status.toLowerCase()];

  // The reason / status-hint line. On desktop it lives inside the info column
  // (right of the thumbnail); on phone/tablet it's rendered FULL-WIDTH below
  // the thumbnail+title row so it sits at the box's left edge instead of being
  // indented under the thumbnail. `marginRight` only reserves room for the
  // top-right hide button in the desktop in-column position.
  const reasonTextNode = (
    <>
      {isError && (isDeleted || errorMsg) && (
        <span style={{ display: "block", fontSize: fontSizes.body, color: isDeleted ? "rgb(255, 145, 128)" : item.status === "Ready to Release" ? colors.accent.green : item.status === "Music Use" || item.status === "Claim Limit Reached" ? "rgb(255, 145, 128)" : "#f5a623", marginTop: 6, marginRight: isCompact ? 0 : 56, lineHeight: 1.4, paddingLeft: 10, borderLeft: `2px solid ${isDeleted ? "rgba(255, 145, 128, 0.4)" : item.status === "Ready to Release" ? "rgba(52, 211, 153, 0.4)" : item.status === "Music Use" || item.status === "Claim Limit Reached" ? "rgba(255, 145, 128, 0.4)" : "rgba(245, 166, 35, 0.4)"}` }}>
          {isDeleted ? "This video was removed from YouTube. No actions possible." : errorMsg}
        </span>
      )}
      {reviewHint && (
        <span style={{ display: "block", fontSize: fontSizes.body, color: "rgba(251, 244, 236, 0.65)", marginTop: 6, marginRight: isCompact ? 0 : 56, lineHeight: 1.4, paddingLeft: 10, borderLeft: "2px solid rgba(156, 211, 253, 0.4)" }}>
          {reviewHint}
        </span>
      )}
    </>
  );
  const mobileReviewBadge = !isError && !isDeleted && item.status !== "Whitelisted" ? (
    <SubmissionStatusBadge $status={item.status} style={{ alignSelf: "flex-start" }}>
      {(item.status === "Analyzing" || item.status === "Searching") && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
      )}
      {item.status === "Searching" ? "Waiting" : item.status === "Analyzing" ? "Reviewing" : item.status}
    </SubmissionStatusBadge>
  ) : null;

  return (
    <SubmissionCard ref={cardRef} style={{ flexDirection: "column", gap: 0, alignItems: "stretch", position: "relative", ...(isCompact ? {
      padding: 0, overflow: "hidden",
      // Pop the card off the darker page: the card surface is brown.medium at
      // 0.6 alpha — make it solid + add a soft lift so the box reads clearly
      // (no new colours, just our existing brown + a shadow).
      background: colors.brown.medium,
      border: "1px solid rgba(235, 194, 142, 0.18)",
      boxShadow: "0 6px 22px rgba(0, 0, 0, 0.32)",
    } : {}) }}>
      {/* Green "New" badge — an action-needed claim the user hasn't had on
          screen yet. Top-left so it never collides with the Hide ✕ (top-right). */}
      {isNew && (
        <span style={{
          position: "absolute", top: isCompact ? 8 : 14, left: isCompact ? 8 : 14, zIndex: 2,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          padding: "4px 10px", borderRadius: 9,
          background: "#34d399", color: "#0b3b2a",
          fontFamily: fonts.number, fontSize: fontSizes.xs, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.04em",
          // letterSpacing adds a trailing gap on the right; nudge the text left
          // by that much so it reads optically centred in the pill.
          textIndent: "0.04em", lineHeight: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}><span style={{ position: "relative", top: 0 }}>New</span></span>
      )}
      {/* Hide / Unhide icon — top right corner. Unhide takes priority
          when provided so the card can be brought back from the Hidden
          tab using the same slot the Hide icon usually occupies. The
          caller decides which statuses get a Hide button — we just show
          it whenever a handler is passed. */}
      {!isDeleted && (onUnhide || onHide) && (
        <div
          style={{ position: "absolute", top: 14, right: 14, zIndex: 2 }}
          onMouseEnter={(e) => {
            const btn = e.currentTarget.querySelector("button") as HTMLElement;
            const tip = e.currentTarget.querySelector("[data-tooltip]") as HTMLElement;
            if (btn) { btn.style.background = "rgba(235, 194, 142, 0.35)"; btn.style.borderColor = "rgba(235, 194, 142, 0.5)"; btn.style.opacity = "1"; }
            if (tip) { tip.style.opacity = "1"; tip.style.transform = "translateX(-50%) translateY(0)"; }
          }}
          onMouseLeave={(e) => {
            const btn = e.currentTarget.querySelector("button") as HTMLElement;
            const tip = e.currentTarget.querySelector("[data-tooltip]") as HTMLElement;
            if (btn) { btn.style.background = "rgba(235, 194, 142, 0.08)"; btn.style.borderColor = "rgba(235, 194, 142, 0.12)"; btn.style.opacity = "0.4"; }
            if (tip) { tip.style.opacity = "0"; tip.style.transform = "translateX(-50%) translateY(4px)"; }
          }}
        >
          <button
            onClick={() => (onUnhide ? onUnhide(item.id) : onHide?.(item.id))}
            style={{
              width: isCompact ? 30 : 28, height: isCompact ? 30 : 28, borderRadius: "50%",
              // Mobile: sits on the thumbnail, so a dark chip stays legible.
              background: isCompact ? "rgba(0,0,0,0.5)" : "rgba(235, 194, 142, 0.08)",
              border: isCompact ? "1px solid rgba(255,255,255,0.22)" : "1px solid rgba(235, 194, 142, 0.12)",
              color: "#fff", fontSize: "0.85rem", cursor: "pointer", opacity: isCompact ? 1 : 0.4,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.3s ease", padding: 0, lineHeight: 1,
            }}
          >
            {onUnhide ? <UnhideIcon /> : <HiddenIcon />}
          </button>
          <div
            data-tooltip
            style={{
              position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%) translateY(4px)",
              marginTop: 6, whiteSpace: "nowrap",
              fontFamily: fonts.body, fontSize: fontSizes.xs, fontWeight: 500,
              color: colors.text.primary, background: colors.surface.modal,
              border: "1px solid rgba(235, 194, 142, 0.2)", borderRadius: "6px",
              padding: "5px 10px", pointerEvents: "none",
              opacity: 0, transition: "opacity 0.2s ease, transform 0.2s ease",
            }}
          >
            {onUnhide ? (unhideTargetLabel ?? "Unhide") : "Move to Hidden"}
          </div>
        </div>
      )}

      {/* ── Mobile "Card A": full-width thumbnail (hide icon overlays it via the
            absolute button above) + a status-tinted meta strip with the claim #
            on the left and the white envelope on the right. ── */}
      {isCompact && (
        <div style={{ width: "100%", aspectRatio: "16 / 9", background: "rgba(75,55,39,0.5)", ...(isDeleted ? { filter: "grayscale(1)", opacity: 0.5 } : {}) }}>
          <img src={`https://img.youtube.com/vi/${getVideoId(item.videoUrl)}/mqdefault.jpg`} alt={item.videoTitle} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
      )}

      {/* Body — padded wrapper (mobile has card padding 0; desktop card owns its padding). */}
      <div style={{ padding: isCompact ? "14px 16px 16px" : 0 }}>
      {/* Top row: thumbnail + info + badge */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: isCompact ? 0 : 16 }}>
        {!isCompact && (
        <VideoThumb style={isDeleted ? { filter: "grayscale(1)", opacity: 0.4 } : undefined}>
          <img
            src={`https://img.youtube.com/vi/${getVideoId(item.videoUrl)}/mqdefault.jpg`}
            alt={item.videoTitle}
          />
        </VideoThumb>
        )}
        <SubmissionInfo>
          {(() => {
            // When the backend had no real title at submit time (deleted
            // / private video where oEmbed didn't return metadata) it falls
            // back to videoUrl as the title. Detect that and show a big "—"
            // placeholder instead of the URL — the URL still appears in
            // the meta row below, so no info is lost.
            const hasRealTitle = !!item.videoTitle && item.videoTitle !== item.videoUrl;
            const titleContent = hasRealTitle
              ? item.videoTitle
              : <span style={{ fontSize: "1.4em", opacity: 0.5 }}>—</span>;
            return (
              <SubmissionTitle style={isDeleted ? { opacity: 0.4 } : undefined}>
                {item.videoUrl && !isDeleted ? (
                  <a href={item.videoUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{titleContent}</a>
                ) : titleContent}
              </SubmissionTitle>
            );
          })()}
          {(() => {
            const hasChannel = !!item.channelName;
            const hasTrack = !!item.trackTitle && item.trackTitle !== "—" && item.status !== "No Claim Detected";
            if (!hasChannel && !hasTrack) {
              return (
                <SubmissionMeta style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <a
                    href={item.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "none" }}
                    onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                  >{item.videoUrl}</a>
                </SubmissionMeta>
              );
            }
            return (
              <SubmissionMeta style={isDeleted ? { opacity: 0.4 } : undefined}>
                {hasChannel && (
                  <>on <strong style={{ fontWeight: 500 }}>{item.channelUrl ? (
                    <a href={item.channelUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{item.channelName}</a>
                  ) : item.channelName}</strong></>
                )}
                {hasChannel && hasTrack && <span style={{ margin: "0 5px", fontSize: "1.6em", lineHeight: 0, position: "relative", top: "-0.04em", verticalAlign: "middle" }}>·</span>}
                {hasTrack && (
                  <>
                    <strong style={{ fontWeight: 500 }}><a href={`/music?q=${encodeURIComponent(item.trackTitle)}`} style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{item.trackTitle}</a></strong>
                    {item.trackArtist && item.trackArtist !== "—" && <> by <a href={`/music?q=${encodeURIComponent(item.trackArtist)}`} style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{item.trackArtist}</a></>}
                  </>
                )}
                {!isError && item.status === "Whitelisted" && item.releasePending && (hasChannel || hasTrack) && (
                  <>
                    <span style={{ margin: "0 5px", fontSize: "1.6em", lineHeight: 0, position: "relative", top: "-0.04em", verticalAlign: "middle" }}>·</span>
                    The claim will be removed from your video within 24 hours — nothing further is needed from you
                  </>
                )}
                {!isError && item.status === "Whitelisted" && !item.releasePending && item.releasedAt && (hasChannel || hasTrack) && (
                  <>
                    <span style={{ margin: "0 5px", fontSize: "1.6em", lineHeight: 0, position: "relative", top: "-0.04em", verticalAlign: "middle" }}>·</span>
                    Claim removed on {new Date(item.releasedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </>
                )}
              </SubmissionMeta>
            );
          })()}
          {/* Desktop: reason line lives in the info column. Phone/tablet
              renders it full-width below the row (see after this block). */}
          {!isCompact && reasonTextNode}
          {/* Whitelisted / Resolved cards render the status pill INSIDE the
              info block, below the track meta, so the pill aligns with the
              text rather than the thumbnail. */}
          {!isError && !isDeleted && item.status === "Whitelisted" && (
            item.creditMissing ? (
              // Licensed video whose credit a post-release re-check found missing.
              <SubmissionStatusBadge $status="Credit Missing" style={{ marginTop: 10, alignSelf: "flex-start" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Credit missing
              </SubmissionStatusBadge>
            ) : item.licenceVoidReason ? (
              // A voided certificate must not read "Licensed" — the pill is the
              // status, so it is where the state belongs.
              <SubmissionStatusBadge $status="Void" style={{ marginTop: 10, alignSelf: "flex-start" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                Void
              </SubmissionStatusBadge>
            ) : (
              <SubmissionStatusBadge $status="Whitelisted" style={{ marginTop: 10, alignSelf: "flex-start" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Licensed
              </SubmissionStatusBadge>
            )
          )}
          {/* Only surface WHEN a breach was detected (frozen failure date, ToU
              §17.4) — the customer needs that to act. The "credit last verified"
              date is admin-only info and is NOT shown on the user's card. */}
          {!isError && !isDeleted && item.status === "Whitelisted" && item.creditMissing && item.lastCreditCheckAt && (
            <div style={{ marginTop: 6, fontSize: "0.75rem", color: "#e6a3a3" }}>
              Credit missing since {new Date(item.lastCreditCheckAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </SubmissionInfo>
        {!isCompact && !isError && item.status !== "Whitelisted" && (
          <SubmissionStatusBadge
            $status={isDeleted ? "Private Video" : item.status}
            style={{
              marginLeft: 16,
              ...(isDeleted ? { opacity: 0.5 } : {}),
            }}
          >
            {!isDeleted && (item.status === "Analyzing" || item.status === "Searching") && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            )}
            {isDeleted ? "Removed" : item.status === "Searching" ? "Waiting" : item.status === "Analyzing" ? "Reviewing" : item.status}
          </SubmissionStatusBadge>
        )}
      </div>

      {/* Phone/tablet: status badge + reason line FULL-WIDTH below the
          thumbnail row, so they sit at the box's left edge instead of being
          indented under the thumbnail. */}
      {isCompact && (mobileReviewBadge || isError || reviewHint) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {mobileReviewBadge}
          {reasonTextNode}
        </div>
      )}

      {/* Action row: action buttons on the left, #N pill + envelope on the
          right. Always renders when either action buttons apply for the
          status OR the card has an event id to display. */}
      {(isDeleted || item.status === "Ready to Release" || item.status === "Not Covered" || item.status === "Music Use" || item.status === "Credit Text" || item.status === "Private Video" || item.status === "No Claim Detected" || item.status === "Claim Limit Reached" || (item.status === "Whitelisted" && !!onOpenCertificate) || item.eventSequentialId !== undefined || item.accountSuspended) && (
        <div style={{ display: "flex", flexDirection: isCompact ? "column" : "row", alignItems: isCompact ? "stretch" : "center", flexWrap: isCompact ? "nowrap" : "wrap", gap: 10, marginTop: 16 }}>
          {/* Account paused (ToU §17.4): every card shows a single Paused button
              that opens the Account Issues modal; nothing else can be actioned
              until the credit is restored. */}
          {showPausedButton && (
            // Every actionable claim while paused — a muted gray "Paused" pill
            // that opens the Account Issues modal. (The credit-missing Licensed
            // card gets its own "Fix issue" button below, not this.)
            <ActionBtn
              onClick={() => window.dispatchEvent(new CustomEvent("as-open-recheck"))}
              style={{ borderColor: "rgba(251, 244, 236, 0.15)", color: colors.text.muted }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
              Paused
            </ActionBtn>
          )}
          {/* Action buttons — suppressed for parked (Queued, read-only) cards
              and while the account is paused; the event-id pill still renders. */}
          {!parked && !showPausedButton && (<>
          {/* The licence certificate for a resolved claim. Read-only, so it
              stays available while a subscription is PAST_DUE — freezing the
              document someone was already granted would be a different
              promise than freezing new releases. */}
          {!isDeleted && item.status === "Whitelisted" && onOpenCertificate && (
            <ActionBtn $primary onClick={() => onOpenCertificate(item.id)}>
              {/* The same document mark the Buy License button carries — one
                  licence icon across the page. */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
              License Text
            </ActionBtn>
          )}
          {/* Licensed video whose credit a re-check found missing (ToU §17.3) —
              a blue "Fix issue" that opens the Account Issues modal, right on
              the Licensed card (the only place this can be needed). */}
          {!isDeleted && item.status === "Whitelisted" && item.creditMissing && (
            <ActionBtn $primary onClick={() => window.dispatchEvent(new CustomEvent("as-open-recheck"))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
              Fix issue
            </ActionBtn>
          )}
          {/* The licence number, beside the button that opens it — so the card
              carries the reference a customer would quote at us without having
              to open the document to find it. Click to copy, same as the #N
              pill in the footer. */}
          {!isDeleted && item.status === "Whitelisted" && item.licenceCode && (
            <LicenceNumber code={item.licenceCode} voidReason={item.licenceVoidReason} />
          )}

          {/* Dismiss button for deleted videos */}
          {isDeleted && onDismissDeleted && (
            <ActionBtn $danger onClick={() => onDismissDeleted(item.id)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Dismiss
            </ActionBtn>
          )}

          {/* Resolve button for "Ready to Release" (terminal pre-release state, all checks passed).
              When the free-tier user has an unconnected license matching this card's track,
              the button label flips to "License Now (uses 1 License)" and calls a different
              backend endpoint that attaches the license to this video before releasing.
              Two transient post-click states driven by `resolveStage`:
                "success" — green button, "Claim resolved!"
                "moving"  — greyed-out, spinner, "Moving to resolved tab…" */}
          {!isDeleted && item.status === "Ready to Release" && (
            resolveStage === "success" ? (
              <ActionBtn
                $primary
                disabled
                style={{
                  background: "linear-gradient(135deg, #28a87a, #1f8c63)",
                  color: "#fff",
                  cursor: "default",
                  transition: "background 0.3s ease, opacity 0.3s ease",
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: "#fff" }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Claim licensed!
              </ActionBtn>
            ) : resolveStage === "moving" ? (
              <ActionBtn
                $primary
                disabled
                style={{
                  background: "rgba(255, 255, 255, 0.08)",
                  borderColor: "rgba(255, 255, 255, 0.15)",
                  color: "rgba(255, 255, 255, 0.65)",
                  cursor: "default",
                  opacity: 0.85,
                  transition: "background 0.3s ease, opacity 0.3s ease",
                }}
              >
                <ResolveSpinner />
                Moving to &ldquo;Licensed&rdquo;…
              </ActionBtn>
            ) : (
              <PausedTip on={!!isPastDue}>
              <ActionBtn
                $primary
                onClick={isPastDue ? undefined : () => onResolve?.(item.id)}
                aria-disabled={isPastDue || undefined}
                style={{ background: "linear-gradient(135deg, #28a87a, #1f8c63)", color: "#fff", ...(isPastDue ? pausedStyle : {}) }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: "#fff" }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {/* What licensing this claim costs, stated on the button. Free Use
                    costs nothing; a subscription spends one of the channel's
                    monthly claim credits. The single-licence case has its own
                    button (below), so it isn't a third cost here. */}
                {item.useLicenseAvailable
                  ? "License Now (uses 1 License)"
                  : accountType === AccountType.FREE
                    ? "License Now (Free)"
                    : "License Now (1 Credit)"}
              </ActionBtn>
              </PausedTip>
            )
          )}

          {/* Credit Missing — a released video whose Credit Text a re-check
              found removed. Opens the re-check modal to restore + re-verify. */}
          {item.status === "Credit Missing" && (
            <ActionBtn $primary onClick={() => window.dispatchEvent(new CustomEvent("as-open-recheck"))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
              </svg>
              Fix issue
            </ActionBtn>
          )}

          {/* Not Covered — two distinct paths so the user picks the
              outcome they want without having to read both tabs of the
              license modal. "Start Subscription" opens the plan tab;
              "Buy License" opens the single-track tab pre-selected for
              this track. Both call the same `onOpenLicense` handler
              with a `preferMode` hint. Both buttons render for any
              plan — even subscribers can hit a stranded Not Covered
              card via an edge case and might want either path. */}
          {item.status === "Not Covered" && item.requiredTier && (
            <>
              {!isPaidPlan && (
                <ActionBtn $primary onClick={() => onOpenLicense?.(item, "subscription")}>
                  {/* Our ◆ diamond — the brand subscription mark (not a crown),
                      blue to match the other gold-button icons. */}
                  <svg viewBox="0 0 24 24" fill="#60a3d7">
                    <path d="M6 3h12l4 6-10 13L2 9Z" />
                  </svg>
                  Start Subscription
                </ActionBtn>
              )}
              <ActionBtn $primary onClick={() => onOpenLicense?.(item, "single")}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                Buy License
              </ActionBtn>
              {/* Escape hatch — both real actions cost money; a user who
                  won't subscribe or buy needs an explicit way to clear it.
                  Hidden in the Hidden tab itself (onHide not passed there). */}
              {onHide && (
                <ActionBtn onClick={() => onHide(item.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                  Move to Hidden
                </ActionBtn>
              )}
            </>
          )}

          {/* Queue or increase claims for "Claim Limit Reached". Queue
              button has the same two-stage transition as Resolve: brief
              green "Queued!" confirmation → greyed spinner →
              fade-and-collapse. No tab switch. */}
          {item.status === "Claim Limit Reached" && (
            <>
              <PausedTip on={!!isPastDue}>
              <ActionBtn
                $primary
                onClick={isPastDue ? undefined : () => onManageSub?.()}
                style={queueStage ? disabledSiblingStyle : (isPastDue ? pausedStyle : undefined)}
                disabled={!!queueStage}
                aria-disabled={(!!queueStage || isPastDue) || undefined}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
                Increase Monthly Claims
              </ActionBtn>
              </PausedTip>
              {queueStage === "success" ? (
                <ActionBtn $primary disabled style={{ cursor: "default", transition: "background 0.3s ease, opacity 0.3s ease" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Queued!
                </ActionBtn>
              ) : queueStage === "moving" ? (
                <ActionBtn $primary disabled style={{ background: "rgba(255, 255, 255, 0.08)", borderColor: "rgba(255, 255, 255, 0.15)", color: "rgba(255, 255, 255, 0.65)", cursor: "default", opacity: 0.85, transition: "background 0.3s ease, opacity 0.3s ease" }}>
                  <ResolveSpinner />
                  Moving to &ldquo;Queued&rdquo;…
                </ActionBtn>
              ) : (
                <PausedTip on={!!isPastDue}>
                <ActionBtn
                  onClick={isPastDue ? undefined : () => onQueue?.(item.id)}
                  aria-disabled={isPastDue || undefined}
                  style={isPastDue ? pausedStyle : undefined}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  Queue for Next Month
                </ActionBtn>
                </PausedTip>
              )}
            </>
          )}

          {/* Apply for music partner for "Music Use".
              Navigates to /music-creator#applicationForm so the page's
              hash-aware effect smooth-scrolls down to the contact form
              once the page mounts. SPA navigation (not full reload) so
              the scroll lands cleanly. */}
          {item.status === "Music Use" && (
            <>
              <ActionBtn $primary onClick={() => navigate("/music-creator#applicationForm")}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                Apply for Music Creator Program
              </ActionBtn>
              {/* Escape hatch — the only positive action here is "apply",
                  which not every user wants. Give them an explicit way to
                  clear the card so the Action Required count stays meaningful.
                  Hidden in the Hidden tab itself (onHide not passed there). */}
              {onHide && (
                <ActionBtn onClick={() => onHide(item.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                  Move to Hidden
                </ActionBtn>
              )}
            </>
          )}

          {/* Credit text toggle + submit again for "Credit Text" status.
              Show-Credit-Text greys out while the submit animates so it
              reads as "wait, this one is going". */}
          {item.status === "Credit Text" && showCreditText && (() => {
            const showCreditBtn = (
              <ActionBtn
                key="show-credit"
                onClick={() => setOpenCredit(!openCredit)}
                style={submitStage ? disabledSiblingStyle : undefined}
                disabled={!!submitStage}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
                </svg>
                {isPhone ? (
                  <span>Credit Text</span>
                ) : (
                  <span style={{ position: "relative" }}>
                    <span style={{ visibility: "hidden" }}>Show Credit Text</span>
                    <span style={{ position: "absolute", left: 0, top: 0 }}>{openCredit ? "Hide Credit Text" : "Show Credit Text"}</span>
                  </span>
                )}
              </ActionBtn>
            );
            const submitBtn = <React.Fragment key="submit">{renderSubmitAgain(item.id)}</React.Fragment>;
            // Mobile: Submit again first, Show Credit Text LAST so the credit
            // panel opens directly below it. Desktop keeps the original order.
            return isCompact ? <>{submitBtn}{showCreditBtn}</> : <>{showCreditBtn}{submitBtn}</>;
          })()}

          {/* Private / Deleted video — two choices. "Video was deleted"
              greys out while submit is animating. */}
          {!isDeleted && item.status === "Private Video" && (
            <>
              {renderSubmitAgain(item.id)}
              <ActionBtn
                $danger
                onClick={() => onConfirmDeleted?.(item.id)}
                style={submitStage ? disabledSiblingStyle : undefined}
                disabled={!!submitStage}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Video was deleted
              </ActionBtn>
            </>
          )}

          {/* Dismiss + Re-Submit for "No Claim Detected" — wait window
              expired. Remove sibling greys out while submit is animating. */}
          {item.status === "No Claim Detected" && (
            <>
              {renderSubmitAgain(item.id)}
              <ActionBtn
                $danger
                onClick={() => onHide?.(item.id)}
                style={submitStage ? disabledSiblingStyle : undefined}
                disabled={!!submitStage}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                </svg>
                Remove
              </ActionBtn>
            </>
          )}

          </>)}
          {/* Desktop only — on mobile the #id + envelope live in the strip
              under the thumbnail. */}
          {!isCompact && <div style={{ flex: 1 }} />}
          {!isCompact && item.eventSequentialId !== undefined && (
            <>
              <EventIdPill id={item.eventSequentialId} />
            </>
          )}
        </div>
      )}

      {/* A refused resolve, said where the button was pressed. */}
      {resolveError && <RefusalNotice message={resolveError} style={{ margin: "10px 0 0" }} />}

      {/* Credit text expand — smooth. Inside the body wrapper, so opening it
          pushes the bottom strip (claim # + envelope) further down. */}
      {item.status === "Credit Text" && <CreditTextPanel open={openCredit} artist={item.trackArtist} title={item.trackTitle} creditText={item.creditText} />}
      </div>

      {/* ── Mobile: claim # + envelope strip at the BOTTOM of the card,
            full-width edge-to-edge. ── */}
      {isCompact && item.eventSequentialId !== undefined && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderTop: "1px solid rgba(235, 194, 142, 0.08)" }}>
          <span style={{ fontFamily: fonts.number, fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.3px", color: "#fff" }}>#{item.eventSequentialId}</span>
        </div>
      )}
    </SubmissionCard>
  );
};

/* Search toolbar removed for now — will add back when needed */

/** Connect channel CTA banner — shown when no channels connected.
 *  On desktop the bouncing up-arrow points at the header "Channels" button (the
 *  toggle that opens the add panel). On phone/tablet that button is hidden and
 *  the add panel is shown inline right below this banner, so the arrow would
 *  point at nothing — `hideArrow` drops it there. */
const ConnectChannelBanner: React.FC<{ multiple?: boolean; hideArrow?: boolean }> = ({ multiple, hideArrow }) => (
  <div data-banner-surface style={{
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "20px 24px",
    // On mobile the inline flow drops the header button, so the heading sits
    // closer — pull the banner up to tighten the (global 24px) gap below the title.
    marginTop: hideArrow ? -12 : 0,
    marginBottom: hideArrow ? 12 : 24,
    ...bannerSurface("rgba(156, 211, 253, 0.06)"),
    border: "1px solid rgba(156, 211, 253, 0.15)",
    borderRadius: "12px",
  }}>
    <div style={{
      width: 44,
      height: 44,
      borderRadius: "50%",
      background: "rgba(156, 211, 253, 0.1)",
      border: "1px solid rgba(156, 211, 253, 0.2)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill={colors.accent.blue} style={{ flexShrink: 0 }}>
        <path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500, color: colors.text.primary }}>
        Connect your YouTube channel{multiple ? "s" : ""}{hideArrow ? " below" : ""} to get started
      </div>
    </div>
    {!hideArrow && (
      <>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, animation: "bounceUp 1.5s ease-in-out infinite" }}>
          <path d="M12 19V5" />
          <path d="M5 12l7-7 7 7" />
        </svg>
        <style>{`@keyframes bounceUp { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }`}</style>
      </>
    )}
  </div>
);

/** Submit Video URL input — mirrors the channel connect flow: show only the
    input, then on a valid URL resolve the video (server-side oEmbed) and show a
    confirm card with the thumbnail + title + channel and Cancel/Submit. Same on
    every device. */
const SubmitVideoInput: React.FC<{ onSubmit?: (url: string, videoLooksDeleted: boolean) => void; existingVideoIds?: Set<string> }> = ({ onSubmit, existingVideoIds }) => {
  // Below this width a pasted URL and two buttons cannot share a row.
  const isNarrow = useMediaQuery("(max-width: 860px)");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [preview, setPreview] = useState<ResolvedVideoDto | null>(null);
  const timerRef = useRef<number | null>(null);
  const seqRef = useRef(0);

  const resolve = async (raw: string) => {
    const trimmed = raw.trim();
    const id = getVideoId(trimmed);
    if (!id) return; // wait for a complete URL — no error while still typing
    if (existingVideoIds && existingVideoIds.has(id)) {
      setError("You already submitted this video.");
      return;
    }
    const seq = ++seqRef.current;
    setResolving(true);
    setError(null);
    try {
      const res = await resolveClaimVideo(trimmed);
      if (seq !== seqRef.current) return; // superseded by newer input
      setPreview(res); // res.exists may be false → preview card offers submit anyway
    } catch (err) {
      if (seq !== seqRef.current) return;
      const parsed = parseClaimSubmissionError(err);
      setError(parsed.message || "Couldn't look up that video. Check the URL and try again.");
    } finally {
      if (seq === seqRef.current) setResolving(false);
    }
  };

  const handleChange = (value: string) => {
    setUrl(value);
    if (preview) setPreview(null);
    if (error) setError(null);
    seqRef.current++; // invalidate any in-flight lookup
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (getVideoId(value.trim())) timerRef.current = window.setTimeout(() => { void resolve(value); }, 550);
  };

  const handleClear = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    seqRef.current++;
    setUrl("");
    setPreview(null);
    setResolving(false);
    setError(null);
  };

  const doSubmit = async () => {
    const trimmed = url.trim();
    if (!trimmed || submitting) return;
    const id = getVideoId(trimmed);
    if (id && existingVideoIds && existingVideoIds.has(id)) {
      setError("You already submitted this video.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await submitClaimUrl(trimmed);
      // videoLooksDeleted (backend oEmbed check) tells the parent which tab to
      // switch to (Action Required vs Under Review).
      const videoLooksDeleted = res.kind === "pending" && !!res.videoLooksDeleted;
      onSubmit?.(trimmed, videoLooksDeleted);
      handleClear();
    } catch (err) {
      const parsed = parseClaimSubmissionError(err);
      setError(parsed.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const showClear = !!url && !preview && !resolving;
  // Once a video resolves, the confirm details join the input inside ONE box
  // rather than appearing as a second box below it. The two buttons sit at the
  // bottom of that box — on a wide screen at their own width on the left, and
  // full-width once the box is narrow enough that two labels look stranded.
  const confirming = !!preview;
  const buttonsInRow = confirming && !isNarrow;

  const actionButtons = (
    <>
      {/* In the row the two keep a fixed, generous width so they read as a
          pair of buttons rather than two labels squeezed beside the URL. */}
      <ActionBtn onClick={handleClear} style={{ flex: buttonsInRow ? "0 0 auto" : 1, minWidth: buttonsInRow ? 120 : undefined, justifyContent: "center", height: 40 }} disabled={submitting}>Cancel</ActionBtn>
      <ActionBtn $primary onClick={doSubmit} style={{ flex: buttonsInRow ? "0 0 auto" : 1, minWidth: buttonsInRow ? 150 : undefined, justifyContent: "center", height: 40, fontWeight: 700 }} disabled={submitting}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13" />
          <path d="M22 2L15 22L11 13L2 9L22 2Z" />
        </svg>
        {submitting ? "Submitting…" : "Submit"}
      </ActionBtn>
    </>
  );

  return (
    // More air below on desktop — the first claim card sat almost against the
    // box, so the two read as one stack.
    <div style={{ marginBottom: isNarrow ? 12 : 24 }}>
      <div
        style={{
          // The input's own frame and fill, grown to hold the confirm details —
          // so the box a URL was pasted into is the box that confirms it.
          borderRadius: 8,
          border: confirming ? "1px solid rgba(186, 144, 92, 0.2)" : "none",
          background: confirming ? "rgba(255, 255, 255, 0.06)" : "transparent",
          transition: "background 0.22s ease",
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, padding: confirming ? 6 : 0 }}>
          <svg className="yt-icon" width="19" height="19" viewBox="0 0 24 24" fill="rgba(251, 244, 236, 0.5)" style={{ position: "absolute", left: confirming ? 20 : 14, top: "50%", transform: "translateY(-50%)", flexShrink: 0, transition: "fill 0.15s ease", pointerEvents: "none" }}>
            <path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
          <SearchInput
            value={url}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
            placeholder="Paste YouTube video URL…"
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter") { if (timerRef.current) window.clearTimeout(timerRef.current); if (preview) { void doSubmit(); } else { void resolve(url); } } }}
            style={{
              flex: 1,
              minWidth: 0,
              paddingLeft: "44px",
              paddingRight: (showClear || resolving) ? "84px" : "16px",
              height: confirming ? 40 : 48,
              fontSize: fontSizes.body,
              // The shell owns the frame while confirming, so the input drops
              // its own — two nested borders read as two boxes again.
              ...(confirming ? { border: "none", background: "transparent" } : {}),
            }}
            onFocus={(e) => { const icon = e.currentTarget.parentElement?.querySelector(".yt-icon") as SVGElement; if (icon) icon.style.fill = "#fff"; }}
            onBlur={(e) => { const icon = e.currentTarget.parentElement?.querySelector(".yt-icon") as SVGElement; if (icon) icon.style.fill = "rgba(251, 244, 236, 0.5)"; }}
          />
          {resolving && (
            <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontFamily: fonts.body, fontSize: fontSizes.sm, color: colors.text.muted, pointerEvents: "none" }}>Checking…</span>
          )}
          {showClear && (
            <button
              type="button"
              aria-label="Clear"
              onClick={handleClear}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = colors.text.muted; e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"; }}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", padding: 0, background: "rgba(255, 255, 255, 0.1)", border: "none", cursor: "pointer", color: colors.text.muted, transition: "color 0.15s ease, background 0.15s ease" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* What we resolved, inside the same box — the thing being confirmed. */}
        {preview && (
          <div
            ref={(el) => { if (el && !el.dataset.animated) { el.dataset.animated = "1"; el.style.opacity = "0"; el.style.transition = "opacity 0.22s ease"; requestAnimationFrame(() => { el.style.opacity = "1"; }); } }}
            style={{ padding: "12px", borderTop: "1px solid rgba(186, 144, 92, 0.2)", marginTop: 6 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              {preview.exists && preview.thumbnailUrl ? (
                <img src={preview.thumbnailUrl} alt="" style={{ width: 96, height: 54, borderRadius: 8, flexShrink: 0, objectFit: "cover", background: "rgba(235, 194, 142, 0.15)" }} />
              ) : (
                <div style={{ width: 96, height: 54, borderRadius: 8, flexShrink: 0, background: "rgba(235, 194, 142, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="rgba(251, 244, 236, 0.4)"><path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.3, gap: 2 }}>
                <span style={{ fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 600, color: colors.text.primary, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {preview.title || (preview.exists ? "YouTube video" : "Preview unavailable")}
                </span>
                <span style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: colors.text.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {preview.channelName || (preview.exists ? "" : "We couldn't load a preview — you can still submit.")}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 12, justifyContent: "flex-start" }}>{actionButtons}</div>
            {error && <RefusalNotice message={error} style={{ marginTop: 12 }} />}
          </div>
        )}
      </div>

      {/* No preview yet — the URL itself was rejected, so it sits under the input. */}
      {error && !preview && <RefusalNotice message={error} style={{ marginTop: 10 }} />}
    </div>
  );
};

/** Wrapper that handles fade-out then height-collapse animation */
/** Reverse of HideWrapper: starts at height 0 + opacity 0, expands down then fades in. */
const AppearWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [shown, setShown] = useState(false);

  // useLayoutEffect (not useEffect) so the measure-and-collapse-and-queue-
  // animation all runs synchronously after the DOM mutation but BEFORE the
  // browser paints — eliminating the brief flash where the card showed at
  // natural height before the height-0 collapse.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measure natural height (the JSX paints at height 0; this temporarily
    // flips to auto, reads, and immediately re-collapses — all pre-paint).
    el.style.height = "auto";
    const target = el.offsetHeight;
    el.style.height = "0px";
    // Force reflow so the transition has a starting frame at 0.
    void el.offsetHeight;
    // Expand
    el.style.transition = "height 0.3s ease";
    el.style.height = `${target}px`;
    const onEnd = () => {
      el.style.height = "auto";
      el.style.overflow = "visible";
      el.removeEventListener("transitionend", onEnd);
      setExpanded(true);
      // Fade content in slightly after height finishes
      requestAnimationFrame(() => setShown(true));
    };
    el.addEventListener("transitionend", onEnd);
    return () => { el.removeEventListener("transitionend", onEnd); };
  }, []);

  return (
    // height/overflow set inline on first render so the JSX-painted state
    // is already collapsed; the layout effect above takes over before the
    // browser commits the next frame.
    <div ref={ref} style={{ height: 0, overflow: "hidden" }}>
      <div
        style={{
          transition: "opacity 0.3s ease, transform 0.3s ease",
          opacity: shown ? 1 : 0,
          transform: expanded ? "none" : "translateX(-20px) scale(0.97)",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const HideWrapper: React.FC<{ isFading: boolean; isCollapsing: boolean; children: React.ReactNode }> = ({ isFading, isCollapsing, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isCollapsing && ref.current) {
      const el = ref.current;
      // Freeze at current height
      el.style.height = `${el.offsetHeight}px`;
      el.style.transition = "none";
      void el.offsetHeight; // force reflow
      // Animate to 0
      el.style.transition = "height 0.3s ease, margin 0.3s ease";
      el.style.height = "0px";
      el.style.marginBottom = "0px";
      el.style.overflow = "hidden";
    }
  }, [isCollapsing]);

  return (
    <div
      ref={ref}
      style={{
        transition: "opacity 0.35s ease, transform 0.35s ease",
        opacity: isFading || isCollapsing ? 0 : 1,
        transform: isFading ? "scale(0.97) translateX(20px)" : "none",
      }}
    >
      {children}
    </div>
  );
};

/** Per-channel credit row used by the channel filter + warning banners.
    Derived from the slot list (occupied slots only) so legacy per-channel
    consumers keep working under the slot-based model. */
type ChannelCreditRow = { youtubeChannelId: string; channelName: string | null; used: number; limit: number; channelThumbnailUrl?: string | null };

/** Adapt the slot list → the legacy per-channel credit rows the filter
    dropdown + claim-warning banners consume (occupied slots only). */
const slotsToChannelCredits = (
  slots: ChannelSlot[],
): Array<{ youtubeChannelId: string; channelName: string | null; used: number; limit: number; cycleEnd: string | null }> =>
  slots
    .filter((s) => s.channel)
    .map((s) => ({
      youtubeChannelId: s.channel!.youtubeChannelId,
      channelName: s.channel!.channelName,
      used: s.used,
      limit: s.limit,
      cycleEnd: s.cycleEnd,
    }));

/** Usage colour: green → amber → red as the cycle fills. */
const channelUsageColor = (used: number, limit: number): string => {
  const pct = limit > 0 ? used / limit : 0;
  // Thresholds + shades match renderUsageBar's bar so the filter-button count
  // and the slot-box bar always read the same colour (green < 80% ≤ amber < 100% = red).
  if (pct >= 1) return colors.accent.red;
  if (pct >= 0.8) return "#f5a623";
  return colors.accent.green;
};

/**
 * Channel filter — sits in front of the status tabs (V5). One channel →
 * a static label (name + usage, no dropdown). Multiple → a dropdown whose
 * button shows the selected channel + usage (or "All channels"), and whose
 * list shows every channel with its per-channel claim usage. Selecting a
 * channel scopes the claim list + tab counts to it.
 */
const ChannelFilterDropdown: React.FC<{
  channels: ChannelCreditRow[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Manage-tab callbacks (forwarded to ChannelsPanel inside the dropdown). */
  onChannelChange?: () => void;
  onChannelDisconnected?: (youtubeChannelId: string) => void;
  onManageSubscription?: () => void;
  isPastDue?: boolean;
  /** Channel cap — when channels.length >= cap, a "Need more channels?" link
   *  shows below the Filter list (mirrors the one in the Manage panel). */
  cap?: number;
  /** Per-channel + total "new claims" counts (claims the user hasn't had on
   *  screen yet). Drives the green badge on the trigger + each channel row. */
  newCounts?: { byChannel: Record<string, number>; total: number };
}> = ({ channels, selectedId, onSelect, onChannelChange, onChannelDisconnected, onManageSubscription, isPastDue, cap = 0, newCounts }) => {
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  const [open, setOpen] = useState(false);
  // The dropdown hosts two tabs: Filter (pick a channel) + Manage (add/remove).
  const [tab, setTab] = useState<"filter" | "manage">("filter");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Element | null;
      // A portalled modal opened from inside the dropdown (e.g. the disconnect
      // confirm) lives on document.body, so its clicks fall OUTSIDE the dropdown
      // ref — don't treat those as an outside-click, or closing the modal would
      // also collapse the dropdown behind it.
      if (t && t.closest("[data-claims-modal]")) return;
      if (ref.current && !ref.current.contains(t as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const BLUE = "#9cd3fd";
  const avatar = (url: string | null | undefined, size = 22) => url
    ? <img src={url} alt="" style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
    : <span style={{ width: size, height: size, borderRadius: "50%", background: "rgba(235, 194, 142, 0.3)", flexShrink: 0, display: "inline-block" }} />;
  // "All channels" glyph — blue (matches the blue accent bar).
  const allIcon = (size = 22) => (
    <span style={{ width: size, height: size, borderRadius: "50%", background: "rgba(156, 211, 253, 0.2)", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <svg width={Math.round(size * 0.62)} height={Math.round(size * 0.62)} viewBox="0 0 24 24" fill={BLUE}>
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.5 15.5v-7l6.3 3.5-6.3 3.5z" />
      </svg>
    </span>
  );
  const usageSpan = (c: ChannelCreditRow) => (
    <span style={{ fontFamily: fonts.number, fontSize: fontSizes.sm, fontWeight: 600, lineHeight: 1, color: channelUsageColor(c.used, c.limit) }}>
      {c.used}/{c.limit}
    </span>
  );
  // Green "new claims" pill — count of action-needed claims the user hasn't
  // had on screen yet (per channel, or the total on the trigger / All row).
  const newBadge = (n: number | undefined) => (n && n > 0) ? (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", height: 18, padding: "0 7px", borderRadius: 9, background: "#34d399", color: "#0b3b2a", fontFamily: fonts.number, fontSize: "0.7rem", fontWeight: 700, lineHeight: 1, flexShrink: 0, whiteSpace: "nowrap" }}>{n} new</span>
  ) : null;
  const total = newCounts?.total ?? 0;
  const byChannel = newCounts?.byChannel ?? {};
  const chev = (stroke: string) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  // Channel filter trigger — rounded control with a BLUE left accent bar +
  // avatar; deliberately distinct from the neutral status tabs. `c` null = All.
  const buildTrigger = (c: ChannelCreditRow | null, interactive: boolean): React.ReactElement => {
    const isAll = c === null;
    const label = isAll ? "All channels" : (c!.channelName || "Channel");
    const Tag: any = interactive ? "button" : "div";
    // Opening always returns to the picker (Filter view); Edit Channels in the
    // footer switches to the manage panel.
    const onClick = interactive ? () => { if (!open) setTab("filter"); setOpen((o) => !o); } : undefined;
    return (
      <Tag
        onClick={onClick}
        style={{
          // Full-width on phone/tablet; content-sized inline on desktop.
          display: isCompact ? "flex" : "inline-flex", width: isCompact ? "100%" : undefined,
          alignItems: "center", gap: 10, lineHeight: 1, whiteSpace: "nowrap",
          fontFamily: fonts.number, fontSize: fontSizes.body, fontWeight: 500, cursor: interactive ? "pointer" : "default",
          padding: isCompact ? "11px 14px" : "7px 13px",
          // When open, square the bottom corners + drop the bottom border so the
          // trigger merges with the dropdown below into one connected area.
          borderRadius: (interactive && open) ? (isCompact ? "10px 10px 0 0" : "9px 9px 0 0") : (isCompact ? 10 : 9),
          overflow: "hidden",
          background: "rgba(255,255,255,.05)", border: `1px solid ${colors.border.light}`,
          borderBottomColor: (interactive && open) ? "transparent" : colors.border.light,
          color: colors.text.primary,
        }}
      >
        {isAll ? allIcon() : avatar(c!.channelThumbnailUrl)}
        <span style={isCompact ? { flex: 1, overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" } : undefined}>{label}</span>
        {!isAll && usageSpan(c!)}
        {/* Hide the trigger's total while the dropdown is open — the per-channel
            rows inside show their own "N new" then. */}
        {!open && newBadge(total)}
        {interactive && chev(colors.text.muted)}
      </Tag>
    );
  };

  // Single channel — static trigger only when there's no Manage tab to host
  // (legacy/no-callbacks). When manage IS available, keep the dropdown so the
  // user can still add channels via the Manage tab.
  if (channels.length === 1 && !onChannelChange) {
    return buildTrigger(channels[0], false);
  }

  const selected = selectedId ? channels.find((c) => c.youtubeChannelId === selectedId) ?? null : null;
  return (
    <div ref={ref} style={{ position: "relative", display: isCompact ? "block" : "inline-block", width: isCompact ? "100%" : undefined }}>
      {buildTrigger(selected, true)}
        {open && (
          <div style={{
            position: "absolute", top: "100%", left: 0, zIndex: 20,
            ...(isCompact
              ? { right: 0, width: "100%" }
              // Desktop filter menu: `width: max-content` so it sizes to the
              // longest channel name instead of shrink-to-fitting against the
              // (narrow) trigger it's anchored to — at 240px the name column got
              // squeezed until every channel ellipsised to a single letter.
              // Bounded so one very long name can't stretch it across the page.
              : (tab === "manage" ? { width: 520 } : { width: "max-content", minWidth: 340, maxWidth: 520 })),
            background: "#7D6049",
            border: "1px solid rgba(255, 255, 255, 0.08)", borderTop: "none",
            // Square only where it meets the trigger. On desktop the menu is
            // wider than the trigger, so its top-right corner is a free edge and
            // was left sharp; on phone/tablet it spans the full width and both
            // top corners sit under the trigger.
            borderRadius: isCompact ? "0 0 12px 12px" : "0 12px 12px 12px",
            boxShadow: "0 18px 40px rgba(0, 0, 0, 0.35)",
            display: "flex", flexDirection: "column",
            maxHeight: "min(70vh, 520px)", overflow: "hidden",
          }}>
            {/* Body (scrolls) — the channel picker by default; Edit Channels in
                the footer switches it to the manage panel. */}
            <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: 6, display: "flex", flexDirection: "column", gap: 3, ...(tab === "manage" && isCompact ? { maxHeight: 330 } : null) }}>
            {tab === "filter" ? (
              <>
              {[null, ...channels.map((c) => c.youtubeChannelId)].map((id) => {
                const c = id ? channels.find((x) => x.youtubeChannelId === id)! : null;
                const active = selectedId === id || (id === null && selectedId === null);
                return (
                  <div
                    key={id ?? "__all__"}
                    onClick={() => { onSelect(id); setOpen(false); }}
                    style={{
                      // Fixed columns so the green "new" pills + the usage figures
                      // line up vertically across every channel row (table-like),
                      // with a thin "|" divider between the two.
                      display: "grid", gridTemplateColumns: "1fr 64px 8px 46px",
                      alignItems: "center", gap: 10,
                      padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                      background: active ? "rgba(255, 210, 155, 0.18)" : "transparent",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0, fontFamily: fonts.number, fontSize: fontSizes.body, fontWeight: 500, lineHeight: 1, color: colors.text.primary }}>
                      {c ? avatar(c.channelThumbnailUrl) : allIcon()}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c ? (c.channelName || "Channel") : "All channels"}</span>
                    </span>
                    <span style={{ display: "flex", justifyContent: "flex-end" }}>{c ? newBadge(byChannel[c.youtubeChannelId]) : null}</span>
                    <span style={{ display: "flex", justifyContent: "center", color: "rgba(251, 244, 236, 0.25)", fontSize: fontSizes.sm, lineHeight: 1 }}>{c && byChannel[c.youtubeChannelId] > 0 ? "|" : ""}</span>
                    <span style={{ display: "flex", justifyContent: "flex-end" }}>{c ? usageSpan(c) : null}</span>
                  </div>
                );
              })}
              </>
            ) : (
              // eslint-disable-next-line @typescript-eslint/no-use-before-define -- ChannelsPanel is defined later in the same module; the forward ref is resolved at render time.
              <ChannelsPanel onChannelChange={onChannelChange} onChannelDisconnected={onChannelDisconnected} onManageSubscription={onManageSubscription} isPastDue={isPastDue} hideNeedMore title="Edit Channels" embedded />
            )}
            </div>
            {/* Footer — flush, full-width, two halves split by a full-height
                vertical line. Right = Edit Channels (opens the manage panel).
                Left is context-aware: on the manage view it's a back button to
                the picker; on the picker it's "Need more?" when the cap is hit. */}
            <div style={{ display: "flex", flexShrink: 0, borderTop: "1px solid rgba(255, 255, 255, 0.12)" }}>
              {tab === "manage" ? (
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setTab("filter"); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "12px 14px", border: "none", borderRight: "1px solid rgba(255, 255, 255, 0.12)", cursor: "pointer", fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 500, background: "transparent", color: "rgba(251, 244, 236, 0.85)", transition: "background 0.15s ease" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                  Channels
                </button>
              ) : (cap > 0 && channels.length >= cap && onManageSubscription) ? (
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setOpen(false); onManageSubscription(); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(156, 211, 253, 0.1)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  style={{ flex: 1, padding: "12px 14px", border: "none", borderRight: "1px solid rgba(255, 255, 255, 0.12)", cursor: "pointer", fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 500, background: "transparent", color: colors.accent.blue, transition: "background 0.15s ease" }}>
                  Need more?
                </button>
              ) : null}
              <button type="button"
                onClick={(e) => { e.stopPropagation(); setTab("manage"); }}
                onMouseEnter={(e) => { if (tab !== "manage") e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)"; }}
                onMouseLeave={(e) => { if (tab !== "manage") e.currentTarget.style.background = "transparent"; }}
                style={{ position: "relative", flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "12px 14px", border: "none", cursor: tab === "manage" ? "default" : "pointer", fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 500, background: "transparent", color: tab === "manage" ? "rgba(251, 244, 236, 0.35)" : "rgba(251, 244, 236, 0.85)", transition: "background 0.15s ease, color 0.15s ease" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                Edit Channels
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

type TabDef = { key: string; label: string; count: number; highlight: boolean };

/* Phone/tablet only: a warm tinted band behind the channel picker + status
   pills + auto-resolve toggle (and any per-channel warning banners). Flush top
   so it merges seamlessly with the tinted heading band above (AccountLayout
   TitleBlock $tinted); margin-bottom gives the gap down to the cards. Desktop
   (≥1150) renders a plain passthrough — no tint, no band. */
const HeaderBand = styled.div<{ $tight?: boolean }>`
  @media (max-width: 1149.98px) {
    /* Darker band — continues the AccountLayout HeaderBandOuter (same colour,
       flush top) so the heading band and this toolbar band read as one. Wraps
       the per-channel warning banners + the channel picker. Bleeds edge-to-edge
       (full viewport width), re-padding by the page gutter so the inner content
       stays aligned with the cards. Its bottom edge (dark → page bg) is the
       separation from the pills/cards below; margin-bottom is that gap.
       $tight (Free view only — the submit box needs far less breathing room
       below it than the subscription picker) trims the bottom padding + gap. */
    background: rgba(0, 0, 0, 0.2);
    width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    box-sizing: border-box;
    padding: 14px 16px ${({ $tight }) => ($tight ? "10px" : "24px")};
    margin-bottom: ${({ $tight }) => ($tight ? "20px" : "24px")};
  }
  /* Tablet band (1024–1149.98): page gutter is 40px, not 16px. */
  @media (min-width: 1024px) and (max-width: 1149.98px) {
    padding-left: 40px;
    padding-right: 40px;
  }
`;

/* Same fade-up entrance the site's content pages use (AccountLayout ContentArea
   / PageContainer). Applied to the active tab's content and keyed on the tab,
   so switching pills replays the gentle rise-and-fade. */
const tabContentEnter = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;
const TabContent = styled.div`
  animation: ${tabContentEnter} 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

const TabBarRenderer: React.FC<{
  tabs: TabDef[];
  activeTab: string;
  setActiveTab: (k: string) => void;
  /** Optional element rendered at the right edge of the tab row
   *  (used for the Auto-resolve toggle on Free / Subscription). */
  rightSlot?: React.ReactNode;
  /** Optional element rendered at the LEFT edge, before the tabs — the
   *  channel filter (desktop only; on phone/tablet the picker lives in the
   *  header band and this renders pills-only). A 1px divider separates it
   *  from the pills on desktop. */
  leftSlot?: React.ReactNode;
  /** Optional spacing overrides around the tab row (px). Defaults: no top
   *  margin, 22/28 bottom. The Free view uses these to sit lower (more top)
   *  and hug the cards (less bottom). */
  tabTop?: number;
  tabBottom?: number;
}> = ({ tabs, activeTab, setActiveTab, rightSlot, leftSlot, tabTop, tabBottom }) => {
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  // "Action Required" plus its count is the widest pill; at half a row it needs
  // tighter padding on the smallest phones or it would clip.
  const isNarrow = useMediaQuery("(max-width: 400px)");

  const renderPill = (tab: TabDef, fullWidth = false) => {
    const active = activeTab === tab.key;
    return (
      <button
        key={tab.key}
        onClick={() => setActiveTab(tab.key)}
        style={{
          // Phone/tablet: every tab is exactly half a row — four tabs, two rows
          // of two, all the same width. Fixed at half rather than growing:
          // letting the odd one out absorb the leftover space is what made them
          // come out at different widths. `fullWidth` is the exception: with an
          // odd number of tabs the first one takes the whole top row so the rest
          // still pair up 50-50 (otherwise the odd one out sat alone at half).
          flex: isCompact ? (fullWidth ? "1 1 100%" : "0 0 calc(50% - 4px)") : "0 0 auto",
          height: isCompact ? 38 : 40, boxSizing: "border-box", padding: isCompact ? (isNarrow ? "0 9px" : "0 13px") : "0 20px", borderRadius: 999, lineHeight: 1,
          // Smaller on phone/tablet so the chips pack the way the demo does
          // (Action / Review on one row, Queued / Resolved / Hidden together).
          // The weight is FIXED on compact: a bolder active pill is a wider
          // pill, and with wrapping chips that re-flowed the whole tab bar on
          // every tap — the tabs appeared to rearrange themselves.
          fontFamily: fonts.number, fontSize: isCompact ? (isNarrow ? "0.8rem" : "0.85rem") : fontSizes.body, fontWeight: isCompact ? 600 : active ? 600 : 500,
          letterSpacing: "0.2px",
          // The FAQ pill treatment, used everywhere else on the site for a
          // selected tab: a gold TINT with a bright gold edge and a glow, not a
          // solid gold fill. Solid gold is the button language — a tab that
          // wears it reads as something to press rather than as where you are.
          // The inactive pill dims with it: against a tint rather than a fill,
          // full-brightness labels on the others left nothing marking the
          // active one.
          color: active ? "#fbf4ec" : "rgba(251, 244, 236, 0.65)",
          background: active ? "rgba(255, 220, 168, 0.18)" : "rgba(255, 255, 255, 0.05)",
          border: active ? "1px solid rgba(255, 210, 155, 0.65)" : "1px solid rgba(186, 144, 92, 0.25)",
          boxShadow: active ? "0 0 16px rgba(186, 144, 92, 0.28)" : "none",
          cursor: "pointer", transition: "all 0.2s ease",
          whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: isCompact ? (isNarrow ? 6 : 8) : 10,
        }}
        onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = "rgba(255, 255, 255, 0.09)"; e.currentTarget.style.color = "#fbf4ec"; } }}
        onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"; e.currentTarget.style.color = "rgba(251, 244, 236, 0.65)"; } }}
      >
        {tab.label}
        {tab.count > 0 && (
          <span style={{
            fontFamily: fonts.number,
            fontSize: tab.highlight ? 10 : fontSizes.xs,
            fontWeight: tab.highlight ? 600 : 700,
            minWidth: tab.highlight ? 18 : 20,
            height: tab.highlight ? 18 : 20,
            padding: tab.highlight ? "0 5px" : "0 6px",
            borderRadius: tab.highlight ? 9 : 10,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            // The blue count on Action Required is untouched — it is the one
            // thing on this bar that means "look here", and it has to survive
            // the tab being selected. The others follow the pill: light on a
            // tint now, rather than dark on gold.
            background: tab.highlight
              ? "#9cd3fd"
              : active
              ? "rgba(255, 255, 255, 0.12)"
              : "rgba(255, 255, 255, 0.08)",
            color: tab.highlight ? "#193e5b" : active ? colors.text.primary : colors.text.muted,
            border: "1px solid transparent",
          }}>{tab.count}</span>
        )}
      </button>
    );
  };

  // Phone / tablet: channel selector full-width on top, then the tabs as
  // full-width wrapping chips (each grows to fill its row), auto-resolve below.
  if (isCompact) {
    // An odd number of tabs (e.g. the 5-tab Subscription view with Queued
    // active) leaves one pill stranded alone at half width. Promote the first
    // tab — Action Required — to a full-width row on top so the remaining even
    // count pairs up cleanly 50-50. With an even count, everything is 50-50.
    const oddCount = tabs.length % 2 === 1;
    const [firstTab, ...restTabs] = tabs;
    return (
      <div style={{ marginTop: tabTop ?? 0, marginBottom: tabBottom ?? 22 }}>
        {leftSlot && <div style={{ minWidth: 0, marginBottom: 22 }}>{leftSlot}</div>}
        {oddCount && firstTab ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {renderPill(firstTab, true)}
            {restTabs.map((t) => renderPill(t))}
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {tabs.map((t) => renderPill(t))}
          </div>
        )}
        {rightSlot && <div style={{ display: "flex", alignItems: "center", marginTop: 14 }}>{rightSlot}</div>}
      </div>
    );
  }

  // Desktop: single inline row (channel selector | divider | pills … rightSlot).
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: tabTop ?? 0, marginBottom: tabBottom ?? 28, alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {leftSlot && (
          <>
            {leftSlot}
            <div style={{ width: 1, alignSelf: "stretch", minHeight: 26, background: "rgba(235, 194, 142, 0.15)", margin: "0 4px" }} />
          </>
        )}
        {tabs.map((t) => renderPill(t))}
      </div>
      {rightSlot && <div style={{ display: "inline-flex", alignItems: "center" }}>{rightSlot}</div>}
    </div>
  );
};

/** Three-section video list: Action Required / In Review / Whitelisted / Hidden */
const ThreeSectionList: React.FC<{
  items: VideoItem[];
  search: string;
  sortKey: SortKey;
  showUpgrade?: boolean;
  /** Hide the "Queued" tab entirely. Free users have no monthly limits,
      so queueing-for-next-cycle is meaningless for them. Default true. */
  showQueued?: boolean;
  pendingSubmissions?: { id: string; url: string; videoLooksDeleted?: boolean }[];
  onRefresh?: () => void;
  /** Refetch per-channel credits only — fired the moment a manual
      release returns `channelHitLimit: true` so the 100% banner appears
      immediately, without waiting for the 4250ms resolve animation +
      full claims refresh at end-of-timeline. */
  onChannelLimitHit?: () => void;
  emptyHint?: React.ReactNode;
  /** Whether the parent's data fetch has completed. Used to gate the
      global action-required count: writing 0 during the initial loading
      window would briefly hide the header + avatar badges before the
      real count lands. Default true so existing callers keep working. */
  dataLoaded?: boolean;
  /** Optional element rendered at the right edge of the tab bar
   *  (e.g. the Auto-resolve toggle pill for Free / Subscription). */
  tabBarRightSlot?: React.ReactNode;
  /** Optional element rendered immediately BELOW the tab bar, above the
   *  active-tab content. Used by Subscription view for the "X new claims —
   *  Refresh" banner, which sits right above the cards it populates. */
  topAlerts?: React.ReactNode;
  /** Optional element rendered ABOVE the tab bar (channel picker), right
   *  below the page heading. Used by Subscription view for the per-channel
   *  monthly claim-limit warnings (>80% and at-limit alerts), which belong
   *  with the channel picker rather than the card list. */
  channelWarnings?: React.ReactNode;
  /** Optional element rendered INSIDE the header band (phone/tablet) / above
   *  the tab bar (desktop), in the same slot the Subscription view uses for
   *  the channel picker. The Free view passes its Submit-Video-URL box here so
   *  it sits within the dark band (continuous with the heading band) instead
   *  of floating on the page background above an empty band. */
  bandContent?: React.ReactNode;
  /** Trim the header band's bottom padding + gap. Set by the Free view only —
   *  the submit box needs less space below it than the subscription picker. */
  tightBand?: boolean;
  /** Subscription PAST_DUE — freezes the per-card claim actions
   *  (Resolve / Submit again / Queue) on every card this list renders. */
  isPastDue?: boolean;
  /** Per-channel credit rows — when 1+ present, the channel filter renders
   *  in front of the tabs and scopes the list + counts to the selection. */
  channelCredits?: ChannelCreditRow[];
  /** Channel add/manage callbacks — forwarded to the combined picker's Manage
   *  tab (which renders ChannelsPanel). */
  onChannelChange?: () => void;
  onChannelDisconnected?: (youtubeChannelId: string) => void;
  onManageSubscription?: () => void;
  /** Channel cap — drives the "Need more channels?" link in the picker. */
  channelCap?: number;
  /** Optional tab-row spacing overrides (px) forwarded to TabBarRenderer. */
  tabTop?: number;
  tabBottom?: number;
}> = ({ items: rawItems, search, sortKey, showUpgrade = true, showQueued = true, pendingSubmissions, onRefresh, onChannelLimitHit, emptyHint, dataLoaded = true, tabBarRightSlot, topAlerts, channelWarnings, bandContent, tightBand, isPastDue, channelCredits = [], onChannelChange, onChannelDisconnected, onManageSubscription, channelCap = 0, tabTop, tabBottom }) => {
  const dispatch = useAppDispatch();
  /* Read the cart so Buy License can tell whether this track is already in it.
     One claim can only ever consume one licence, and `addToCart` increments. */
  const cartItems = useAppSelector((state) => state.cart.items);
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  // Channel filter — null = All. Scopes every downstream computation
  // (sections + tab counts) to the selected channel.
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const items = selectedChannelId ? rawItems.filter((v) => v.channelId === selectedChannelId) : rawItems;

  // ── "New claims" (unread) state ──────────────────────────────────────────
  // A claim is "new" while the user hasn't had its card on screen. The backend
  // sends `isNew` per claim; we FREEZE the set of new ids for this page-mount so
  // the green badges + per-channel counts persist while the user scrolls and
  // switches channels. Looking at a card records it server-side (markClaimsViewed)
  // but does NOT clear the badge here — only the NEXT page load drops the ones
  // that were viewed (their `isNew` comes back false and is never re-added). A
  // full reload / route remount resets this set, which is exactly the trigger.
  const [freshClaimIds, setFreshClaimIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setFreshClaimIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const v of rawItems) {
        if (v.isNew && !next.has(v.id)) { next.add(v.id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [rawItems]);
  // A card shows "New" while it's a still-action-needed claim in the frozen set.
  const isClaimNew = (v: VideoItem): boolean =>
    freshClaimIds.has(v.id) &&
    !v.isHidden &&
    !v.isQueuedForNextCycle &&
    v.status !== "Whitelisted" &&
    v.status !== "Analyzing" &&
    v.status !== "Searching";
  // Per-channel + total new counts (across ALL channels, unfiltered) for the picker badges.
  const newCounts = useMemo(() => {
    const by: Record<string, number> = {};
    let tot = 0;
    for (const v of rawItems) {
      const isFresh =
        freshClaimIds.has(v.id) &&
        !v.isHidden &&
        !v.isQueuedForNextCycle &&
        v.status !== "Whitelisted" &&
        v.status !== "Analyzing" &&
        v.status !== "Searching";
      if (!isFresh) continue;
      const ch = v.channelId || "";
      if (ch) by[ch] = (by[ch] || 0) + 1;
      tot += 1;
    }
    return { byChannel: by, total: tot };
  }, [rawItems, freshClaimIds]);
  // Batch + debounce "viewed" writes as cards scroll into view (see IntersectionObserver below).
  const pendingViewedRef = useRef<Set<string>>(new Set());
  const viewedFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markCardViewed = (claimId: string) => {
    if (!freshClaimIds.has(claimId)) return;
    pendingViewedRef.current.add(claimId);
    if (viewedFlushRef.current) return;
    viewedFlushRef.current = setTimeout(() => {
      const ids = Array.from(pendingViewedRef.current);
      pendingViewedRef.current.clear();
      viewedFlushRef.current = null;
      if (ids.length) void markClaimsViewed(ids).catch(() => undefined);
    }, 800);
  };
  const subConfig = useSubscriptionConfig();
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [submittedIds, setSubmittedIds] = useState<Set<string>>(new Set());
  /** The claim's `checkedAt` at the moment Submit again was clicked, per card.
      `submittedIds` pins a card to Under Review, and until now nothing ever took
      an id back out — so a re-checked card stayed there for the lifetime of the
      component and only a full page reload freed it. Status alone cannot release
      it: a re-run that reaches the SAME verdict returns the same status, and the
      server reports "Analyzing" only for a claim it has never checked. The
      verdict timestamp is the one signal that always moves. */
  const submittedAtCheckedAt = useRef<Record<string, string | null>>({});
  const [queuedIds, setQueuedIds] = useState<Set<string>>(new Set());
  /** Optimistic-resolve set: the click resolves the claim on the backend
      but we move the card to Resolved + flip its status display before the
      next refresh lands. Mirrors the queuedIds pattern. Cleared on next
      refresh when the backend reports Whitelisted naturally. */
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  /** Ordered list of card IDs that the user just transitioned via a click
      (Hide / Resolve / Queue / Unhide). Newest at index 0. Used to lift
      freshly-moved cards to the top of their destination tab so the
      AppearWrapper grows in *above* existing items, smoothly pushing them
      down — instead of slotting in at a date-sorted position where there's
      nothing to push and the existing items snap. Backend-seeded items
      (isHidden flag, etc.) never enter this list, so persistent state
      keeps its natural sort. */
  const [recentMoves, setRecentMoves] = useState<string[]>([]);
  const noteRecentMove = useCallback((id: string) => {
    setRecentMoves((prev) => [id, ...prev.filter((x) => x !== id)].slice(0, 32));
  }, []);
  /** Sort helper: items present in `recentMoves` go first in recency order
      (newest at top), then the rest in their natural list order. */
  const liftRecentToTop = useCallback(<T extends { id: string }>(list: T[]): T[] => {
    if (recentMoves.length === 0) return list;
    const recentSet = new Set(recentMoves);
    const recent: T[] = [];
    const rest: T[] = [];
    for (const v of list) (recentSet.has(v.id) ? recent : rest).push(v);
    recent.sort((a, b) => recentMoves.indexOf(a.id) - recentMoves.indexOf(b.id));
    return [...recent, ...rest];
  }, [recentMoves]);
  const [manageSubOpen, setManageSubOpen] = useState(false);
  /** Claim whose YouTube Licence certificate is open. Null = modal closed. */
  const [certificateClaimId, setCertificateClaimId] = useState<string | null>(null);
  /** IDs of cards that should play the AppearWrapper entry animation on
      their next render. Set by handleHide and handleUnhide right before
      they switch the active tab, so the user sees the card appear in the
      tab it just landed in. Cleared after ~700ms (longer than the
      AppearWrapper animation so we don't re-trigger on rerender). */
  const [appearingIds, setAppearingIds] = useState<Set<string>>(new Set());
  const scheduleAppear = (id: string) => {
    setAppearingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    window.setTimeout(() => {
      setAppearingIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 700);
  };
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [submittingCollapsingId, setSubmittingCollapsingId] = useState<string | null>(null);
  /** Drives the two transient button states inside VideoCard's Resolve
      action while the resolve flow is animating. Keyed by claim id so
      multiple simultaneous resolves (rare but possible) don't clobber
      each other. See `handleResolve` for the timeline. */
  const [resolveStageById, setResolveStageById] = useState<Record<string, "success" | "moving">>({});
  // Why a resolve was refused, shown on the card that was refused.
  const [resolveError, setResolveError] = useState<{ id: string; message: string } | null>(null);
  /** Same pattern for the Submit-again flow (Credit Text / Private Video /
      No Claim Detected cards). Stage 1 = "Submitted!" confirmation,
      stage 2 = "Moving to "Under Review"…" with spinner. */
  const [submitStageById, setSubmitStageById] = useState<Record<string, "success" | "moving">>({});
  /** Same pattern for the Queue-for-Next-Month flow (Claim Limit Reached
      cards). Stage 1 = "Queued!" confirmation, stage 2 = "Moving to
      "Queued"…" with spinner. */
  const [queueStageById, setQueueStageById] = useState<Record<string, "success" | "moving">>({});

  // Track previous items to detect new/removed cards after refresh
  const prevItemsRef = useRef<Map<string, VideoItem>>(new Map(items.map((v) => [v.id, v])));
  const prevChannelRef = useRef<string | null>(selectedChannelId);
  const [fadingOutIds, setFadingOutIds] = useState<Set<string>>(new Set());
  const [ghostItems, setGhostItems] = useState<VideoItem[]>([]);

  // useLayoutEffect (not useEffect): the new-card detection has to run
  // *before* the browser paints. Otherwise, when items first arrive, the
  // initial render commits the cards as plain (not yet in `appearingIds`),
  // the browser paints them visible, then this effect runs, sets
  // `appearingIds`, the next render wraps them in AppearWrapper at height 0,
  // and the user sees the flash → hide → fade-in pattern. Running
  // synchronously means the AppearWrapper-wrapped render replaces the plain
  // one before the first paint is committed.
  useLayoutEffect(() => {
    const prevMap = prevItemsRef.current;
    const currentIds = new Set(items.map((v) => v.id));

    // Channel filter changed — animate the WHOLE new set in (no ghost
    // fade-out). Without this, switching from "All channels" to a specific
    // channel wouldn't animate, because that channel's cards were already
    // on screen within the All set, so the diff treats them as not-new.
    if (prevChannelRef.current !== selectedChannelId) {
      prevChannelRef.current = selectedChannelId;
      for (const v of items) scheduleAppear(v.id);
      prevItemsRef.current = new Map(items.map((v) => [v.id, v]));
      return;
    }

    // New cards → appear animation
    const newIds = [...currentIds].filter((id) => !prevMap.has(id));
    if (newIds.length > 0) {
      for (const id of newIds) scheduleAppear(id);
    }

    // Removed cards → keep as ghosts, fade out then collapse
    const removedIds = [...prevMap.keys()].filter((id) => !currentIds.has(id) && !hiddenIds.has(id));
    if (removedIds.length > 0) {
      const ghosts = removedIds.map((id) => prevMap.get(id)!).filter(Boolean);
      setGhostItems(ghosts);
      setFadingOutIds(new Set(removedIds));
      // Fade (350ms) → collapse (300ms) → remove ghosts
      setTimeout(() => {
        setFadingOutIds(new Set()); // triggers collapse
        setTimeout(() => setGhostItems([]), 300);
      }, 350);
    }

    prevItemsRef.current = new Map(items.map((v) => [v.id, v]));
  }, [items, selectedChannelId]);
  const [licenseModalItem, setLicenseModalItem] = useState<VideoItem | null>(null);
  // Active tab — backed by `?tab=<key>` in the URL so each tab has its own
  // shareable URL (mirrors the admin pattern). Declared up here so `handleUnhide`
  // below can reference `setActiveTab` without ESLint tripping over use-before-
  // define. The initial "jump to first non-empty tab" heuristic runs in a
  // useEffect below after the filtering buckets are computed.
  // The active tab lives in the URL, and anything not on this list silently
  // falls back to "action" — so a tab added to the bar without being added here
  // renders as a tap that highlights Action Required instead of itself.
  const validTabKeys = ["action", "review", "queued", "licensed", "hidden"] as const;
  const [searchParams, setSearchParams] = useSearchParams();
  const rawUrlTab = searchParams.get("tab");
  // Back-compat: the Licensed tab used to be keyed "resolved" (older links,
  // e.g. the licence-delivery email). Treat that as "licensed" and rewrite the
  // URL below so the visible key is always the current one.
  const urlTab = rawUrlTab === "resolved" ? "licensed" : rawUrlTab;
  const activeTab: string = (validTabKeys as readonly string[]).includes(urlTab ?? "") ? (urlTab as string) : "action";
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Rewrite a legacy ?tab=resolved link to ?tab=licensed so the URL matches the
  // current tab key (the content already renders via the alias above).
  useEffect(() => {
    if (rawUrlTab === "resolved") setActiveTab("licensed");
  }, [rawUrlTab, setActiveTab]);

  // Seed hiddenIds / queuedIds from backend flags when real data arrives.
  // Mock-data items never carry these flags, so dev preview stays intact.
  const seedRef = useRef<string>("");
  useEffect(() => {
    const sig = items.map((v) => v.id).join(",");
    if (sig === seedRef.current) return;
    seedRef.current = sig;
    const hidden = items.filter((v) => v.isHidden).map((v) => v.id);
    const queued = items.filter((v) => v.isQueuedForNextCycle).map((v) => v.id);
    if (hidden.length > 0) {
      setHiddenIds((prev) => {
        const next = new Set(prev);
        for (const id of hidden) next.add(id);
        return next;
      });
    }
    if (queued.length > 0) {
      setQueuedIds((prev) => {
        const next = new Set(prev);
        for (const id of queued) next.add(id);
        return next;
      });
    }
  }, [items]);

  /** Hide click: immediately switch to the Hidden tab, add the card to
      `hiddenIds`, and mark it as "appearing" so it plays the AppearWrapper
      entry animation there. No collapse-from-source-tab phase — the user
      lands on Hidden and sees the card grow in.

      Three possible row origins need different backend calls:
      - pending_url_submissions row (submissionId set) → DELETE (remove)
      - real claim row from GET /claims/my (looks like a UUID) → POST hide
      - mock row from dev preview → local state only
  */
  const handleHide = (id: string) => {
    const target = items.find((v) => v.id === id);
    const realSubmissionId = target?.submissionId;
    const isRealClaim = target && !realSubmissionId && /^[0-9a-f-]{36}$/.test(id);
    if (realSubmissionId) {
      hidePendingSubmission(realSubmissionId).catch(() => {});
    } else if (isRealClaim) {
      hideClaim(id).catch(() => {});
    }
    // Fade out on current tab → collapse → drop into the Hidden bucket
    // silently. Per UX decision (2026-04-20): clicking the eye icon should
    // *not* yank the user to the Hidden tab — the card just vanishes from
    // where they are. Unhide still does the tab-switch + AppearWrapper
    // dance because the user explicitly wants to see where it lands.
    setSubmittingId(id);
    setTimeout(() => {
      setSubmittingId(null);
      setSubmittingCollapsingId(id);
      setTimeout(() => {
        setHiddenIds((prev) => new Set(prev).add(id));
        setSubmittingCollapsingId(null);
        if (realSubmissionId || isRealClaim) onRefresh?.();
      }, 300);
    }, 350);
  };

  /** "Video was deleted" button on Private Video cards.
      - Pending submission → hard-delete the row so the user can resubmit the same URL.
      - Matched claim → mark reason = video_deleted (sticks around as a dismissible card).
      Animation plays first, refresh fires after so the card doesn't vanish
      mid-fade when the backend responds fast. */
  const handleConfirmDeleted = (id: string) => {
    const target = items.find((v) => v.id === id);
    const realSubmissionId = target?.submissionId;
    const isRealClaim = !realSubmissionId && /^[0-9a-f-]{36}$/i.test(id);
    if (realSubmissionId) {
      removePendingSubmission(realSubmissionId).catch(() => {});
    } else if (isRealClaim) {
      import("@/services/claimSubmissions.service").then(({ confirmVideoDeleted }) => {
        confirmVideoDeleted(id).catch(() => {});
      });
    }
    // Fade out → collapse → hide → refresh (pending only, matched claims
    // re-appear via confirmVideoDeleted → greyed state on next refresh).
    setSubmittingId(id);
    setTimeout(() => {
      setSubmittingId(null);
      setSubmittingCollapsingId(id);
      setTimeout(() => {
        setHiddenIds((prev) => new Set(prev).add(id));
        setSubmittingCollapsingId(null);
        if (realSubmissionId) onRefresh?.();
      }, 300);
    }, 350);
  };

  const handleQueue = (id: string) => {
    const target = items.find((v) => v.id === id);
    const isRealClaim = target && !target.submissionId && /^[0-9a-f-]{36}$/.test(id);
    if (isRealClaim) {
      queueClaimForNextCycle(id).catch(() => {});
    }
    // Same timeline as handleResolve so all action-card transitions feel
    // identical. NO tab switch — user stays on Action Required while the
    // card animates out; they can keep working through their queue.
    //   0ms     button → "Queued!" (green confirmation, disabled)
    //   1800ms  button → "Moving to "Queued"…" (greyed, spinner)
    //   3600ms  card starts fading out
    //   3950ms  card collapses height
    //   4250ms  card silently moves to Queued bucket
    setQueueStageById((prev) => ({ ...prev, [id]: "success" }));
    setTimeout(() => {
      setQueueStageById((prev) => ({ ...prev, [id]: "moving" }));
    }, 1800);
    setTimeout(() => {
      setSubmittingId(id);
    }, 3600);
    setTimeout(() => {
      setSubmittingId(null);
      setSubmittingCollapsingId(id);
    }, 3950);
    setTimeout(() => {
      setQueuedIds((prev) => new Set(prev).add(id));
      setSubmittingCollapsingId(null);
      setQueueStageById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      noteRecentMove(id);
      if (isRealClaim) onRefresh?.();
    }, 4250);
  };

  /** Compute which tab a card should live in based on its current status. */
  const resolveNaturalTab = (item: VideoItem): string => {
    if (queuedIds.has(item.id)) return "queued";
    if (submittedIds.has(item.id) || item.status === "Analyzing" || item.status === "Searching") return "review";
    if (item.status === "Whitelisted") return "licensed";
    if (ERROR_STATUSES.includes(item.status)) return "action";
    return "action";
  };

  /** Unhide click: mirror of handleHide. Remove the card from `hiddenIds`,
      compute which tab it naturally belongs in, switch there, and mark
      it as "appearing" so the user sees it expand in. */
  const handleUnhide = (id: string) => {
    const target = items.find((v) => v.id === id);
    const realSubmissionId = target?.submissionId;
    const isRealClaim = target && !realSubmissionId && /^[0-9a-f-]{36}$/.test(id);
    if (realSubmissionId) {
      unhidePendingSubmission(realSubmissionId).catch(() => {});
    } else if (isRealClaim) {
      unhideClaim(id).catch(() => {});
    }
    // Fade out on Hidden tab → collapse → move to natural tab with appear animation.
    const targetTab = target ? resolveNaturalTab(target) : "action";
    setSubmittingId(id);
    setTimeout(() => {
      setSubmittingId(null);
      setSubmittingCollapsingId(id);
      setTimeout(() => {
        setHiddenIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setSubmittingCollapsingId(null);
        setActiveTab(targetTab);
        scheduleAppear(id);
        noteRecentMove(id);
      }, 300);
    }, 350);
  };

  const handleResolve = (id: string) => {
    const target = items.find((v) => v.id === id);
    const isReal = target && /^[0-9a-f-]{36}$/i.test(id);
    if (!isReal || !target) return;
    // Timeline:
    //   0ms     button → "Claim resolved!" (green, confirmation)
    //   1800ms  button → "Moving to "Resolved"…" (greyed, spinner)
    //   3600ms  card starts fading out
    //   3950ms  card collapses height
    //   4250ms  card removed from Action Required, added to Resolved bucket
    // No active-tab switch — user stays where they were and can keep
    // working through more claims. Card lifts to top of Resolved tab via
    // noteRecentMove so it's findable if they switch over.
    setResolveError(null);
    setResolveStageById((prev) => ({ ...prev, [id]: "success" }));

    // Fire backend in parallel. ANY refusal has to roll the card back and say
    // why — it used to roll back for credit_limit alone, so a blocked channel,
    // an overdue payment or a provider failure all animated to "Resolved" with
    // no message while the claim sat untouched on the server.
    // On success, if the backend reports `channelHitLimit` (this release
    // was the one that took the channel over its monthly cap), refetch
    // channel-credits immediately so the 100% banner appears on top
    // while the resolve animation is still running.
    let creditDenied = false;
    import("@/services/claimSubmissions.service").then((svc) => {
      const call = target.useLicenseAvailable ? svc.useLicenseOnClaim(id) : svc.releaseClaim(id);
      call
        .then((res: any) => {
          if (res?.channelHitLimit) {
            onChannelLimitHit?.();
          }
        })
        .catch((err: any) => {
          creditDenied = true;
          // The error interceptor rejects with `error.response.data` itself,
          // so the payload IS the error object. Reading err.response.data
          // found undefined every time — which is why this always fell back to
          // the generic sentence, and why the old credit_limit check never
          // fired either.
          const data = err?.response?.data ?? err;
          setResolveStageById((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          setResolvedIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setSubmittingId(null);
          setSubmittingCollapsingId(null);
          // The credit case already repaints as "Claim Limit Reached", so it
          // needs no sentence of its own; everything else does.
          if (data?.code !== "credit_limit") {
            setResolveError({
              id,
              message: String(data?.message || "This claim could not be resolved. Please try again."),
            });
          }
          onRefresh?.();
        });
    });

    // Stage 2: success → moving (button label + spinner).
    setTimeout(() => {
      if (creditDenied) return;
      setResolveStageById((prev) => ({ ...prev, [id]: "moving" }));
    }, 1800);

    // Stage 3: fade card out (350ms).
    setTimeout(() => {
      if (creditDenied) return;
      setSubmittingId(id);
    }, 3600);

    // Stage 4: collapse card height (300ms) → silently move to Resolved.
    setTimeout(() => {
      if (creditDenied) return;
      setSubmittingId(null);
      setSubmittingCollapsingId(id);
    }, 3950);

    // Stage 5: commit the move.
    setTimeout(() => {
      if (creditDenied) return;
      setResolvedIds((prev) => new Set(prev).add(id));
      setSubmittingCollapsingId(null);
      setResolveStageById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      noteRecentMove(id);
      // Same reason as Submit again below — a release is a provider round-trip,
      // so one fetch at 4.25s can land before it has settled.
      RECHECK_POLL_MS.forEach((d) => window.setTimeout(() => { onRefresh?.(); }, d));
    }, 4250);
  };

  /** Visual-only resolve animation for one card — used by the
      auto-release toggle flow where the backend has already released
      the claim (so we skip the .releaseClaim() call) and just want the
      per-card Resolved! → Moving → fade → collapse sequence to play
      out. Returns a Promise that resolves at the END of the animation
      so callers can chain a sequential walk through multiple cards. */
  const animateResolvedVisualOnly = (id: string): Promise<void> => {
    return new Promise((resolve) => {
      setResolveStageById((prev) => ({ ...prev, [id]: "success" }));
      setTimeout(() => {
        setResolveStageById((prev) => ({ ...prev, [id]: "moving" }));
      }, 1800);
      setTimeout(() => {
        setSubmittingId(id);
      }, 3600);
      setTimeout(() => {
        setSubmittingId(null);
        setSubmittingCollapsingId(id);
      }, 3950);
      setTimeout(() => {
        setResolvedIds((prev) => new Set(prev).add(id));
        setSubmittingCollapsingId(null);
        setResolveStageById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        noteRecentMove(id);
        resolve();
      }, 4250);
    });
  };

  /** Set when the user switches auto-resolve off during a drive-mode run, so the
      loop stops after the claim it is currently licensing. */
  const autoReleaseCancelledRef = useRef(false);
  useEffect(() => {
    const onCancel = () => { autoReleaseCancelledRef.current = true; };
    window.addEventListener("auto-release-cancel", onCancel);
    return () => window.removeEventListener("auto-release-cancel", onCancel);
  }, []);

  /** Listen for the `auto-release-animate-claims` window event fired by
      AccountLicensesPage when the user toggles auto-release ON. Find
      every Ready-to-Release card in the current `items`, animate each
      sequentially through the same Resolved! → fade → collapse
      sequence the manual Release button uses, then refresh at the end.
      Backend has already released the claims via the toggle's
      setAutoRelease() call — this is visual-only. */
  useEffect(() => {
    const onAutoReleaseAnimate = (e: Event) => {
      // Prefer the ids the SERVER says it released. Filtering our own list is a
      // guess: it animates cards that were never released (a sweep the user
      // cancelled, a channel that hit its credit limit mid-batch), and misses
      // any the server released that our snapshot didn't have yet. Falls back to
      // the local filter when no ids are supplied.
      const detail = (e as CustomEvent<{ claimIds?: string[]; drive?: boolean }>)?.detail;
      // `drive` = this page owns the licensing: release each claim as its card
      // animates, so the run is real rather than a replay and can be stopped.
      const driveReleases = !!detail?.drive;
      autoReleaseCancelledRef.current = false;
      const fromServer = detail?.claimIds;
      const readyIds = Array.isArray(fromServer) && fromServer.length > 0
        ? fromServer.filter((id) => items.some((v) => v.id === id) && !resolvedIds.has(id))
        : items
          .filter((v) => v.status === "Ready to Release" && !resolvedIds.has(v.id))
          .map((v) => v.id);
      // Always answer with `done`, including the nothing-to-do case and via
      // `finally` — the pill's spinner is bracketed by these two events and must
      // never be left running.
      if (readyIds.length === 0) {
        window.dispatchEvent(new Event("auto-release-animate-done"));
        return;
      }
      (async () => {
        const licensed: string[] = [];
        try {
          for (const id of readyIds) {
            // Stop when the user switches auto-resolve back off. Checked between
            // claims, never mid-claim: the one already being licensed finishes
            // (we don't abandon a half-done provider release), then the run ends
            // and the remaining cards stay in Action Required.
            if (autoReleaseCancelledRef.current) break;
            if (driveReleases) {
              try {
                // The licence email is the server's business, fired as the claim
                // actually enters Licensed. It is NOT deferred to the end of the
                // run: a run that is closed or navigated away from part-way would
                // then leave licensed claims with no email at all. The email
                // follows the state change, always.
                const svc = await import("@/services/claimSubmissions.service");
                // `auto` — the machine is licensing this under the standing
                // instruction the user gave by switching auto-resolve on, not a
                // click on this card. Keeps the timeline and the toggle's audit
                // row telling the truth about which it was.
                await svc.releaseClaim(id, { auto: true });
                licensed.push(id);
              } catch {
                // Already released, or the release failed — don't animate a card
                // into Licensed that isn't.
                continue;
              }
            }
            await animateResolvedVisualOnly(id);
          }
          onRefresh?.();
        } finally {
          window.dispatchEvent(new Event("auto-release-animate-done"));
        }
      })();
    };
    window.addEventListener("auto-release-animate-claims", onAutoReleaseAnimate);
    return () => window.removeEventListener("auto-release-animate-claims", onAutoReleaseAnimate);
  }, [items, resolvedIds, onRefresh]);

  const handleSubmitAgain = (id: string) => {
    const target = items.find((v) => v.id === id);
    submittedAtCheckedAt.current[id] = target?.checkedAt ?? null;
    const realSubmissionId = target?.submissionId;
    const isRealClaim = target && !realSubmissionId && /^[0-9a-f-]{36}$/i.test(id);

    if (realSubmissionId) {
      // Pending submission → resubmit
      resubmitPending(realSubmissionId).catch(() => {});
    } else if (isRealClaim) {
      // Matched claim → re-run through the runner
      import("@/services/claimSubmissions.service").then(({ reRunClaim }) => {
        reRunClaim(id).catch(() => {});
      });
    }

    // Same two-stage button transition + silent move as handleResolve /
    // handleQueue. NO tab switch — user stays on Action Required so they
    // can keep churning through error cards.
    //   0ms     button → "Submitted!" (green confirmation, disabled)
    //   1800ms  button → "Moving to "Under Review"…" (greyed, spinner)
    //   3600ms  card starts fading out
    //   3950ms  card collapses height
    //   4250ms  card silently moves to Under Review bucket
    setSubmitStageById((prev) => ({ ...prev, [id]: "success" }));
    setTimeout(() => {
      setSubmitStageById((prev) => ({ ...prev, [id]: "moving" }));
    }, 1800);
    setTimeout(() => {
      setSubmittingId(id);
    }, 3600);
    setTimeout(() => {
      setSubmittingId(null);
      setSubmittingCollapsingId(id);
    }, 3950);
    setTimeout(() => {
      setSubmittedIds((prev) => new Set(prev).add(id));
      setSubmittingCollapsingId(null);
      setSubmitStageById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      noteRecentMove(id);
      // One refresh here is not enough. The runner takes a few seconds — longer
      // when the GPT music-use call is involved — so a single fetch at 4.25s
      // usually lands while the job is still running. The card then sits on
      // "Under Review" indefinitely, and the only way to see the verdict you
      // just asked for is to reload the page. Poll across a window instead, the
      // same shape `pollClaimsForReChecks` uses after a channel change.
      RECHECK_POLL_MS.forEach((d) => window.setTimeout(() => { onRefresh?.(); }, d));
    }, 4250);
  };

  /* Let a re-checked card go once the runner has actually answered.
     `submittedIds` is what holds it in Under Review; this is the only thing that
     ever removes an id. A card is released when its `checkedAt` is newer than
     the value captured at the click — which covers the case that broke this
     before, a re-run landing on the SAME verdict, where nothing else about the
     card changes. Claims that had never been checked release as soon as they
     have a `checkedAt` at all. */
  useEffect(() => {
    if (submittedIds.size === 0) return;
    const finished = [...submittedIds].filter((id) => {
      const item = items.find((v) => v.id === id);
      if (!item) return false;
      const now = item.checkedAt ?? null;
      if (!now) return false;
      const before = submittedAtCheckedAt.current[id] ?? null;
      return before === null || new Date(now).getTime() > new Date(before).getTime();
    });
    if (finished.length === 0) return;
    setSubmittedIds((prev) => {
      const next = new Set(prev);
      finished.forEach((id) => {
        next.delete(id);
        delete submittedAtCheckedAt.current[id];
      });
      return next;
    });
  }, [items, submittedIds]);

  const q = search.toLowerCase().trim();
  const filtered = q
    ? items.filter((v) =>
        v.videoTitle.toLowerCase().includes(q) ||
        v.trackTitle.toLowerCase().includes(q) ||
        v.trackArtist.toLowerCase().includes(q) ||
        v.status.toLowerCase().includes(q)
      )
    : items;

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === "status") return (STATUS_ORDER[a.status.toLowerCase()] ?? 0) - (STATUS_ORDER[b.status.toLowerCase()] ?? 0);
    if (sortKey === "date") return (b.date || "").localeCompare(a.date || "");
    if (sortKey === "title") return a.trackTitle.localeCompare(b.trackTitle);
    return 0;
  });

  const activeBase = sorted.filter((v) => !hiddenIds.has(v.id));
  // Merge ghost items (fading out after removal) so they animate before disappearing
  const active = [...activeBase, ...ghostItems.filter((g) => !hiddenIds.has(g.id) && !activeBase.some((a) => a.id === g.id))];
  const hidden = liftRecentToTop(sorted.filter((v) => hiddenIds.has(v.id)));
  const actionRequired = liftRecentToTop(
    active.filter((v) => (ERROR_STATUSES.includes(v.status) || v.isVideoDeleted) && !submittedIds.has(v.id) && !queuedIds.has(v.id) && !resolvedIds.has(v.id)),
  );
  const queued = liftRecentToTop(active.filter((v) => queuedIds.has(v.id)));
  const inReview = liftRecentToTop(
    active.filter((v) => v.status === "Analyzing" || v.status === "Searching" || submittedIds.has(v.id)).map(
      (v) => submittedIds.has(v.id) && v.status !== "Analyzing" && v.status !== "Searching" ? { ...v, status: "Analyzing" as VideoStatus } : v,
    ),
  );
  // Whitelisted bucket includes both backend-confirmed Whitelisted and
  // optimistic resolvedIds (force-flipped to "Whitelisted" so the card
  // renders the resolved chrome immediately, before refresh lands).
  const whitelisted = liftRecentToTop(
    active
      .filter((v) => v.status === "Whitelisted" || resolvedIds.has(v.id))
      .map((v) => (resolvedIds.has(v.id) && v.status !== "Whitelisted" ? { ...v, status: "Whitelisted" as VideoStatus } : v)),
  );

  // Sync the global action-required count so header + sidebar badges stay in
  // sync. The badge is the TOTAL across all channels, so only push when the
  // list is UNFILTERED (no channel selected) — `actionRequired.length` then
  // equals the full count. While a channel filter is active we skip the push,
  // leaving the badge at the last total (the /claims/my/counts endpoint also
  // backs it). Skip while loading so the empty snapshot doesn't flash 0.
  useEffect(() => {
    if (!dataLoaded || selectedChannelId !== null) return;
    setActionRequiredCount(actionRequired.length);
  }, [actionRequired.length, dataLoaded, selectedChannelId]);

  // Note: we used to early-return an empty state here when everything was
  // empty, but that hid the tab bar during initial load (items=[] during
  // fetch, see FreeView/PremiumView loading gate). The tab bar now always
  // renders; each tab shows its own empty state when its bucket is zero.

  const tabs = [
    { key: "action", label: "Action Required", count: actionRequired.length, highlight: actionRequired.length > 0 },
    { key: "review", label: "Under Review", count: inReview.length, highlight: false },
    ...(showQueued ? [{ key: "queued", label: "Queued", count: queued.length, highlight: false }] : []),
    { key: "licensed", label: "Licensed", count: whitelisted.length, highlight: false },
    { key: "hidden", label: "Hidden", count: hidden.length, highlight: false },
  ];

  // A paramless entry point (the header "Claims" link → /account/claims) stays
  // on Action Required — the `activeTab` fallback above. We deliberately do NOT
  // rewrite the URL or hop to the first non-empty tab on mount: that showed
  // Action Required for a frame and then visibly forwarded the user to
  // ?tab=licensed. Action Required has its own empty state, so landing there
  // with nothing to do reads fine. Deep links (?tab=…) are unaffected.

  // When parent signals a fresh submission, jump to the tab where the new
  // card will land. Backend's submit response (oEmbed check result) tells us
  // whether the video is deleted/private — that card goes to Action Required,
  // everything else starts in Under Review.
  useEffect(() => {
    if (!pendingSubmissions || pendingSubmissions.length === 0) return;
    const latest = pendingSubmissions[pendingSubmissions.length - 1];
    setActiveTab(latest.videoLooksDeleted ? "action" : "review");
  }, [pendingSubmissions]);

  const pickerEl = channelCredits.length >= 1
    ? <ChannelFilterDropdown channels={channelCredits} selectedId={selectedChannelId} onSelect={setSelectedChannelId} onChannelChange={onChannelChange} onChannelDisconnected={onChannelDisconnected} onManageSubscription={onManageSubscription} isPastDue={isPastDue} cap={channelCap} newCounts={newCounts} />
    : null;

  return (
    <>
      {isCompact ? (
        <>
          {/* Phone/tablet: the darker band (continuing the heading band above)
              wraps the per-channel warning banners + the channel picker
              (Subscription) or the Submit-Video box (Free). Its bottom edge is
              the separation — the status pills and cards sit below on the page
              background. Auto-resolve stays right of the heading (in the
              AccountLayout title row). Rendered only when it has content, so
              the Free view (no warnings/picker) doesn't show an empty strip. */}
          {(channelWarnings || bandContent || pickerEl) && (
            <HeaderBand $tight={tightBand}>
              {bandContent}
              {pickerEl}
              {channelWarnings}
            </HeaderBand>
          )}
          <TabBarRenderer tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} tabTop={tabTop} tabBottom={tabBottom} />
        </>
      ) : (
        <>
          {bandContent}
          {/* Desktop: original inline tab bar — picker | divider | pills. The
              per-channel warning sits BELOW the tab bar/picker. */}
          <TabBarRenderer
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabTop={tabTop}
            tabBottom={tabBottom}
            rightSlot={tabBarRightSlot}
            leftSlot={pickerEl ?? undefined}
          />
          {/* Desktop: extra bottom gap below the warning (the node's -8 bottom
              margin is tuned for the tighter mobile band). */}
          {/* 8, not 16: each warning already carries 10px below it and the
              list wrapper pulls 8 back, so 16 here made the step from the
              last warning to whatever follows nearly twice the step
              between the warnings themselves. */}
          <div style={{ paddingBottom: 8 }}>{channelWarnings}</div>
        </>
      )}

      {/* Top alerts — rendered between the header band and the active-tab
          content: the "X new claims — Refresh" banner sits right above the
          cards it populates. (Per-channel usage warnings live in the header
          band above, not here.) Subscription-view only; FreeView passes
          nothing. MusicUseInviteBanner self-manages its own visibility
          (returns null when there's nothing new to surface) so it can live
          here unconditionally for both free and premium views. */}
      {topAlerts}
      <MusicUseInviteBanner />

      {/* Tab content — keyed on activeTab so switching pills remounts it and
          replays the fade-up entrance. */}
      <TabContent key={activeTab}>
      {activeTab === "action" && (
        actionRequired.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {actionRequired.map((v) => {
              const card = (
                <HideWrapper isFading={submittingId === v.id || fadingOutIds.has(v.id)} isCollapsing={submittingCollapsingId === v.id || (ghostItems.some((g) => g.id === v.id) && !fadingOutIds.has(v.id))}>
                  <VideoCard item={v} showUpgrade={showUpgrade} isNew={isClaimNew(v)} onVisible={() => markCardViewed(v.id)} onHide={handleHide} onSubmitAgain={handleSubmitAgain} onQueue={handleQueue} onManageSub={() => setManageSubOpen(true)} onOpenLicense={(it, preferMode) => {
                    // Direct dispatch — bypasses the LicenseModal entirely.
                    // "subscription" opens the Configure-Your-Plan cart step;
                    // "single" adds this track to the cart and opens the
                    // cart on the cart-review step. Both flows already exist
                    // and are wired to checkout; we just feed them the right
                    // payload from the matched track context on the claim.
                    if (preferMode === "subscription") {
                      dispatch(openSubscriptionCheckout({}));
                      return;
                    }
                    if (preferMode === "single") {
                      if (!it.trackId) {
                        // Track couldn't be matched server-side — fall back
                        // to the legacy modal so the user can still see the
                        // license options. Shouldn't happen for real catalog
                        // tracks but defensive against odd assetTitle data.
                        setLicenseModalItem(it);
                        return;
                      }
                      // One claim needs ONE licence. `addToCart` always ADDS to
                      // the existing quantity — correct for "Add to Cart" on a
                      // track page, wrong here: clicking this twice, or coming
                      // back after a failed attempt, silently doubles the bill
                      // for a single video that can only use one licence. So if
                      // it is already in the cart, just open the cart.
                      const alreadyInCart = cartItems.some(
                        (ci) => ci.trackId === it.trackId && ci.licenseType === "youtube-video",
                      );
                      if (!alreadyInCart) {
                        dispatch(
                          addToCart({
                            trackId: it.trackId,
                            // The catalogue track, not the provider's asset
                            // title — they can differ, and the cart has to name
                            // the recording it is charging for.
                            trackName: it.matchedTrackName || it.trackTitle,
                            composerName: it.trackArtist,
                            coverUrl: it.coverUrl || "",
                            licenseType: "youtube-video",
                            licenseLabel: "YouTube Video License",
                            // Priced by the track's licence tier. This used to
                            // pass `singleLicensePrice` for every track, so a
                            // `plus` track entered the cart 30 under and only
                            // the re-pricing pass corrected it — after the
                            // customer had already read the wrong total.
                            price: singleLicensePriceForTier(subConfig, it.licenseTier),
                            quantity: 1,
                          }),
                        );
                        recordMediaEvent(it.trackId, "track", "cart_add");
                      }
                      dispatch(openCart());
                      return;
                    }
                    setLicenseModalItem(it);
                  }} onResolve={handleResolve} resolveError={resolveError?.id === v.id ? resolveError.message : null} resolveStage={resolveStageById[v.id]} submitStage={submitStageById[v.id]} queueStage={queueStageById[v.id]} onConfirmDeleted={handleConfirmDeleted} isPastDue={isPastDue} />
                </HideWrapper>
              );
              return appearingIds.has(v.id)
                ? <AppearWrapper key={v.id}>{card}</AppearWrapper>
                : <React.Fragment key={v.id}>{card}</React.Fragment>;
            })}
          </div>
        ) : dataLoaded ? (
          <S.EmptyState>
            <S.EmptyText>No actions required at the moment.</S.EmptyText>
            {emptyHint}
          </S.EmptyState>
        ) : null
      )}

      {activeTab === "review" && (
        inReview.length > 0 ? (
          <>
          {UnderReviewHint}
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {inReview.map((v) => {
              const isNewSubmission = v.id.startsWith("sub-");
              const card = <VideoCard item={v} showUpgrade={showUpgrade} />;
              return isNewSubmission || appearingIds.has(v.id) ? (
                <AppearWrapper key={v.id}>{card}</AppearWrapper>
              ) : (
                <React.Fragment key={v.id}>{card}</React.Fragment>
              );
            })}
          </div>
          </>
        ) : dataLoaded ? (
          <S.EmptyState><S.EmptyText>No videos under review.</S.EmptyText></S.EmptyState>
        ) : null
      )}

      {activeTab === "queued" && (
        queued.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {queued.map((v) => {
              const card = <VideoCard item={v} showUpgrade={showUpgrade} parked />;
              return appearingIds.has(v.id)
                ? <AppearWrapper key={v.id}>{card}</AppearWrapper>
                : <React.Fragment key={v.id}>{card}</React.Fragment>;
            })}
          </div>
        ) : dataLoaded ? (
          <S.EmptyState><S.EmptyText>No videos queued for next billing.</S.EmptyText></S.EmptyState>
        ) : null
      )}

      {activeTab === "licensed" && (
        whitelisted.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {whitelisted.map((v) => {
              const card = (
                <HideWrapper
                  isFading={submittingId === v.id}
                  isCollapsing={submittingCollapsingId === v.id}
                >
                  <VideoCard
                    item={v}
                    showUpgrade={showUpgrade}
                    onHide={handleHide}
                    // Only real claim rows have a certificate — a dev-preview
                    // mock card has nothing to certify, so it gets no button
                    // rather than a button that 404s.
                    onOpenCertificate={/^[0-9a-f-]{36}$/.test(v.id) ? setCertificateClaimId : undefined}
                  />
                </HideWrapper>
              );
              return appearingIds.has(v.id)
                ? <AppearWrapper key={v.id}>{card}</AppearWrapper>
                : <React.Fragment key={v.id}>{card}</React.Fragment>;
            })}
          </div>
        ) : dataLoaded ? (
          <S.EmptyState><S.EmptyText>No licensed videos yet. Every resolved claim becomes a license, with its own number and document.</S.EmptyText></S.EmptyState>
        ) : null
      )}

      {activeTab === "hidden" && (
        hidden.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {hidden.map((v) => {
              const targetTab = resolveNaturalTab(v);
              const targetLabel = TAB_LABELS[targetTab] ?? "Unhide";
              const card = (
                <HideWrapper
                  isFading={submittingId === v.id}
                  isCollapsing={submittingCollapsingId === v.id}
                >
                  <VideoCard
                    item={v}
                    showUpgrade={showUpgrade}
                    onUnhide={handleUnhide}
                    unhideTargetLabel={`Move to ${targetLabel}`}
                  />
                </HideWrapper>
              );
              return appearingIds.has(v.id)
                ? <AppearWrapper key={v.id}>{card}</AppearWrapper>
                : <React.Fragment key={v.id}>{card}</React.Fragment>;
            })}
          </div>
        ) : dataLoaded ? (
          <S.EmptyState><S.EmptyText>No hidden videos.</S.EmptyText></S.EmptyState>
        ) : null
      )}
      </TabContent>

      {/* Manage Subscription Modal */}
      <ManageAddonsModal
        open={manageSubOpen}
        onClose={() => setManageSubOpen(false)}
        currentChannels={2}
        currentClaims={15}
        billingPeriod="monthly"
        daysLeftInPeriod={15}
        periodEndDate="2026-05-10"
      />

      {/* Plan + Single License modal for "Not Covered" items */}
      <LicenseModal
        open={!!licenseModalItem}
        onClose={() => setLicenseModalItem(null)}
        trackName={licenseModalItem?.trackTitle || ""}
        trackId={licenseModalItem?.id || ""}
        trackSlug={(licenseModalItem?.trackTitle || "").toLowerCase().replace(/\s+/g, "-")}
        composerName={licenseModalItem?.trackArtist || ""}
        coverUrl={licenseModalItem ? `https://img.youtube.com/vi/${getVideoId(licenseModalItem.videoUrl)}/mqdefault.jpg` : ""}
        tier="premium"
        licenseTier={(licenseModalItem as any)?.licenseTier}
      />

      <LicenceCertificateModal
        claimId={certificateClaimId}
        onClose={() => setCertificateClaimId(null)}
      />
    </>
  );
};

/* ══════════════════════════════════════
   CHANNELS PANEL (multi-channel)
   ══════════════════════════════════════ */

// Mock per-channel claims usage
const MOCK_CHANNEL_CLAIMS: Record<string, { used: number; limit: number }> = {
  "https://www.youtube.com/@AmbientMusicChannel": { used: 11, limit: 15 },
  "https://www.youtube.com/@MeditationSoundsOfficial": { used: 7, limit: 15 },
};



/** Friendly, input-agnostic copy for channel resolve/connect errors. Never
 *  echoes the URL/handle the user typed; leads with "This …" where it reads
 *  naturally. Keyed off the backend error code. */
const friendlyChannelError = (code: string): string => {
  switch (code) {
    case "already_connected": return "This channel is already connected to your account.";
    case "channel_taken": return "This channel is already connected to another Audio Sanctum account.";
    case "channel_not_found": return "This doesn't match any YouTube channel.";
    case "video_not_found": return "This doesn't match any YouTube video.";
    case "not_youtube": return "This isn't a YouTube link.";
    case "invalid_url":
    case "unrecognized_format":
    case "invalid_input": return "This doesn't look like a YouTube channel, video, or @handle.";
    case "youtube_api_error": return "Couldn't reach YouTube just now — please try again in a moment.";
    case "free_plan_no_channels": return "Upgrade to a subscription to connect channels.";
    case "past_due": return "Connecting channels is paused while your payment is overdue.";
    case "channel_limit_reached": return "All your channel slots are full.";
    case "slot_taken": return "This slot already has a channel — pick an empty one.";
    case "invalid_slot": return "That slot isn't available.";
    default: return "This doesn't seem to be a YouTube Channel, @Handle or YouTube Video. Please check and try again.";
  }
};

/** Smooth in/out error banner shown BELOW a slot's textbox (so the box itself
 *  never grows). Left-indented to line up with the textbox (past the slot
 *  number). Keeps the last message during the collapse so it doesn't vanish
 *  mid-animation. */
const SlotErrorBanner: React.FC<{ message: string | null; variant?: "error" | "success"; onClose?: () => void }> = ({ message, variant = "error", onClose }) => {
  const [shown, setShown] = useState<string | null>(message);
  useEffect(() => { if (message) setShown(message); }, [message]);
  const open = !!message;
  const bg = variant === "success" ? "rgb(16, 117, 70)" : "#dd7a33";
  return (
    <div style={{ overflow: "hidden", maxHeight: open ? (variant === "success" ? 90 : 160) : 0, opacity: open ? 1 : 0, transition: "max-height 0.4s ease, opacity 0.4s ease" }}>
      {/* paddingLeft 26 = slot-number width (16) + row gap (10) so the banner's
          left edge aligns with the textbox, not the slot number. */}
      <div style={{ padding: "0 0 8px 26px" }}>
        <div data-banner-surface style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", ...bannerSurface(bg), borderRadius: 6, fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 500, color: "#fff" }}>
          <span style={{ flex: 1 }}>{shown}</span>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Dismiss"
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, marginRight: -4, borderRadius: 5, background: "transparent", border: "none", cursor: "pointer", color: "#fff", fontSize: 16, lineHeight: 1, padding: 0, opacity: 0.9, transition: "background 0.15s ease" }}
            >
              ×
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** Slot usage bar (green → amber → red). Module-level so both the occupied
 *  row and the empty-slot row can render it. */
const renderUsageBar = (used: number, limit: number) => {
  if (!limit) return null;
  const pct = Math.round((used / limit) * 100);
  const fill =
    pct >= 100 ? "linear-gradient(90deg, #e47a6a, #f09080)"
    : pct >= 80 ? "linear-gradient(90deg, #f5a623, #ffcc66)"
    : `linear-gradient(90deg, ${colors.accent.green}, #6ee7b7)`;
  const textColor =
    pct >= 100 ? colors.accent.red
    : pct >= 80 ? "#f5a623"
    : colors.text.muted;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
      <span style={{ width: 78, height: 4, borderRadius: 2, background: "rgba(255, 255, 255, 0.06)", overflow: "hidden", display: "flex", flexShrink: 0 }}>
        <span style={{ height: "100%", borderRadius: 2, width: `${Math.min(100, pct)}%`, background: fill, transition: "width 0.4s ease" }} />
      </span>
      <span style={{ fontFamily: fonts.number, fontSize: fontSizes.xs, color: textColor, whiteSpace: "nowrap" }}>{used}/{limit}</span>
    </span>
  );
};

const slotNumberBadge = (n: number) => (
  <span style={{ fontFamily: fonts.number, fontSize: fontSizes.sm, color: colors.text.muted, width: 16, flexShrink: 0, textAlign: "center" }}>{n}</span>
);

/** Canvas text measurement (shared 2d context) — used to size the channel-name
 *  column to the longest name so the rows align like a table. */
const NAME_FONT = `500 13px ${fonts.body}`; // fontSizes.sm (0.8125rem) in px — canvas needs absolute units
let _measureCtx: CanvasRenderingContext2D | null | undefined;
const measureTextWidth = (text: string, font: string): number => {
  if (typeof document === "undefined") return text.length * 7;
  if (_measureCtx === undefined) _measureCtx = document.createElement("canvas").getContext("2d");
  if (!_measureCtx) return text.length * 7;
  _measureCtx.font = font;
  return _measureCtx.measureText(text).width;
};
/** Hard cap so one very long name can't eat the row (it ellipsises instead). */
const NAME_COL_MAX = 220;

/** The per-channel disconnect button — a 28px chip with a permanent subtle
 *  background (so it always reads as a button, never an empty gap). The trash
 *  glyph is grey by default and turns red with a red-tinted hover background;
 *  click opens the disconnect confirm modal. `disabled` greys it out (empty
 *  slots, or while past_due). */
const TrashButton: React.FC<{ disabled?: boolean; onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }> = ({ disabled, onClick }) => (
  <button
    type="button"
    aria-label="Disconnect channel"
    disabled={disabled}
    onClick={onClick}
    onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.background = "rgba(228, 122, 106, 0.16)"; e.currentTarget.style.color = colors.accent.red; } }}
    onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.background = "rgba(255, 255, 255, 0.07)"; e.currentTarget.style.color = colors.text.muted; } }}
    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, background: "rgba(255, 255, 255, 0.07)", border: "none", color: disabled ? "rgba(251, 244, 236, 0.28)" : colors.text.muted, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.7 : 1, transition: "background 0.15s ease, color 0.15s ease", padding: 0 }}
  >
    <TrashIcon />
  </button>
);

/** Status pill for a slot — green "Connected" / grey "Not connected", text
 *  centered. Renders as a block that FILLS its cell (width 100%) so the cell
 *  width equals the pill width — that keeps the gaps on either side of the
 *  flanking `|` dividers equal. */
const renderSlotLabel = (connected: boolean): React.ReactNode => (
  <span style={{
    display: "block", width: "100%", boxSizing: "border-box",
    fontFamily: fonts.body, fontSize: fontSizes.xs, fontWeight: 500,
    whiteSpace: "nowrap", padding: "3px 0", borderRadius: 999, textAlign: "center",
    color: connected ? colors.accent.green : colors.text.muted,
    background: connected ? "rgba(52, 211, 153, 0.14)" : "rgba(255, 255, 255, 0.05)",
    border: `1px solid ${connected ? "rgba(52, 211, 153, 0.3)" : "rgba(255, 255, 255, 0.1)"}`,
  }}>{connected ? "Connected" : "Not connected"}</span>
);

/** Width of the status-pill cell — sized to fit "Not connected"; both pills use
 *  it so the connected/empty states share one footprint and the dividers stay
 *  aligned across rows. */
const SLOT_LABEL_WIDTH = 104;

/**
 * Shared right-hand cluster for a channel slot row — usage bar + count,
 * dividers, status pill, and the ⋮ options slot. Used by BOTH the connected
 * row and the empty (add-a-channel) row with identical fixed widths so the
 * columns line up exactly across states. Empty slots show their retained usage
 * bar, a grey "Not connected" pill, and a greyed-out ⋮ button — every column
 * filled, just inert. The pill is framed by a divider on each side with equal
 * spacing.
 */
const SlotRightCluster: React.FC<{
  used: number;
  limit: number;
  /** Connected slot → green pill; empty → grey pill. */
  connected?: boolean;
  /** Overrides the default label content (e.g. a transient "Looking up…"). */
  label?: React.ReactNode;
  /** The ⋮ slot content. Defaults to a disabled KebabButton (empty slots). */
  kebab?: React.ReactNode;
  /** Draw a divider BEFORE the usage bar too, separating it from whatever sits
   *  to the left (the channel name on a connected row). Spaced identically to the
   *  dividers between the bar, the label and the ⋮. */
  leadingDivider?: boolean;
}> = ({ used, limit, connected, label, kebab, leadingDivider }) => {
  const labelContent = label ?? renderSlotLabel(!!connected);
  const showDivider = limit > 0 && !!labelContent;
  const divider = (
    <span style={{ width: 8, display: "flex", justifyContent: "center", flexShrink: 0 }}>
      {showDivider ? <span style={{ color: "rgba(251, 244, 236, 0.15)" }}>|</span> : null}
    </span>
  );
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, paddingLeft: leadingDivider ? 8 : 12, marginLeft: "auto" }}>
      {/* paddingLeft matches the 8px flex gap so the space either side of this
          divider is equal, same as the ones further right. */}
      {leadingDivider ? divider : null}
      {limit > 0 ? renderUsageBar(used, limit) : <span style={{ width: 110, flexShrink: 0 }} />}
      {divider}
      <span style={{ width: SLOT_LABEL_WIDTH, flexShrink: 0, display: "flex", alignItems: "center" }}>
        {labelContent}
      </span>
      {divider}
      <span style={{ width: 28, flexShrink: 0, position: "relative", display: "flex", justifyContent: "center" }}>
        {kebab ?? <TrashButton disabled />}
      </span>
    </span>
  );
};

/**
 * One EMPTY slot's add-a-channel row — owns ALL its own state (input, lookup,
 * preview, error, submit) so every slot's add flow is fully independent: a
 * preview in slot 1 stays in slot 1 while the user types a different URL into
 * slot 2. Type → debounced auto-resolve → preview → "+ Connect" commits.
 */
const EmptySlotRow: React.FC<{
  slotIndex: number;
  used: number;
  limit: number;
  isPastDue?: boolean;
  /** Channel ids already connected in another slot. */
  connectedChannelIds: string[];
  /** Channel ids currently previewed (not yet connected) in OTHER slots. */
  otherPreviewIds: string[];
  /** Report this slot's resolved-preview channel id up (or null when cleared)
   *  so sibling slots can block the same channel. */
  onPreviewChange: (channelId: string | null) => void;
  onConnected: () => void | Promise<void>;
}> = ({ slotIndex, used, limit, isPastDue, connectedChannelIds, otherPreviewIds, onPreviewChange, onConnected }) => {
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  const [inputValue, setInputValue] = useState("");
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<ResolvedChannelDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const seqRef = useRef(0);
  const onPreviewChangeRef = useRef(onPreviewChange);
  onPreviewChangeRef.current = onPreviewChange;

  // If this row unmounts mid-preview (e.g. a downgrade removed the slot),
  // release its reservation so a sibling isn't blocked forever.
  useEffect(() => () => onPreviewChangeRef.current(null), []);

  // Plain function (recreated each render) so it always sees the latest
  // connected/other-preview ids without ref juggling.
  const resolve = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    const seq = ++seqRef.current;
    setResolving(true);
    setErrorMessage(null);
    try {
      const preview = await resolveUserChannel(trimmed);
      if (seq !== seqRef.current) return; // superseded by newer input
      // Dedup on the RESOLVED channel id, so different URLs / @handles that
      // point at the same channel are still caught.
      if (connectedChannelIds.includes(preview.channelId)) {
        setErrorMessage(friendlyChannelError("already_connected"));
        return;
      }
      if (otherPreviewIds.includes(preview.channelId)) {
        setErrorMessage("This channel is already selected in another slot.");
        return;
      }
      setPendingPreview(preview);
      onPreviewChange(preview.channelId);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setErrorMessage(friendlyChannelError(parseChannelError(err).code));
    } finally {
      if (seq === seqRef.current) setResolving(false);
    }
  };

  const handleChange = (value: string) => {
    setInputValue(value);
    if (pendingPreview) { setPendingPreview(null); onPreviewChange(null); }
    if (errorMessage) setErrorMessage(null);
    seqRef.current++; // invalidate any in-flight lookup
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (value.trim().length >= 3) timerRef.current = window.setTimeout(() => { void resolve(value); }, 550);
  };

  const handleClear = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    seqRef.current++;
    setInputValue("");
    if (pendingPreview) onPreviewChange(null);
    setPendingPreview(null);
    setResolving(false);
    setErrorMessage(null);
  };

  const handleConfirm = async () => {
    if (!pendingPreview || submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await connectUserChannel(inputValue.trim(), slotIndex);
      onPreviewChange(null);
      setInputValue("");
      setPendingPreview(null);
      // onConnected reloads the panel (this empty row becomes a connected row)
      // and raises the green "scanning" notice at the panel level.
      await onConnected();
    } catch (err) {
      setErrorMessage(friendlyChannelError(parseChannelError(err).code));
    } finally {
      setSubmitting(false);
    }
  };

  if (pendingPreview) {
    // Phone/tablet: the Cancel/Connect buttons live INSIDE the slot box (a column
    // — channel info on top, buttons below), not as a separate row beneath it.
    if (isCompact) {
      return (
        <>
        <div
          ref={(el) => { if (el && !el.dataset.animated) { el.dataset.animated = "1"; el.style.opacity = "0"; el.style.transition = "opacity 0.22s ease"; requestAnimationFrame(() => { el.style.opacity = "1"; }); } }}
          style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {slotNumberBadge(slotIndex + 1)}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10, padding: "10px 12px", borderRadius: 8, border: `1px solid ${colors.border.light}`, background: "rgba(235, 194, 142, 0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              {pendingPreview.thumbnailUrl ? (
                <img src={pendingPreview.thumbnailUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }} />
              ) : (
                <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: "rgba(235, 194, 142, 0.15)" }} />
              )}
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.2 }}>
                <span style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 600, color: colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pendingPreview.name}</span>
                <span style={{ fontFamily: fonts.body, fontSize: "0.75rem", color: colors.text.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pendingPreview.canonicalUrl.replace(/^https?:\/\//, "")}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <ActionBtn onClick={handleClear} style={{ flex: 1, justifyContent: "center", height: 42, padding: "0 14px" }} disabled={submitting}>Cancel</ActionBtn>
              <ActionBtn $primary onClick={handleConfirm} style={{ flex: 1, justifyContent: "center", height: 42, padding: "0 16px", background: "#9cd3fd" }} disabled={submitting}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: "#173243" }}>
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {submitting ? "Connecting…" : "Connect"}
              </ActionBtn>
            </div>
          </div>
        </div>
        {/* Trailing banner (collapsed unless a confirm error) — mirrors the empty
            slot row's structure so the flex-gap spacing to neighbours matches. */}
        <SlotErrorBanner message={errorMessage} />
        </>
      );
    }
    return (
      <div
        ref={(el) => {
          if (el && !el.dataset.animated) {
            el.dataset.animated = "1";
            el.style.opacity = "0";
            el.style.transition = "opacity 0.22s ease";
            requestAnimationFrame(() => { el.style.opacity = "1"; });
          }
        }}
        style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 10, margin: "0 -12px", padding: "8px 12px", minHeight: 46 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          {slotNumberBadge(slotIndex + 1)}
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, padding: "6px 12px", height: 46, borderRadius: 8, border: `1px solid ${colors.border.light}`, background: "rgba(235, 194, 142, 0.05)", minWidth: 0 }}>
            {pendingPreview.thumbnailUrl ? (
              <img src={pendingPreview.thumbnailUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }} />
            ) : (
              <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: "rgba(235, 194, 142, 0.15)" }} />
            )}
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, lineHeight: 1.2 }}>
              <span style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 600, color: colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {pendingPreview.name}
              </span>
              <span style={{ fontFamily: fonts.body, fontSize: "0.75rem", color: colors.text.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {pendingPreview.canonicalUrl.replace(/^https?:\/\//, "")}
              </span>
            </div>
          </div>
        </div>
        {/* Buttons drop below the preview and split the width on phone/tablet
            (space-limited rows), inline on desktop. paddingLeft 26 (badge 16 +
            gap 10) aligns Cancel's left edge with the input/preview box. */}
        <div style={{ display: "flex", gap: 10, flexShrink: 0, paddingLeft: isCompact ? 26 : 0 }}>
          <ActionBtn onClick={handleClear} style={{ flex: isCompact ? 1 : "0 0 auto", justifyContent: "center", height: 46, padding: "0 16px" }} disabled={submitting}>
            Cancel
          </ActionBtn>
          <ActionBtn $primary onClick={handleConfirm} style={{ flex: isCompact ? 1 : "0 0 auto", justifyContent: "center", height: 46, padding: "0 20px", background: "#9cd3fd" }} disabled={submitting}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: "#173243" }}>
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {submitting ? "Connecting…" : "Connect"}
          </ActionBtn>
        </div>
      </div>
    );
  }

  const showClear = !!inputValue;
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: isCompact ? 0 : "0 -12px", padding: isCompact ? 0 : "8px 12px", minHeight: 46 }}>
        {slotNumberBadge(slotIndex + 1)}
        <div style={{ position: "relative", flex: 1, display: "flex" }}>
          <SearchInput
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
            placeholder={isPastDue ? "Adding channels is paused while your payment is overdue" : (isCompact ? "Enter a channel URL or @handle…" : "Enter a YouTube channel URL, @handle, or video URL…")}
            onKeyDown={(e: React.KeyboardEvent) => { if (!isPastDue && e.key === "Enter") { if (timerRef.current) window.clearTimeout(timerRef.current); void resolve(inputValue); } }}
            style={{ flex: 1, paddingLeft: "16px", paddingRight: showClear ? "36px" : "16px", opacity: isPastDue ? 0.4 : 1, cursor: isPastDue ? "not-allowed" : undefined }}
            disabled={submitting || isPastDue}
          />
          {showClear && (
            <button
              type="button"
              aria-label="Clear"
              onClick={handleClear}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = colors.text.muted; e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"; }}
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: "50%", padding: 0, background: "rgba(255, 255, 255, 0.1)", border: "none", cursor: "pointer", color: colors.text.muted, transition: "color 0.15s ease, background 0.15s ease" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        {/* Desktop: the same fixed-width cluster as a connected row so the bar /
            count align across states (retained usage + transient "Looking up…").
            On phone/tablet that cluster is too wide for the row, so it's dropped —
            the input gets the full width and resolving shows a small inline hint. */}
        {!isCompact ? (
          <SlotRightCluster
            used={used}
            limit={limit}
            label={resolving ? (
              <span style={{ fontFamily: fonts.body, fontSize: fontSizes.xs, color: colors.text.muted, whiteSpace: "nowrap" }}>Looking up…</span>
            ) : null}
          />
        ) : resolving ? (
          <span style={{ flexShrink: 0, fontFamily: fonts.body, fontSize: fontSizes.xs, color: colors.text.muted, whiteSpace: "nowrap" }}>Looking up…</span>
        ) : null}
      </div>
      <SlotErrorBanner message={!resolving ? errorMessage : null} />
    </>
  );
};

const ChannelsPanel: React.FC<{ empty?: boolean; maxChannels?: number; onManageSubscription?: () => void; onClose?: () => void; onChannelChange?: () => void; onChannelDisconnected?: (youtubeChannelId: string) => void; isPastDue?: boolean; accountSuspended?: boolean; hideNeedMore?: boolean; title?: string; embedded?: boolean }> = ({ onManageSubscription, onClose, onChannelChange, onChannelDisconnected, isPastDue, accountSuspended, hideNeedMore, title, embedded }) => {
  // Whitelisting suspended (ToU §17.4) blocks channel connect/remove/replace
  // exactly like PAST_DUE — no account changes until the credit is restored.
  // Read from context so it applies whether this panel is the connect panel or
  // the dropdown "Edit Channels", without threading the flag down every layer.
  const suspendedFromCtx = React.useContext(AccountSuspendedContext);
  const channelActionsBlocked = !!isPastDue || !!accountSuspended || suspendedFromCtx;
  const isCompact = useMediaQuery("(max-width: 1149.98px)");
  // Slot-based model: always show all N slots (N = cap), filled or empty. The
  // monthly usage belongs to the slot, so a channel connected into a slot with
  // retained usage inherits it. See docu/flows/whitelisting/slot-based-credits.md.
  // Slot add flow now lives PER-SLOT in <EmptySlotRow> (its own state), so each
  // slot's typing/preview is independent. The panel only owns the slot list.
  const [slots, setSlots] = useState<ChannelSlot[]>([]);
  const [cap, setCap] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [_removing, setRemoving] = useState<string | null>(null);
  const [confirmSlot, setConfirmSlot] = useState<ChannelSlot | null>(null);
  // Green "we're scanning this channel" notice, raised after a successful
  // connect. Holds the slotIndex that was just connected so the banner renders
  // directly under THAT slot's row (the empty row unmounts into a connected
  // row, so the notice is owned here at the panel level). Dismissible.
  const [scanNoticeSlot, setScanNoticeSlot] = useState<number | null>(null);
  // Re-measure once webfonts settle — first paint may use fallback metrics.
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    const f = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    f?.ready?.then(() => setFontsReady(true)).catch(() => { /* ignore */ });
  }, []);
  // (The fixed name-column width went with the URL column it existed to align
  //  — the name now just takes the space the URL was wasting.)
  // Channel ids currently PREVIEWED (not yet connected) per slot — so a channel
  // being added in one slot is blocked in the others, even before it's saved.
  const [previewedIds, setPreviewedIds] = useState<Record<number, string>>({});

  const handlePreviewChange = useCallback((slotIndex: number, channelId: string | null) => {
    setPreviewedIds((prev) => {
      if (channelId === null) {
        if (!(slotIndex in prev)) return prev;
        const next = { ...prev };
        delete next[slotIndex];
        return next;
      }
      if (prev[slotIndex] === channelId) return prev;
      return { ...prev, [slotIndex]: channelId };
    });
  }, []);

  const connectedCount = slots.filter((s) => s.channel).length;
  const allFull = cap > 0 && connectedCount >= cap;
  const connectedChannelIds = slots.filter((s) => s.channel).map((s) => s.channel!.youtubeChannelId);

  const reload = useCallback(async () => {
    const res = await getMyChannelCredits();
    setSlots(res.slots);
    setCap(res.cap);
  }, []);

  // Fetch on mount. The slot list carries occupant + per-slot usage in one go.
  useEffect(() => {
    let cancelled = false;
    getMyChannelCredits()
      .then((res) => {
        if (cancelled) return;
        setSlots(res.slots);
        setCap(res.cap);
      })
      .catch(() => {
        // Silent — the panel just stays empty if the user isn't subscribed
        // or the endpoint is unreachable.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRemove = async (slot: ChannelSlot) => {
    if (!slot.channel) return;
    const channelId = slot.channel.id;
    const youtubeChannelId = slot.channel.youtubeChannelId;
    setRemoving(channelId);
    // Optimistic — vacate the slot immediately (its usage stays on the slot)
    // and fade out this channel's claims in the list (backend removes the
    // unresolved ones; they re-detect from scratch if reconnected).
    setSlots((prev) => prev.map((s) => (s.slotIndex === slot.slotIndex ? { ...s, channel: null } : s)));
    onChannelDisconnected?.(youtubeChannelId);
    try {
      await disconnectUserChannel(channelId);
      onChannelChange?.();
      await reload();
    } catch {
      // Restore real state on failure.
      try {
        await reload();
      } catch {
        /* leave optimistic state */
      }
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div style={{
      // Embedded (inside the channel dropdown's manage tab): no card chrome —
      // it sits flush in the brown popover. Standalone: its own card surface.
      background: embedded ? "transparent" : colors.surface.card,
      border: embedded ? "none" : `1px solid ${colors.border.light}`,
      borderRadius: embedded ? 0 : "12px",
      // Embedded: uniform 6px inset around the slot list — first slot 6px below
      // the heading divider, last slot 6px above the panel bottom, 6px sides.
      padding: embedded ? "0 6px 6px" : "20px 24px 24px",
      display: "flex",
      flexDirection: "column",
      gap: embedded ? 6 : 4,
      position: "relative",
    }}>
      {/* Header — title (X close lives absolute top-right of the panel) */}
      <h3 style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: fonts.body,
        fontSize: "1rem",
        fontWeight: 600,
        color: colors.text.primary,
        // Equal space above and below the heading text (14px each). The divider
        // then sits 12px above the first channel (6px margin + 6px panel gap),
        // matching the slots' real 12px side/bottom inset (dropdown body's 6px
        // padding + the panel's own 6px).
        margin: "0 -8px 6px -8px",
        // 10px horizontal padding gives the title (left) and "Need more?"
        // (right) ~2px more breathing room from the panel edges than the slots.
        padding: "14px 10px 14px 10px",
        borderBottom: `1px solid ${colors.border.light}`,
        letterSpacing: "0.02em",
      }}>
        <span>{title ?? "Channels"}</span>
        {cap > 0 && (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 8px",
            borderRadius: 999,
            background: "rgba(235, 194, 142, 0.1)",
            border: "1px solid rgba(235, 194, 142, 0.2)",
            fontFamily: fonts.number,
            fontSize: "0.75rem",
            fontWeight: 500,
            lineHeight: 1,
            color: colors.text.muted,
            letterSpacing: 0,
          }}>
            {connectedCount}/{cap}
          </span>
        )}
        {/* "Need more?" lives on the far right of the heading row (by the N/N
            badge) when every slot is full — replaces the old link + divider
            below the last slot. */}
        {allFull && onManageSubscription && !hideNeedMore && (
          <span
            role="button"
            tabIndex={0}
            onClick={onManageSubscription}
            onKeyDown={(e) => { if (e.key === "Enter") onManageSubscription(); }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
            style={{ marginLeft: "auto", fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 400, color: colors.accent.blue, cursor: "pointer", letterSpacing: 0, textUnderlineOffset: "3px" }}
          >
            Need more?
          </span>
        )}
      </h3>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close channels"
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(235, 194, 142, 0.35)"; e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.5)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(235, 194, 142, 0.18)"; e.currentTarget.style.borderColor = "rgba(235, 194, 142, 0.3)"; }}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 2,
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "rgba(235, 194, 142, 0.18)",
            border: "1px solid rgba(235, 194, 142, 0.3)",
            cursor: "pointer",
            color: "#ebc28e",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.9rem",
            lineHeight: 1,
            transition: "background 0.2s ease, border-color 0.2s ease",
          }}
        >
          &#x2715;
        </button>
      )}

      {slots.map((slot) => {
        // Slot number badge (number font, same as durations) — sits in front
        // of every slot row, filled or empty.
        const slotNo = (
          <span style={{ fontFamily: fonts.number, fontSize: fontSizes.sm, color: colors.text.muted, width: 16, flexShrink: 0, textAlign: "center" }}>
            {slot.slotIndex + 1}
          </span>
        );
        // ── Occupied slot — channel + per-slot usage + Disconnect ──
        if (slot.channel) {
          const ch = slot.channel;
          const channelUrl = ch.channelHandle
            ? `https://www.youtube.com/${ch.channelHandle.startsWith("@") ? ch.channelHandle : "@" + ch.channelHandle}`
            : `https://www.youtube.com/channel/${ch.youtubeChannelId}`;
          const displayName = ch.channelName || ch.channelHandle || ch.youtubeChannelId;
          // ── Phone/tablet: a self-contained stacked card. Name + trash on the
          //    top line; usage bar + count + status on a second line. Nothing is
          //    fixed-width, so the row always fits the screen (no h-scroll). ──
          if (isCompact) {
            // Connected channels always show their thumbnail; if one is missing
            // we fall back to a neutral avatar circle (NOT a YouTube icon — those
            // are reserved for empty slots).
            const avatarFallback = (
              <span style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(235, 194, 142, 0.25)", color: colors.text.primary, fontFamily: fonts.body, fontSize: "0.7rem", fontWeight: 600 }}>
                {displayName.charAt(0).toUpperCase()}
              </span>
            );
            const avatarEl = ch.channelThumbnailUrl ? (
              <img
                src={ch.channelThumbnailUrl}
                alt=""
                onError={(e) => { const img = e.currentTarget; img.style.display = "none"; const n = img.nextElementSibling as HTMLElement | null; if (n) n.style.display = "inline-flex"; }}
                style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }}
              />
            ) : null;
            return (
              <React.Fragment key={`slot-${slot.slotIndex}`}>
              <div
                id={`channel-row-${ch.id}`}
                ref={(el) => {
                  if (el && !el.dataset.animated) {
                    el.dataset.animated = "1";
                    el.style.opacity = "0";
                    el.style.transition = "opacity 0.3s ease";
                    requestAnimationFrame(() => { el.style.opacity = "1"; });
                  }
                }}
                style={{ display: "flex", flexDirection: "column", gap: 9, padding: "11px 12px", borderRadius: 10, background: "rgba(0, 0, 0, 0.18)", border: "none" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontFamily: fonts.number, fontSize: fontSizes.xs, color: colors.text.muted, flexShrink: 0 }}>{slot.slotIndex + 1}</span>
                  {avatarEl}
                  <span style={{ display: avatarEl ? "none" : "inline-flex", flexShrink: 0 }}>{avatarFallback}</span>
                  <a
                    href={channelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ flex: 1, minWidth: 0, fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 600, color: colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: "none" }}
                  >
                    {displayName}
                  </a>
                  <TrashButton disabled={channelActionsBlocked} onClick={(e) => { e.stopPropagation(); setConfirmSlot(slot); }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, paddingLeft: 26 }}>
                  {slot.limit > 0 && renderUsageBar(slot.used, slot.limit)}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto", fontFamily: fonts.body, fontSize: fontSizes.xs, fontWeight: 500, color: colors.accent.green }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: colors.accent.green, flexShrink: 0 }} />
                    Connected
                  </span>
                </div>
                {/* The "we're scanning this channel" notice lives INSIDE the card,
                    full width, right after a connect. */}
                {scanNoticeSlot === slot.slotIndex && (
                  <div
                    ref={(el) => { if (el && !el.dataset.animated) { el.dataset.animated = "1"; el.style.opacity = "0"; el.style.transition = "opacity 0.3s ease"; requestAnimationFrame(() => { el.style.opacity = "1"; }); } }}
                    style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "9px 11px", background: "rgb(16, 117, 70)", borderRadius: 8, fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 500, color: "#fff" }}
                  >
                    <span style={{ flex: 1, lineHeight: 1.45 }}>We start scanning this channel for Audio Sanctum music and notify you once claims are detected.</span>
                    <button
                      type="button"
                      aria-label="Dismiss"
                      onClick={(e) => { e.stopPropagation(); setScanNoticeSlot(null); }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, marginRight: -3, borderRadius: 5, background: "transparent", border: "none", cursor: "pointer", color: "#fff", fontSize: 16, lineHeight: 1, padding: 0, opacity: 0.9, transition: "background 0.15s ease" }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={`slot-${slot.slotIndex}`}>
            <div
              id={`channel-row-${ch.id}`}
              ref={(el) => {
                if (el && !el.dataset.animated) {
                  el.dataset.animated = "1";
                  el.style.opacity = "0";
                  // Transition both opacity (entrance) and background (hover);
                  // omitted from the inline style prop so React never clobbers it.
                  el.style.transition = "opacity 0.3s ease, background 0.15s ease";
                  requestAnimationFrame(() => { el.style.opacity = "1"; });
                }
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              style={{ margin: "0 -12px", padding: "8px 12px", borderRadius: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {slotNo}
                {ch.channelThumbnailUrl ? (
                  <img
                    src={ch.channelThumbnailUrl}
                    alt=""
                    onError={(e) => {
                      const img = e.currentTarget;
                      img.style.display = "none";
                      const next = img.nextElementSibling as HTMLElement | null;
                      if (next) next.style.display = "flex";
                    }}
                    style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, objectFit: "cover", position: "relative", top: -1 }}
                  />
                ) : null}
                <span style={{ display: ch.channelThumbnailUrl ? "none" : "flex", flexShrink: 0, position: "relative", top: -1 }}>
                  <YtIcon />
                </span>
                {/* The NAME is the link to the channel. The URL used to sit in
                    its own column behind a "|" separator, where it had so little
                    room it rendered as "h…" — useless, and it stole width from
                    the name. Linking the name says the same thing and reads. */}
                <a
                  href={channelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                  style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, fontWeight: 500, color: colors.text.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", textDecoration: "none", textUnderlineOffset: "3px", cursor: "pointer", ...(isCompact ? { flex: 1, minWidth: 0 } : { flex: 1, minWidth: 0 }) }}
                >
                  {displayName}
                </a>
                <SlotRightCluster
                  used={slot.used}
                  limit={slot.limit}
                  connected
                  leadingDivider
                  kebab={
                    <TrashButton
                      disabled={channelActionsBlocked}
                      onClick={(e) => { e.stopPropagation(); setConfirmSlot(slot); }}
                    />
                  }
                />
              </div>
            </div>
            {/* Green dismissible "scanning" notice, directly under the slot
                whose channel was just connected. Always rendered (message-driven)
                so it animates open AND closed — a conditional mount would skip
                the max-height transition. */}
            <SlotErrorBanner
              message={scanNoticeSlot === slot.slotIndex ? "We start scanning this channel for Audio Sanctum music and notify you once claims are detected." : null}
              variant="success"
              onClose={() => setScanNoticeSlot(null)}
            />
            </React.Fragment>
          );
        }

        // ── Empty slot — independent add flow (its own component/state) ──
        const otherPreviewIds = Object.entries(previewedIds)
          .filter(([idx]) => Number(idx) !== slot.slotIndex)
          .map(([, id]) => id);
        return (
          <EmptySlotRow
            key={`slot-${slot.slotIndex}-empty`}
            slotIndex={slot.slotIndex}
            used={slot.used}
            limit={slot.limit}
            isPastDue={channelActionsBlocked}
            connectedChannelIds={connectedChannelIds}
            otherPreviewIds={otherPreviewIds}
            onPreviewChange={(id) => handlePreviewChange(slot.slotIndex, id)}
            onConnected={async () => {
              const idx = slot.slotIndex;
              await reload();
              onChannelChange?.();
              // Sequence: first let the row switch from textbox → connected view
              // and settle, THEN fade the banner in (a short delay, not the
              // near-instant rAF — otherwise both happen together).
              window.setTimeout(() => setScanNoticeSlot(idx), 550);
            }}
          />
        );
      })}

      {loading && slots.length === 0 && (
        <div style={{ fontFamily: fonts.body, fontSize: fontSizes.sm, color: colors.text.muted, padding: "8px 0" }}>
          Loading your channels…
        </div>
      )}

      <DisconnectChannelModal
        open={!!confirmSlot}
        channelName={confirmSlot?.channel?.channelName ?? null}
        submitting={!!confirmSlot?.channel && _removing === confirmSlot.channel.id}
        onConfirm={() => { if (confirmSlot) void handleRemove(confirmSlot); setConfirmSlot(null); }}
        onCancel={() => setConfirmSlot(null)}
      />
    </div>
  );
};

/**
 * Smooth open/close wrapper for `ChannelsPanel`. Uses the same
 * height + clip-path + margin-bottom animation as `FreeTrackCard` so the
 * panel expands/collapses in sync when the Channels toggle is clicked.
 * See `FreeTrackCard` above for the reference implementation — this is
 * the same pattern, just parameterized for a slightly different surface.
 *
 * Key details:
 * - We keep the inner panel mounted while `open === false` so the closing
 *   animation can run to completion before the wrapper's height hits 0.
 * - `ResizeObserver` watches the inner panel while open so changes in its
 *   height (e.g. connecting a new channel, adding an error message) grow
 *   the wrapper smoothly.
 * - `overflow: hidden` + `clipPath: inset(0 0 100% 0 round 16px)` clip the
 *   content to avoid visual bleed during the transition.
 */
const AnimatedChannelsPanel: React.FC<{ open?: boolean; empty?: boolean; maxChannels?: number; onManageSubscription?: () => void; onClose?: () => void; onChannelChange?: () => void; onChannelDisconnected?: (youtubeChannelId: string) => void; isPastDue?: boolean; accountSuspended?: boolean }> = ({ open, empty, maxChannels, onManageSubscription, onClose, onChannelChange, onChannelDisconnected, isPastDue, accountSuspended }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const prevOpen = useRef<boolean>(!!open);
  const [rendered, setRendered] = useState<boolean>(!!open);

  // Keep the child mounted while the closing animation plays, then
  // unmount after the transition is done.
  useEffect(() => {
    if (open) {
      setRendered(true);
      return;
    }
    if (!prevOpen.current) return;
    const timer = window.setTimeout(() => setRendered(false), 420);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    if (open && !prevOpen.current) {
      // Opening: measure, start at 0, transition to natural height
      const h = inner.offsetHeight;
      wrap.style.transition = "none";
      wrap.style.height = "0px";
      wrap.style.clipPath = "inset(0 0 100% 0 round 16px)";
      wrap.style.marginBottom = "0px";
      void wrap.offsetHeight;
      wrap.style.transition =
        "height 0.4s cubic-bezier(0.4, 0, 0.2, 1), clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      wrap.style.height = `${h}px`;
      wrap.style.clipPath = "inset(0 0 0 0 round 16px)";
      wrap.style.marginBottom = "24px";
    } else if (!open && prevOpen.current) {
      // Closing: snap wrapper from `auto` → current px value, then to 0
      const h = inner.offsetHeight;
      wrap.style.transition = "none";
      wrap.style.height = `${h}px`;
      void wrap.offsetHeight;
      wrap.style.transition =
        "height 0.4s cubic-bezier(0.4, 0, 0.2, 1), clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      wrap.style.height = "0px";
      wrap.style.clipPath = "inset(0 0 100% 0 round 16px)";
      wrap.style.marginBottom = "0px";
    } else if (open && prevOpen.current) {
      // Mounted ALREADY open (the first-connect panel renders with open=true
      // from the start, so neither animate branch fires) — show it immediately
      // with no animation. Without this the wrapper stayed at its initial
      // height:0 + clip and the whole add-channels panel was invisible.
      wrap.style.height = "auto";
      wrap.style.clipPath = "inset(0 0 0 0 round 16px)";
      wrap.style.marginBottom = "24px";
    }

    prevOpen.current = !!open;
  }, [open, rendered]);

  // After the opening animation finishes, release the wrapper height to
  // `auto` so content growth doesn't get clipped by a stale pixel value.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !open) return;
    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName === "height" && wrap.style.height !== "0px") {
        wrap.style.height = "auto";
      }
    };
    wrap.addEventListener("transitionend", onEnd);
    return () => wrap.removeEventListener("transitionend", onEnd);
  }, [open]);

  // While open, track the inner panel's height so the wrapper grows/shrinks
  // smoothly with internal content changes (new channel row, error line).
  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner || !open) return;
    const ro = new ResizeObserver(() => {
      if (!wrap || !inner) return;
      if (wrap.style.height === "0px") return;
      const h = inner.offsetHeight;
      wrap.style.transition = "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)";
      wrap.style.height = `${h}px`;
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [open, rendered]);

  if (!rendered) return null;

  return (
    <div
      ref={wrapRef}
      style={{
        overflow: "hidden",
        height: 0,
        marginBottom: 0,
        clipPath: "inset(0 0 100% 0 round 16px)",
      }}
    >
      <div ref={innerRef}>
        <ChannelsPanel
          empty={empty}
          maxChannels={maxChannels}
          onManageSubscription={onManageSubscription}
          onClose={onClose}
          onChannelChange={onChannelChange}
          onChannelDisconnected={onChannelDisconnected}
          isPastDue={isPastDue}
          accountSuspended={accountSuspended}
        />
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   FREE VIEW
   ════════════════════════════════════════════ */

const FreeView: React.FC<{ planModalOpen?: boolean; onClosePlanModal?: () => void; realClaims?: VideoItem[] | null; onRefresh?: () => void; tabBarRightSlot?: React.ReactNode }> = ({ planModalOpen, onClosePlanModal, realClaims, onRefresh, tabBarRightSlot }) => {
  const [pendingSubmissions, setPendingSubmissions] = useState<{ id: string; url: string; videoLooksDeleted?: boolean }[]>([]);
  // Render lifecycle:
  //   realClaims === null   → still loading the first fetch. Pass [] so
  //                           tab counts start at 0 (prevents the mock
  //                           counts flashing before real data arrives).
  //   realClaims.length > 0 → show real cards.
  //   realClaims.length === 0 → zero-card real users see mocks for dev preview.
  const items = realClaims === null ? [] : realClaims.length > 0 ? realClaims : MOCK_FREE_VIDEOS;
  // Client-side dedupe uses ONLY `items` (the fresh server snapshot), not
  // `pendingSubmissions` (a session log that never gets pruned when rows
  // are deleted). Otherwise clicking "Video was deleted" and resubmitting
  // the same URL would be blocked by a stale local entry even though the
  // backend row is gone. If dedupe here misses a race, the backend's
  // `already_submitted` guard catches it and we surface the inline error.
  const existingVideoIds = new Set<string>(
    items.map((v) => getVideoId(v.videoUrl)).filter(Boolean),
  );
  const submitBox = (
    <SubmitVideoInput
      onSubmit={(url, videoLooksDeleted) => {
        setPendingSubmissions((prev) => [...prev, { id: `sub-${Date.now()}-${prev.length}`, url, videoLooksDeleted }]);
        onRefresh?.();
      }}
      existingVideoIds={existingVideoIds}
    />
  );
  return (
    <>
      {/* The Submit-Video box rides in the header band (phone/tablet) — the
          same slot the Subscription view's channel picker uses — so it sits
          inside the dark band continuous with the heading, not floating above
          an empty strip. On desktop it renders above the tab bar. */}
      <ThreeSectionList items={items} search="" sortKey="status" showQueued={false} pendingSubmissions={pendingSubmissions} onRefresh={onRefresh} dataLoaded={realClaims !== null} tabBarRightSlot={tabBarRightSlot} bandContent={submitBox} tightBand tabTop={18} tabBottom={12} />
      <PlanUpgradeModal open={!!planModalOpen} onClose={() => onClosePlanModal?.()} />
    </>
  );
};

/** Compact settings area for Premium. All props except credits are
    currently decorative (full Auto Release / credit wiring lives elsewhere).
    The "Free Track Video" submit flow was removed in session 4 — subscription
    users receive claims exclusively via the backward path, and free/premium
    tracks are treated identically per §20.1. */
const SettingsCards: React.FC<{
  autoApprove: boolean;
  onEnableAutoApprove: () => void;
  onDisableAutoApprove: () => void;
  creditsRemaining: number;
  creditsTotal: number;
}> = ({ autoApprove, onEnableAutoApprove, onDisableAutoApprove, creditsRemaining, creditsTotal }) => {
  void creditsRemaining; void creditsTotal; void autoApprove; void onEnableAutoApprove; void onDisableAutoApprove;
  return null;
};

/* ════════════════════════════════════════════
   PREMIUM VIEW
   ════════════════════════════════════════════ */

const PremiumView: React.FC<{ realClaims?: VideoItem[] | null; onRefresh?: () => void; onChannelLimitHit?: () => void; tabBarRightSlot?: React.ReactNode; topAlerts?: React.ReactNode; channelWarnings?: React.ReactNode; isPastDue?: boolean; accountSuspended?: boolean; onChannelChange?: () => void; onChannelDisconnected?: (youtubeChannelId: string) => void; onManageSubscription?: () => void }> = ({ realClaims, onRefresh, onChannelLimitHit, tabBarRightSlot, topAlerts, channelWarnings, isPastDue, accountSuspended, onChannelChange, onChannelDisconnected, onManageSubscription }) => {
  // No pending submissions for subscription users — claims arrive via the
  // backward path (channel → runner) exclusively per §20.1.
  // Loading gate: null = still loading → [] so tab counts stay 0 until data
  // arrives. Empty-array path keeps mocks for dev preview.
  // Dev preview: ?mock=1 forces the full mock card set (every status at once)
  // regardless of real claims — handy for reviewing card states + buttons.
  const forceMock = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("mock") === "1";
  const baseItems = forceMock
    ? MOCK_PREMIUM_VIDEOS
    : realClaims === null ? [] : realClaims.length > 0 ? realClaims : MOCK_PREMIUM_VIDEOS;
  // In mock preview, give items a channelId (= channelName) so the channel
  // filter actually filters the mock cards.
  const items = forceMock ? baseItems.map((v) => ({ ...v, channelId: v.channelName ?? undefined })) : baseItems;

  // Per-channel credit rows for the channel filter — real from the API,
  // synthesized from the mock channels under ?mock=1 so the dropdown is
  // reviewable without real multi-channel data.
  const [channelCredits, setChannelCredits] = useState<ChannelCreditRow[]>([]);
  const [channelCap, setChannelCap] = useState(0);
  useEffect(() => {
    if (forceMock) {
      const names = Array.from(new Set(MOCK_PREMIUM_VIDEOS.map((v) => v.channelName).filter(Boolean))) as string[];
      const demo = [{ used: 3, limit: 5 }, { used: 1, limit: 5 }, { used: 5, limit: 5 }];
      setChannelCredits(names.map((n, i) => ({ youtubeChannelId: n, channelName: n, used: demo[i % 3].used, limit: demo[i % 3].limit, channelThumbnailUrl: null })));
      setChannelCap(names.length); // mock = maxed, so the "Need more channels?" link previews
      return;
    }
    getMyChannelCredits()
      .then((res) => {
        setChannelCredits(
          res.slots
            .filter((s) => s.channel)
            .map((s) => ({ youtubeChannelId: s.channel!.youtubeChannelId, channelName: s.channel!.channelName, used: s.used, limit: s.limit, channelThumbnailUrl: s.channel!.channelThumbnailUrl })),
        );
        setChannelCap(res.cap);
      })
      .catch(() => {});
    // Re-fetch when the claim set changes (a connect/disconnect bumps realClaims
    // via the parent's refreshClaims), so the dropdown updates without a reload.
  }, [forceMock, realClaims]);

  return (
    <>
      <SettingsCards
        autoApprove={false}
        onEnableAutoApprove={() => {}}
        onDisableAutoApprove={() => {}}
        creditsRemaining={7}
        creditsTotal={10}
      />
      <ThreeSectionList
        items={items}
        search=""
        sortKey="status"
        showUpgrade={false}
        onRefresh={onRefresh}
        onChannelLimitHit={onChannelLimitHit}
        dataLoaded={realClaims !== null}
        tabBarRightSlot={tabBarRightSlot}
        topAlerts={topAlerts}
        channelWarnings={channelWarnings}
        isPastDue={isPastDue}
        channelCredits={channelCredits}
        channelCap={channelCap}
        onChannelChange={onChannelChange}
        onChannelDisconnected={onChannelDisconnected}
        onManageSubscription={onManageSubscription}
      />
    </>
  );
};

/* ════════════════════════════════════════════
   MUSIC CREATOR VIEW (unchanged)
   ════════════════════════════════════════════ */

const MusicCreatorConnectedView: React.FC = () => {
  const [openCredit, setOpenCredit] = useState<string | null>(null);
  const [copiedCredit, setCopiedCredit] = useState<string | null>(null);
  const [submittedIds, setSubmittedIds] = useState<Set<string>>(new Set());
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [submittingCollapsingId, setSubmittingCollapsingId] = useState<string | null>(null);
  const [hidingId, setHidingId] = useState<string | null>(null);
  const [collapsingId, setCollapsingId] = useState<string | null>(null);

  const handleCreatorSubmit = (id: string) => {
    setSubmittingId(id);
    setTimeout(() => {
      setSubmittingId(null);
      setSubmittingCollapsingId(id);
      setTimeout(() => {
        setSubmittedIds((prev) => new Set(prev).add(id));
        setSubmittingCollapsingId(null);
      }, 300);
    }, 350);
  };
  const handleCreatorHide = (id: string) => {
    setHidingId(id);
    setTimeout(() => {
      setHidingId(null);
      setCollapsingId(id);
      setTimeout(() => {
        setHiddenIds((prev) => new Set(prev).add(id));
        setCollapsingId(null);
      }, 300);
    }, 350);
  };
  const [openTimestamps, setOpenTimestamps] = useState<string | null>(null);

  const sortedSplits = [...MOCK_CREATOR_SPLITS].sort((a, b) =>
    (b.submittedAt || "").localeCompare(a.submittedAt || ""),
  );

  const creatorActionRequired = MOCK_CREATOR_SUBMISSIONS.filter((s) => !submittedIds.has(s.id) && !hiddenIds.has(s.id));
  const creatorUnderReview = MOCK_CREATOR_SUBMISSIONS.filter((s) => submittedIds.has(s.id) && !hiddenIds.has(s.id));
  const creatorHidden = MOCK_CREATOR_SUBMISSIONS.filter((s) => hiddenIds.has(s.id));

  const creatorTabs = [
    { key: "action", label: "Action Required", count: creatorActionRequired.length, highlight: creatorActionRequired.length > 0 },
    { key: "review", label: "Under Review", count: creatorUnderReview.length, highlight: false },
    { key: "split", label: "Split", count: sortedSplits.length, highlight: false },
    { key: "hidden", label: "Hidden", count: creatorHidden.length, highlight: false },
  ];
  const creatorValidTabs = ["action", "review", "split", "hidden"] as const;
  const [creatorSearchParams, setCreatorSearchParams] = useSearchParams();
  const creatorUrlTab = creatorSearchParams.get("tab");
  const creatorFallback = creatorActionRequired.length > 0 ? "action" : creatorUnderReview.length > 0 ? "review" : "split";
  const creatorActiveTab: string = (creatorValidTabs as readonly string[]).includes(creatorUrlTab ?? "") ? (creatorUrlTab as string) : creatorFallback;
  const setCreatorActiveTab = useCallback((tab: string) => {
    setCreatorSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    }, { replace: true });
  }, [setCreatorSearchParams]);

  // Canonicalize a paramless URL → the resolved tab, so the header "Claims"
  // link (/account/claims) lands on an explicit ?tab= like every other entry
  // point. Respects deep links. Mock-backed, so no data-load gate is needed.
  const didPickCreatorTabRef = useRef(false);
  if (!didPickCreatorTabRef.current && !creatorUrlTab) {
    didPickCreatorTabRef.current = true;
    Promise.resolve().then(() => setCreatorActiveTab(creatorFallback));
  }

  return (
    <>
      <TabBarRenderer tabs={creatorTabs} activeTab={creatorActiveTab} setActiveTab={setCreatorActiveTab} />

      {/* Action Required */}
      {creatorActiveTab === "action" && (
        creatorActionRequired.length > 0 ? (
          <>
          {creatorActionRequired.map((sub) => (
            <HideWrapper key={sub.id} isFading={submittingId === sub.id || hidingId === sub.id} isCollapsing={submittingCollapsingId === sub.id || collapsingId === sub.id}>
            <SubmissionCard style={{ flexDirection: "column", gap: 0, alignItems: "stretch", position: "relative" }}>
              {/* Hide icon — top right corner */}
              <div
                style={{ position: "absolute", top: 14, right: 14, zIndex: 2 }}
                onMouseEnter={(e) => {
                  const btn = e.currentTarget.querySelector("button") as HTMLElement;
                  const tip = e.currentTarget.querySelector("[data-tooltip]") as HTMLElement;
                  if (btn) { btn.style.background = "rgba(235, 194, 142, 0.35)"; btn.style.borderColor = "rgba(235, 194, 142, 0.5)"; btn.style.opacity = "1"; }
                  if (tip) { tip.style.opacity = "1"; tip.style.transform = "translateX(-50%) translateY(0)"; }
                }}
                onMouseLeave={(e) => {
                  const btn = e.currentTarget.querySelector("button") as HTMLElement;
                  const tip = e.currentTarget.querySelector("[data-tooltip]") as HTMLElement;
                  if (btn) { btn.style.background = "rgba(235, 194, 142, 0.08)"; btn.style.borderColor = "rgba(235, 194, 142, 0.12)"; btn.style.opacity = "0.4"; }
                  if (tip) { tip.style.opacity = "0"; tip.style.transform = "translateX(-50%) translateY(4px)"; }
                }}
              >
                <button
                  onClick={() => handleCreatorHide(sub.id)}
                  style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: "rgba(235, 194, 142, 0.08)", border: "1px solid rgba(235, 194, 142, 0.12)",
                    color: "#fff", fontSize: "0.85rem", cursor: "pointer", opacity: 0.4,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.3s ease", padding: 0, lineHeight: 1,
                  }}
                >
                  <HiddenIcon />
                </button>
                <div
                  data-tooltip
                  style={{
                    position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%) translateY(4px)",
                    marginTop: 6, whiteSpace: "nowrap",
                    fontFamily: fonts.body, fontSize: fontSizes.xs, fontWeight: 500,
                    color: colors.text.primary, background: colors.surface.modal,
                    border: "1px solid rgba(235, 194, 142, 0.2)", borderRadius: "6px",
                    padding: "5px 10px", pointerEvents: "none",
                    opacity: 0, transition: "opacity 0.2s ease, transform 0.2s ease",
                  }}
                >
                  Move to Hidden
                </div>
              </div>
              {/* Top row: thumbnail + info + status badge */}
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <VideoThumb>
                  <img src={`https://img.youtube.com/vi/${getVideoId(sub.videoUrl)}/mqdefault.jpg`} alt={sub.videoTitle} />
                </VideoThumb>
                <SubmissionInfo>
                  <SubmissionTitle>{sub.videoTitle}</SubmissionTitle>
                  <SubmissionMeta>
                    Submitted {new Date(sub.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </SubmissionMeta>
                  {(sub.status === "Credit Text" || sub.status === "Timestamps" || sub.status === "Credit Text & Timestamps" || sub.status === "Private Video") && (
                    <span style={{
                      display: "block",
                      fontSize: fontSizes.body,
                      color: sub.status === "Private Video" ? "#f5a623" : "#f5a623",
                      marginTop: 6,
                      marginRight: 56,
                      lineHeight: 1.4,
                      paddingLeft: 10,
                      borderLeft: "2px solid rgba(245, 166, 35, 0.4)",
                    }}>
                      {sub.status === "Credit Text" && "Add the full credit text to your video description."}
                      {sub.status === "Timestamps" && "Add timestamps for each track to your video description."}
                      {sub.status === "Credit Text & Timestamps" && "Add credit text and timestamps to your video description."}
                      {sub.status === "Private Video" && "Your video is set to private. Please change its visibility to unlisted or public in YouTube Studio, then submit again."}
                    </span>
                  )}
                </SubmissionInfo>
                {(sub.status === "Reviewing" || sub.status === "Pending") && (
                  <SubmissionStatusBadge $status={sub.status} style={{ marginLeft: "auto" }}>
                    {sub.status === "Reviewing" && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    )}
                    {sub.status === "Pending" && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    )}
                    {sub.status}
                  </SubmissionStatusBadge>
                )}
              </div>

              {/* Action row with buttons on left, error ID + envelope on right */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 16, marginTop: 16, borderTop: "1px solid rgba(235, 194, 142, 0.06)" }}>
                {(sub.status === "Credit Text" || sub.status === "Credit Text & Timestamps") && (
                  <ActionBtn onClick={() => setOpenCredit(openCredit === sub.id ? null : sub.id)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    <span style={{ position: "relative" }}>
                      <span style={{ visibility: "hidden" }}>Show Credit Text</span>
                      <span style={{ position: "absolute", left: 0, top: 0 }}>{openCredit === sub.id ? "Hide Credit Text" : "Show Credit Text"}</span>
                    </span>
                  </ActionBtn>
                )}
                {(sub.status === "Timestamps" || sub.status === "Credit Text & Timestamps") && (
                  <ActionBtn onClick={() => setOpenTimestamps(openTimestamps === sub.id ? null : sub.id)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span style={{ position: "relative" }}>
                      <span style={{ visibility: "hidden" }}>Show Timestamps Example</span>
                      <span style={{ position: "absolute", left: 0, top: 0 }}>{openTimestamps === sub.id ? "Hide Timestamps Example" : "Show Timestamps Example"}</span>
                    </span>
                  </ActionBtn>
                )}
                {(sub.status === "Credit Text" || sub.status === "Timestamps" || sub.status === "Credit Text & Timestamps" || sub.status === "Private Video") && (
                  <ActionBtn $primary onClick={() => handleCreatorSubmit(sub.id)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13" />
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                    </svg>
                    Submit again
                  </ActionBtn>
                )}

                <div style={{ flex: 1 }} />
                {sub.eventSequentialId !== undefined && (
                  <EventIdPill id={sub.eventSequentialId} />
                )}
              </div>

              {/* Timestamps expand */}
              <SmoothExpand open={openTimestamps === sub.id}>
                <CreditTextBox style={{ width: "100%", marginTop: 14 }}>
                  <span style={{ color: "rgba(251, 244, 236, 0.4)" }}>For videos shorter than 1 hour:</span>
                  {"\n\n00:00 ARTIST NAME - TRACK TITLE\n08:48 ARTIST NAME - TRACK TITLE\n\n"}
                  <span style={{ color: "rgba(251, 244, 236, 0.4)" }}>For videos longer than 1 hour:</span>
                  {"\n\n0:52:37 ARTIST NAME - TRACK TITLE\n0:58:47 ARTIST NAME - TRACK TITLE\n1:03:16 ARTIST NAME - TRACK TITLE"}
                </CreditTextBox>
              </SmoothExpand>

              {/* Credit text expand */}
              <SmoothExpand open={openCredit === sub.id}>
                <CreditTextBox style={{ width: "100%", marginTop: 14, paddingBottom: 32 }}>
                  {CREATOR_CREDIT_TEXT}
                  <div style={{ marginTop: 18 }}>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(CREATOR_CREDIT_TEXT);
                        setCopiedCredit(sub.id);
                        setTimeout(() => setCopiedCredit(null), 2000);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = copiedCredit === sub.id ? "rgba(52, 211, 153, 0.25)" : "rgba(156, 211, 253, 0.25)";
                        e.currentTarget.style.borderColor = copiedCredit === sub.id ? "rgba(52, 211, 153, 0.6)" : "rgba(156, 211, 253, 0.6)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = copiedCredit === sub.id ? "rgba(52, 211, 153, 0.15)" : "rgba(156, 211, 253, 0.12)";
                        e.currentTarget.style.borderColor = copiedCredit === sub.id ? "rgba(52, 211, 153, 0.4)" : "rgba(156, 211, 253, 0.35)";
                      }}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "8px 16px", borderRadius: radius.md,
                        background: copiedCredit === sub.id ? "rgba(52, 211, 153, 0.15)" : "rgba(156, 211, 253, 0.12)",
                        border: `1px solid ${copiedCredit === sub.id ? "rgba(52, 211, 153, 0.4)" : "rgba(156, 211, 253, 0.35)"}`,
                        color: copiedCredit === sub.id ? colors.accent.green : colors.accent.blue,
                        fontFamily: fonts.body, fontSize: fontSizes.body, fontWeight: 500,
                        cursor: "pointer", transition: "all 0.2s ease",
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {copiedCredit === sub.id ? (
                          <polyline points="20 6 9 17 4 12" />
                        ) : (
                          <><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>
                        )}
                      </svg>
                      {copiedCredit === sub.id ? "Copied!" : "Copy Credit Text"}
                    </button>
                  </div>
                </CreditTextBox>
              </SmoothExpand>
            </SubmissionCard>
            </HideWrapper>
          ))}
          </>
        ) : (
          <S.EmptyState><S.EmptyText>No actions required at the moment.</S.EmptyText></S.EmptyState>
        )
      )}

      {/* Under Review */}
      {creatorActiveTab === "review" && (
        creatorUnderReview.length > 0 ? (
          <>
          {UnderReviewHint}
          {creatorUnderReview.map((sub) => (
            <FadeIn key={sub.id}>
              <SubmissionCard style={{ flexDirection: "column", gap: 0, alignItems: "stretch" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <VideoThumb>
                    <img src={`https://img.youtube.com/vi/${getVideoId(sub.videoUrl)}/mqdefault.jpg`} alt={sub.videoTitle} />
                  </VideoThumb>
                  <SubmissionInfo>
                    <SubmissionTitle>{sub.videoTitle}</SubmissionTitle>
                    <SubmissionMeta>
                      Submitted {new Date(sub.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </SubmissionMeta>
                  </SubmissionInfo>
                  <SubmissionStatusBadge $status="Analyzing" style={{ marginLeft: "auto" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    Under Review
                  </SubmissionStatusBadge>
                </div>
              </SubmissionCard>
            </FadeIn>
          ))}
          </>
        ) : (
          <S.EmptyState><S.EmptyText>No videos under review.</S.EmptyText></S.EmptyState>
        )
      )}

      {/* Split Videos */}
      {creatorActiveTab === "split" && (
        sortedSplits.length > 0 ? (
          <>
          {sortedSplits.map((split) => (
            <SubmissionCard key={split.id}>
              <VideoThumb>
                <img src={`https://img.youtube.com/vi/${getVideoId(split.videoUrl)}/mqdefault.jpg`} alt={split.videoTitle} />
              </VideoThumb>
              <SubmissionInfo>
                <SubmissionTitle>{split.videoTitle}</SubmissionTitle>
                <SubmissionMeta>
                  Split since {new Date(split.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </SubmissionMeta>
              </SubmissionInfo>
              <SubmissionStatusBadge $status="Split">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Split
              </SubmissionStatusBadge>
            </SubmissionCard>
          ))}
          </>
        ) : (
          <S.EmptyState><S.EmptyText>No split videos.</S.EmptyText></S.EmptyState>
        )
      )}

      {/* Hidden */}
      {creatorActiveTab === "hidden" && (
        creatorHidden.length > 0 ? (
          <>
          {creatorHidden.map((sub) => (
            <SubmissionCard key={sub.id}>
              <VideoThumb>
                <img src={`https://img.youtube.com/vi/${getVideoId(sub.videoUrl)}/mqdefault.jpg`} alt={sub.videoTitle} />
              </VideoThumb>
              <SubmissionInfo>
                <SubmissionTitle>{sub.videoTitle}</SubmissionTitle>
                <SubmissionMeta>
                  Submitted {new Date(sub.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </SubmissionMeta>
              </SubmissionInfo>
            </SubmissionCard>
          ))}
          </>
        ) : (
          <S.EmptyState><S.EmptyText>No hidden videos.</S.EmptyText></S.EmptyState>
        )
      )}
    </>
  );
};

/* ── Creator Onboarding Steps ── */

const ONBOARDING_STEPS = [
  {
    number: "1",
    title: "Fill Out Your Data",
    description: "Complete your profile with your name, email, and payment details.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    actionLabel: "Go to Account",
  },
  {
    number: "2",
    title: "Sign Contract",
    description: "Review and sign the Music Creator agreement to get started.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
    actionLabel: "View Contract",
  },
  {
    number: "3",
    title: "Create and/or Connect Your YouTube Channel(s)",
    description: "Add your YouTube channels so we can detect your videos and set up revenue splits.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" strokeWidth="0"><path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>,
    actionLabel: "Add Channel",
    hasChannels: true,
  },
  {
    number: "4",
    title: "Upload Your First Video",
    description: "Upload a video to YouTube using our music. Make sure to include credit text and timestamps.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>,
    actionLabel: "Browse Music",
  },
  {
    number: "5",
    title: "Sign Up to SongMate",
    description: "Link your account with our payment provider SongMate. You will be paid by SongMate for each split video.",
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>,
    actionLabel: "Sign Up Now",
  },
];

const SongMateLogo = () => (
  <svg viewBox="0 0 10600 2600" fill="none" style={{ width: "140px", height: "auto" }}>
    <g clipPath="url(#clip0_sm2)">
      <path d="M3790 1013.32C3710.44 1013.32 3654.28 1042.96 3654.28 1094.44C3654.28 1139.68 3687.04 1174 3766.6 1191.16L3878.92 1216.12C4045.84 1253.56 4187.8 1295.68 4187.8 1468.84C4187.8 1642 4014.64 1740.28 3824.32 1740.28C3618.4 1740.28 3462.4 1634.2 3442.12 1442.32H3649.6C3662.08 1523.44 3719.8 1584.28 3830.56 1584.28C3922.6 1584.28 3981.88 1549.96 3981.88 1489.12C3981.88 1428.28 3916.36 1401.76 3844.6 1386.16L3719.8 1359.64C3562.24 1326.88 3457.72 1261.36 3457.72 1108.48C3457.72 955.6 3619.96 858.88 3800.92 858.88C3981.88 858.88 4142.56 940 4170.64 1133.44H3969.4C3953.8 1063.24 3888.28 1013.32 3790 1013.32Z" fill="white" />
      <path d="M4678.82 862C4890.98 862 5100.02 1007.08 5100.02 1301.92C5100.02 1596.76 4890.98 1740.28 4678.82 1740.28C4466.66 1740.28 4257.62 1596.76 4257.62 1301.92C4257.62 1007.08 4466.66 862 4678.82 862ZM4678.82 1571.8C4797.38 1571.8 4881.62 1476.64 4881.62 1301.92C4881.62 1127.2 4797.38 1030.48 4678.82 1030.48C4560.26 1030.48 4476.02 1125.64 4476.02 1301.92C4476.02 1478.2 4560.26 1571.8 4678.82 1571.8Z" fill="white" />
      <path d="M5638.3 862C5839.54 862 5928.46 985.24 5928.46 1191.16V1720H5713.18V1237.96C5713.18 1128.76 5689.78 1035.16 5568.1 1035.16C5426.14 1035.16 5394.94 1150.6 5394.94 1283.2V1720H5179.66V880.72H5394.94V991.48C5443.3 907.24 5535.34 862 5638.3 862Z" fill="white" />
      <path d="M6840 1635.76C6840 1910.32 6685.56 2013.28 6428.16 2013.28C6248.76 2013.28 6089.64 1949.32 6061.56 1752.76H6265.92C6284.64 1822.96 6340.8 1855.72 6439.08 1855.72C6551.4 1855.72 6626.28 1802.68 6626.28 1676.32V1564C6571.68 1656.04 6473.4 1690.36 6386.04 1690.36C6178.56 1690.36 6021 1520.32 6021 1275.4C6021 1030.48 6175.44 862 6386.04 862C6492.12 862 6588.84 916.6 6626.28 989.92V880.72H6840V1635.76ZM6440.64 1025.8C6329.88 1025.8 6247.2 1136.56 6247.2 1276.96C6247.2 1417.36 6329.88 1526.56 6440.64 1526.56C6560.76 1526.56 6634.08 1423.6 6634.08 1275.4C6634.08 1127.2 6560.76 1025.8 6440.64 1025.8Z" fill="white" />
      <path d="M7686.82 1283.2V1720H7471.54V1231.72C7471.54 1139.68 7463.74 1035.16 7337.38 1035.16C7193.86 1035.16 7170.46 1155.28 7170.46 1283.2V1720H6955.18V880.72H7170.46V991.48C7217.26 908.8 7303.06 862 7404.46 862C7547.98 862 7610.38 938.44 7643.14 999.28C7708.66 905.68 7792.9 862 7919.26 862C8129.86 862 8203.18 993.04 8203.18 1191.16V1720H7987.9V1231.72C7987.9 1139.68 7980.1 1035.16 7853.74 1035.16C7710.22 1035.16 7686.82 1155.28 7686.82 1283.2Z" fill="white" />
      <path d="M8303 1493.8C8303 1295.68 8526.08 1236.4 8855.24 1198.96V1184.92C8855.24 1055.44 8785.04 1010.2 8697.68 1010.2C8615 1010.2 8546.36 1053.88 8541.68 1145.92H8340.44C8356.04 980.56 8493.32 858.88 8708.6 858.88C8908.28 858.88 9070.52 944.68 9070.52 1228.6C9070.52 1255.12 9067.4 1365.88 9067.4 1442.32C9067.4 1574.92 9076.76 1649.8 9092.36 1720H8894.24C8888 1693.48 8883.32 1656.04 8880.2 1615.48C8820.92 1702.84 8732 1740.28 8605.64 1740.28C8444.96 1740.28 8303 1646.68 8303 1493.8ZM8524.52 1482.88C8524.52 1543.72 8572.88 1593.64 8669.6 1593.64C8803.76 1593.64 8861.48 1520.32 8861.48 1398.64V1336.24C8621.24 1361.2 8524.52 1389.28 8524.52 1482.88Z" fill="white" />
      <path d="M9448.02 652.96V880.72H9622.74V1028.92H9448.02V1462.6C9448.02 1548.4 9477.66 1565.56 9554.1 1565.56H9619.62V1720H9462.06C9270.18 1720 9232.74 1660.72 9232.74 1493.8V1028.92H9114.18V880.72H9232.74V713L9448.02 652.96Z" fill="white" />
      <path d="M10084.5 1573.36C10150 1573.36 10217.1 1549.96 10260.8 1476.64H10482.3C10437.1 1601.44 10313.8 1740.28 10086.1 1740.28C9816.18 1740.28 9658.62 1539.04 9658.62 1297.24C9658.62 1055.44 9831.78 862 10079.8 862C10327.9 862 10501 1063.24 10491.7 1359.64H9872.34C9883.26 1503.16 9983.1 1573.36 10084.5 1573.36ZM10273.3 1211.44C10268.6 1069.48 10168.7 1019.56 10078.3 1019.56C9997.14 1019.56 9892.62 1071.04 9875.46 1211.44H10273.3Z" fill="white" />
      <path d="M2381.93 1100H2260C2190.55 1100 2125.26 1127.04 2076.15 1176.15C2027.04 1225.26 2000 1290.55 2000 1360V1540C2000 1573.08 1973.08 1600 1940 1600C1906.92 1600 1880 1573.08 1880 1540V760C1880 690.55 1852.96 625.26 1803.85 576.15C1754.74 527.04 1689.45 500 1620 500C1550.55 500 1485.26 527.04 1436.15 576.15C1387.04 625.26 1360 690.55 1360 760V1740C1360 1773.08 1333.08 1800 1300 1800C1266.92 1800 1240 1773.08 1240 1740V960C1240 890.55 1212.96 825.26 1163.85 776.15C1114.74 727.04 1049.45 700 980 700C910.55 700 845.26 727.04 796.15 776.15C747.04 825.26 720 890.55 720 960V1540C720 1573.08 693.08 1600 660 1600C626.92 1600 600 1573.08 600 1540V1360C600 1290.55 572.96 1225.26 523.85 1176.15C474.74 1127.04 409.45 1100 340 1100H218.07C232.4 1021.87 255.21 945.61 286.4 871.86C313.96 806.7 348.08 743.85 387.8 685.05C427.17 626.78 472.38 571.98 522.18 522.18C571.98 472.38 626.78 427.17 685.05 387.8C743.85 348.08 806.7 313.96 871.86 286.4C1007.41 229.07 1151.46 200 1300 200C1448.54 200 1592.59 229.07 1728.14 286.4C1793.3 313.96 1856.15 348.08 1914.95 387.8C1973.22 427.17 2028.01 472.38 2077.82 522.18C2127.62 571.98 2172.83 626.78 2212.2 685.05C2251.93 743.85 2286.04 806.7 2313.6 871.86C2317.55 881.2 2321.36 890.58 2325.04 900H2537.31C2525.73 864.21 2512.55 828.85 2497.79 793.95C2465.2 716.9 2424.87 642.59 2377.91 573.09C2331.42 504.27 2278.03 439.57 2219.23 380.76C2160.43 321.96 2095.72 268.57 2026.9 222.08C1957.4 175.13 1883.09 134.79 1806.04 102.2C1645.71 34.39 1475.46 0 1300 0C1124.54 0 954.29 34.39 793.95 102.2C716.9 134.79 642.59 175.12 573.09 222.08C504.27 268.57 439.57 321.96 380.76 380.76C321.96 439.56 268.57 504.27 222.08 573.09C175.13 642.59 134.79 716.9 102.2 793.95C34.39 954.29 0 1124.54 0 1300H340C373.08 1300 400 1326.92 400 1360V1540C400 1609.45 427.04 1674.74 476.15 1723.85C525.26 1772.96 590.55 1800 660 1800C729.45 1800 794.74 1772.96 843.85 1723.85C892.96 1674.74 920 1609.45 920 1540V960C920 926.92 946.92 900 980 900C1013.08 900 1040 926.92 1040 960V1740C1040 1809.45 1067.04 1874.74 1116.15 1923.85C1165.26 1972.96 1230.55 2000 1300 2000C1369.45 2000 1434.74 1972.96 1483.85 1923.85C1532.96 1874.74 1560 1809.45 1560 1740V760C1560 726.92 1586.92 700 1620 700C1653.08 700 1680 726.92 1680 760V1540C1680 1609.45 1707.04 1674.74 1756.15 1723.85C1805.26 1772.96 1870.55 1800 1940 1800C2009.45 1800 2074.74 1772.96 2123.85 1723.85C2172.96 1674.74 2200 1609.45 2200 1540V1360C2200 1326.92 2226.92 1300 2260 1300H2400C2400 1448.54 2370.93 1592.59 2313.6 1728.14C2286.04 1793.3 2251.92 1856.15 2212.2 1914.95C2172.83 1973.22 2127.62 2028.02 2077.82 2077.82C2028.02 2127.62 1973.22 2172.83 1914.95 2212.2C1856.15 2251.93 1793.3 2286.04 1728.14 2313.6C1592.59 2370.93 1448.54 2400 1300 2400C1151.46 2400 1007.41 2370.93 871.86 2313.6C806.7 2286.04 743.85 2251.92 685.05 2212.2C626.78 2172.83 571.98 2127.62 522.18 2077.82C472.38 2028.02 427.17 1973.22 387.8 1914.95C348.08 1856.15 313.96 1793.3 286.4 1728.14C255.21 1654.4 232.4 1578.13 218.07 1500H15.25C31.44 1605.04 60.49 1707.42 102.2 1806.05C134.79 1883.1 175.12 1957.41 222.08 2026.91C268.57 2095.73 321.96 2160.43 380.76 2219.24C439.56 2278.04 504.27 2331.43 573.09 2377.92C642.59 2424.87 716.9 2465.21 793.95 2497.8C954.29 2565.62 1124.54 2600 1300 2600C1475.46 2600 1645.71 2565.61 1806.05 2497.8C1883.1 2465.21 1957.41 2424.88 2026.91 2377.92C2095.73 2331.43 2160.43 2278.04 2219.24 2219.24C2278.05 2160.44 2331.43 2095.73 2377.92 2026.91C2424.87 1957.41 2465.21 1883.1 2497.8 1806.05C2565.62 1645.71 2600 1475.46 2600 1300C2600 1232.47 2594.89 1165.72 2584.75 1100H2381.92H2381.93Z" fill="#A250FC" />
    </g>
    <defs><clipPath id="clip0_sm2"><rect width="10600" height="2600" fill="white" /></clipPath></defs>
  </svg>
);

const OnboardingStep: React.FC<{ step: typeof ONBOARDING_STEPS[0]; isExpanded: boolean; onToggle: () => void }> = ({ step, isExpanded, onToggle }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isExpanded ? contentRef.current.scrollHeight : 0);
    }
  }, [isExpanded]);

  return (
    <div
      style={{
        background: isExpanded ? colors.surface.cardHover : colors.surface.card,
        border: `1px solid ${isExpanded ? "rgba(235, 194, 142, 0.35)" : colors.border.light}`,
        borderRadius: "12px",
        overflow: "hidden",
        transition: "border-color 0.3s ease, background 0.3s ease",
      }}
    >
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "16px 24px",
          cursor: "pointer",
        }}
      >
        <div style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "rgba(156, 211, 253, 0.1)",
          border: "1px solid rgba(156, 211, 253, 0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: colors.accent.blue,
          fontFamily: fonts.number,
          fontSize: fontSizes.sm,
          fontWeight: 700,
        }}>
          {step.number}
        </div>
        <div style={{ fontFamily: fonts.heading, fontSize: fontSizes.lg, fontWeight: 500, color: colors.text.heading, flex: 1 }}>
          {step.title}
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isExpanded ? colors.text.link : colors.text.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.3s ease, stroke 0.3s ease", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      <div
        ref={contentRef}
        style={{
          maxHeight: `${height}px`,
          overflow: "hidden",
          transition: "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div style={{ padding: "0 24px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontFamily: fonts.body, fontSize: fontSizes.body, color: "rgba(251, 244, 236, 0.7)", lineHeight: 1.6 }}>
            {step.description}
          </div>
          {step.number === "3" && <div style={{ marginBottom: 8 }}><ChannelsPanel /></div>}
          {step.number === "5" && (
            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", background: "rgba(255, 255, 255, 0.03)", borderRadius: "8px", border: "1px solid rgba(255, 255, 255, 0.06)" }}>
              <SongMateLogo />
            </div>
          )}
          {step.number !== "3" && (
            <div>
              <ActionBtn onClick={() => console.log(step.actionLabel)}>
                {step.icon}
                {step.actionLabel}
              </ActionBtn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MusicCreatorNotConnectedView: React.FC = () => {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <>
      <SectionHeading style={{ fontSize: fontSizes.body, letterSpacing: "3px", marginBottom: "20px" }}>Music Creator Onboarding</SectionHeading>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {ONBOARDING_STEPS.map((step) => (
          <OnboardingStep
            key={step.number}
            step={step}
            isExpanded={expanded === step.number}
            onToggle={() => setExpanded(expanded === step.number ? null : step.number)}
          />
        ))}
      </div>
    </>
  );
};

/* ════════════════════════════════════════════
   CLAIM WARNING BANNER (per-channel)
   ════════════════════════════════════════════ */
const ClaimWarningBanner: React.FC<{
  name: string;
  pct: number;
  used: number;
  limit: number;
  severity: "near" | "limit";
  /** ISO timestamp of the cycle-end date. Rendered as a localized date
      after the main copy ("Limit resets on June 21, 2026."). Null for
      mock data without a real cycle row. */
  cycleEnd: string | null;
  onNeedMore: () => void;
  onDismiss: () => void;
}> = ({ name, pct, used: _used, limit, severity, cycleEnd, onNeedMore, onDismiss }) => {
  // Enter/exit animation is owned by the <AnimatedBannerList> wrapper: the X
  // just removes this warning from the list (onDismiss → leaves `claimWarnings`)
  // and the wrapper fades + collapses it out. Same path as a data-driven
  // removal (channel disconnected, credits refreshed), so it animates the
  // same way no matter why the banner goes away.
  const isLimit = severity === "limit";
  // Limit-reached (100% used) is the strongest state — solid red, no
  // border, full-white text. Matches the past_due subscription banner's
  // tone (rgb(195, 75, 54)). "Near" (80–99%) stays as the translucent
  // amber warning so the two severities read at a glance.
  // Both states take the full treatment. The "near" state is a 10% tint, so
  // `bannerSurface` hands it a quieter texture — the same white lines land on
  // the page rather than on a colour that would mute them.
  const bg = isLimit ? "rgb(195, 75, 54)" : "rgba(245, 166, 35, 0.1)";
  const border = isLimit ? "none" : "1px solid rgba(245, 166, 35, 0.4)";
  const textColor = isLimit ? "rgba(255, 255, 255, 0.98)" : "rgba(255, 255, 255, 0.85)";
  const strongStyle: React.CSSProperties = { color: "#fff", letterSpacing: "0.2px" };
  const formattedResetDate = cycleEnd
    ? new Date(cycleEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;
  const text = isLimit
    ? (
      <>
        <strong style={strongStyle}>{name}</strong> ran out of monthly claims (
        <span style={{ fontWeight: 600, color: "#fff", letterSpacing: "0.2px" }}>{limit}</span>
        ).{formattedResetDate ? <> Limit resets on <span style={{ fontWeight: 600, color: "#fff", letterSpacing: "0.2px" }}>{formattedResetDate}</span>.</> : null}
      </>
    )
    : <><strong style={strongStyle}>{name}</strong> already used <span style={{ fontWeight: 600, color: "#fff", letterSpacing: "0.2px" }}>{pct}%</span> of its claims this month.</>;

  return (
    <div
      data-banner-surface
      style={{
        padding: "12px 16px",
        borderRadius: 12,
        ...bannerSurface(bg),
        border,
        marginBottom: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        fontFamily: fonts.number,
        fontSize: fontSizes.body,
        color: textColor,
      }}
    >
      <div style={{ flex: 1, lineHeight: 1.5 }}>
        {text}{" "}
        <span
          onClick={onNeedMore}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
          style={{ color: colors.accent.blue, cursor: "pointer", marginLeft: 4 }}
        >Need more?</span>
      </div>
      {/* Both severities are dismissible — the user can X the warning away
          (near OR at-limit). It re-surfaces on the next data refresh if the
          channel is still in the warning/limit zone. */}
      <button
        onClick={onDismiss}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255, 255, 255, 0.98)"; }}
        aria-label="Dismiss"
        style={{
          background: "transparent",
          border: "none",
          // 0.98, like every other dismiss on the site. It used to be 0.85 at
          // 0.55 opacity — an effective 47% white, which is why this one read
          // dimmer than the banner below it.
          color: "rgba(255, 255, 255, 0.98)",
          fontSize: "1rem",
          lineHeight: 1,
          cursor: "pointer",
          padding: 4,
          transition: "color 0.15s ease",
          flexShrink: 0,
        }}
      >&#x2715;</button>
    </div>
  );
};

/* ════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════ */

type SubBannerState = "active" | "cancelling" | "past_due" | "pending_downgrade";
export type DevWarningsState = "actual" | "none" | "near-1" | "limit-1" | "near-multi" | "mixed";

const LicensesTab: React.FC<{ connectionTab?: string; freePlanModalOpen?: boolean; onCloseFreePlanModal?: () => void; subBannerState?: SubBannerState; devWarningsOverride?: DevWarningsState; refreshTrigger?: number; tabBarRightSlot?: React.ReactNode; isPastDue?: boolean; onChannelsChanged?: () => void; channelCap?: number }> = ({ connectionTab, freePlanModalOpen, onCloseFreePlanModal, subBannerState = "active", devWarningsOverride = "actual", refreshTrigger = 0, tabBarRightSlot, isPastDue = false, onChannelsChanged, channelCap }) => {
  const { accountType } = useAccountType();
  // Real claims + pending submissions from GET /api/claims/my. Null = not
  // yet fetched (show mocks). Empty array = fetched, user has no real
  // cards (still show mocks so dev preview works). Non-empty = use real
  // data and merge any fresh submissions from this session. See §19.12 5c.
  const [realClaims, setRealClaims] = useState<VideoItem[] | null>(null);
  // Channels the user just disconnected — their claims are hidden optimistically
  // so the list fades them out at once, and stay hidden until a refetch confirms
  // the (event-driven) backend delete, avoiding a reappear-flicker in between.
  const [removedChannelIds, setRemovedChannelIds] = useState<Set<string>>(new Set());
  // Live per-channel credit usage for the current cycle. Powers the
  // claim-warning banners further down. Declared before refreshClaims
  // so the parallel-refetch can write to the setter from inside the
  // callback (use-before-define lint rule).
  const [realChannelCredits, setRealChannelCredits] = useState<Array<{ youtubeChannelId: string; channelName: string | null; used: number; limit: number; cycleEnd: string | null }>>([]);
  // Standalone credits refetch — called when a manual release just pushed
  // a channel to atLimit (backend returns `channelHitLimit: true`) so the
  // 100% banner appears immediately rather than waiting for the full
  // claims refresh that runs at the end of the 4250ms resolve animation.
  // Doesn't touch the claims list to avoid re-rendering cards mid-animation.
  const refreshChannelCredits = useCallback(async () => {
    try {
      const credits = await getMyChannelCredits();
      setRealChannelCredits(slotsToChannelCredits(credits.slots));
    } catch {
      // Best-effort — the end-of-animation refreshClaims will pick it up.
    }
  }, []);
  const refreshClaims = useCallback(async () => {
    try {
      // Refetch claims + per-channel credits in parallel so the
      // claim-warning banners (powered by `realChannelCredits`) update
      // in lockstep with the card list. Otherwise a resolve that just
      // pushed the channel to atLimit would flip individual cards but
      // leave the channel-level warning banner stale until the next
      // hard reload.
      const [rows, credits] = await Promise.all([
        getMyClaims(),
        getMyChannelCredits().catch(() => ({ slots: [] as ChannelSlot[], cap: 0, connectedChannelCount: 0 })),
      ]);
      setRealClaims(rows.map(dtoToVideoItem));
      setRealChannelCredits(slotsToChannelCredits(credits.slots));
    } catch {
      setRealClaims(null);
    }
  }, []);
  useEffect(() => {
    void refreshClaims();
  }, [refreshClaims]);
  // Refetch on tab revisit. Card changes are real-time in the acting tab, but a
  // tab left open elsewhere (another tab, or another device) would otherwise keep
  // showing stale Release buttons after a claim is resolved — the 15s banner poll
  // only refreshes the freshness COUNT, not the card list. Refetching whenever the
  // document becomes visible reconciles button/state on every revisit, on any
  // device. visibilitychange also fires on bfcache restore (back/forward nav).
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void refreshClaims();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshClaims]);
  // Connecting a channel re-queues its claims; the runner re-checks them in the
  // background (async, a few seconds each). A single reload would catch only the
  // stale statuses, forcing a manual Refresh — but Refresh is for surfacing NEW
  // claim cards, not status changes on cards already on screen. So after a channel
  // change we poll the list across a short window so re-checked statuses (e.g.
  // Not Covered → Ready to Release) update on their own.
  const pollClaimsForReChecks = useCallback(() => {
    RECHECK_POLL_MS.forEach((d) => window.setTimeout(() => { void refreshClaims(); }, d));
  }, [refreshClaims]);
  // Optimistically hide a just-disconnected channel's claims (list fades them
  // out together). The actual disconnect + refetch run via the panel's
  // onChannelChange right after.
  const handleChannelDisconnected = useCallback((youtubeChannelId: string) => {
    setRemovedChannelIds((prev) => {
      const next = new Set(prev);
      next.add(youtubeChannelId);
      return next;
    });
  }, []);
  // Release the optimistic hide once a refetch no longer returns that channel's
  // claims (backend delete confirmed) — until then they stay hidden.
  useEffect(() => {
    if (!realClaims) return;
    setRemovedChannelIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(realClaims.map((c) => c.channelId).filter(Boolean) as string[]);
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [realClaims]);
  // Re-fetch claims when the parent bumps `refreshTrigger` (e.g. after the
  // auto-resolve toggle flips Ready-to-Release cards to Whitelisted).
  // Cheaper than a full window reload and keeps dev-switcher state intact.
  useEffect(() => {
    if (refreshTrigger > 0) void refreshClaims();
  }, [refreshTrigger, refreshClaims]);
  const [pendingTimeoutDays, setPendingTimeoutDays] = useState<number>(7);
  useEffect(() => {
    // Fetch the admin-configured Pending Submission Timeout once on mount.
    // Silently falls back to 7 if the endpoint errors or 404s on older backends.
    fetch("/api/claims/sync/config", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cfg && typeof cfg.pendingSubmissionTimeoutDays === "number") {
          setPendingTimeoutDays(cfg.pendingSubmissionTimeoutDays);
        }
      })
      .catch(() => {});
  }, []);
  const isConnected = connectionTab === "connected";
  const isNotConnected = connectionTab === "not-connected";
  const hasChannelTiers = accountType !== AccountType.FREE;
  const [channelManageOpen, setChannelManageOpen] = useState(false);
  const isSubscriber = accountType === AccountType.SUBSCRIPTION;

  // Per-channel claim warning banners. Dismissals are session-only.
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("dismissed-claim-warnings") || "[]")); }
    catch { return new Set(); }
  });
  const dismissWarning = (id: string) => {
    setDismissedWarnings((prev) => {
      const next = new Set(prev);
      next.add(id);
      sessionStorage.setItem("dismissed-claim-warnings", JSON.stringify(Array.from(next)));
      return next;
    });
  };

  // (`realChannelCredits` is declared at the top of the component alongside
  // `realClaims` — refreshed via `refreshClaims` so the warning banners
  // update in lockstep with the card list. The legacy MOCK_CHANNEL_CLAIMS
  // fallback for design-tester URLs still lights up when no real
  // channels are connected.)

  type ClaimWarning = { id: string; name: string; pct: number; used: number; limit: number; severity: "limit" | "near"; cycleEnd: string | null };
  // Dev preview cycle-end — 30 days out from "now" so the limit banner
  // copy can render the reset-date suffix even without a real backend.
  const devCycleEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  const buildDevWarnings = (): ClaimWarning[] => {
    switch (devWarningsOverride) {
      case "none": return [];
      case "near-1": return [{ id: "dev-near-1", name: "Ambient Worlds", pct: 90, used: 9, limit: 10, severity: "near", cycleEnd: devCycleEnd }];
      case "limit-1": return [{ id: "dev-limit-1", name: "Sleep Therapy", pct: 100, used: 10, limit: 10, severity: "limit", cycleEnd: devCycleEnd }];
      case "near-multi": return [
        { id: "dev-near-a", name: "Ambient Worlds", pct: 85, used: 13, limit: 15, severity: "near", cycleEnd: devCycleEnd },
        { id: "dev-near-b", name: "Sleep Therapy", pct: 93, used: 14, limit: 15, severity: "near", cycleEnd: devCycleEnd },
        { id: "dev-near-c", name: "Forest Sounds", pct: 87, used: 13, limit: 15, severity: "near", cycleEnd: devCycleEnd },
      ];
      case "mixed": return [
        { id: "dev-mix-near", name: "Ambient Worlds", pct: 90, used: 9, limit: 10, severity: "near", cycleEnd: devCycleEnd },
        { id: "dev-mix-limit", name: "Sleep Therapy", pct: 100, used: 10, limit: 10, severity: "limit", cycleEnd: devCycleEnd },
      ];
      default: return [];
    }
  };

  // Source of truth for the "actual" warnings is the live credit ledger
  // from GET /claims/my/channel-credits. Falls back to MOCK_CHANNEL_CLAIMS
  // (keyed by demo URLs) so the design-tester URLs still surface banners
  // when no real channels are connected.
  const buildActualWarnings = (): ClaimWarning[] => {
    const fromReal: ClaimWarning[] = realChannelCredits
      .map((c) => {
        if (!c.limit) return null;
        const pct = Math.round((c.used / c.limit) * 100);
        if (pct < 80) return null;
        const name = c.channelName || c.youtubeChannelId;
        const id = `${c.youtubeChannelId}-${pct >= 100 ? "limit" : "near"}`;
        if (dismissedWarnings.has(id)) return null;
        return { id, name, pct, used: c.used, limit: c.limit, severity: pct >= 100 ? "limit" as const : "near" as const, cycleEnd: c.cycleEnd };
      })
      .filter((w): w is ClaimWarning => w !== null);
    if (fromReal.length > 0) return fromReal;
    return Object.entries(MOCK_CHANNEL_CLAIMS)
      .map(([channelUrl, cl]) => {
        const pct = Math.round((cl.used / cl.limit) * 100);
        if (pct < 80) return null;
        const name = getChannelName(channelUrl);
        const id = `${channelUrl}-${pct >= 100 ? "limit" : "near"}`;
        if (dismissedWarnings.has(id)) return null;
        return { id, name, pct, used: cl.used, limit: cl.limit, severity: pct >= 100 ? "limit" as const : "near" as const, cycleEnd: devCycleEnd };
      })
      .filter((w): w is ClaimWarning => w !== null);
  };

  const claimWarnings: ClaimWarning[] = (isSubscriber && isConnected)
    ? (devWarningsOverride !== "actual"
        ? buildDevWarnings().filter((w) => !dismissedWarnings.has(w.id))
        : buildActualWarnings())
    : [];

  // (subscription banner state is now rendered via the layout's contentBanner slot in index.tsx)
  void subBannerState;

  // In-page "X new claims detected — Refresh" banner state.
  // Hook is bound to LicensesTab lifetime (mounts when the user opens
  // /account/claims, unmounts when they leave) so the 5-min slow poll
  // only runs on this page.
  const { newClaimsCount: pendingRefreshCount, refresh: refreshBannerRefresh } = useClaimsRefreshBanner();
  // New model: no "Refresh" banner. When the slow poll detects new claims, pull
  // them into the list automatically — they arrive flagged `isNew` so they show
  // the green "New" badge + bump the picker counts. `refreshBannerRefresh` resets
  // the old server watermark so this fires once per fresh batch, not every poll.
  const refreshBannerRefreshRef = useRef(refreshBannerRefresh);
  refreshBannerRefreshRef.current = refreshBannerRefresh;
  useEffect(() => {
    if (pendingRefreshCount > 0) {
      refreshBannerRefreshRef.current();
      void refreshClaims();
    }
  }, [pendingRefreshCount, refreshClaims]);

  // Built once and passed into PremiumView (which forwards it to
  // ThreeSectionList's `topAlerts` slot so banners render between the
  // tab bar and the cards list, not above the tabs). The refresh banner
  // (if any) renders ABOVE the per-channel limit warnings since it's
  // a stronger call-to-action ("new claims need you NOW"). Each
  // ClaimWarningBanner owns its own fade-out + collapse animation on
  // dismiss — the wrapper just provides bottom spacing.
  // Two separate banner stacks now:
  //  1. Per-channel monthly claim-limit warnings → ABOVE the channel picker
  //     (the `channelWarnings` slot), right below the page heading, since they
  //     describe the channels' monthly allotment.
  //  2. The blue "X new claims — Refresh" banner → BELOW the tab bar, right
  //     above the cards it populates (the `topAlerts` slot).
  // AnimatedBannerList stays mounted even when empty so the LAST banner can
  // still animate out (e.g. the sole at-limit warning collapsing when its
  // channel is disconnected) — a null gate would unmount it before the exit.
  const channelWarningItems: BannerPresenceItem[] = claimWarnings.map((w) => ({
    key: w.id,
    node: (
      <ClaimWarningBanner
        name={w.name}
        pct={w.pct}
        used={w.used}
        limit={w.limit}
        severity={w.severity}
        cycleEnd={w.cycleEnd}
        onNeedMore={() => setChannelManageOpen(true)}
        onDismiss={() => dismissWarning(w.id)}
      />
    ),
  }));
  // The old "X new claims — Refresh" banner is retired in favour of the green
  // "New" badges + per-channel picker counts. New claims now pull into the list
  // automatically (see the effect below), so this slot stays empty.
  const refreshItems: BannerPresenceItem[] = [];
  // Below the channel picker — per-channel monthly limit warnings ("X already
  // used Y% of its claims"). A positive top margin gives clear separation from
  // the picker above it. Gated on presence so an empty slot adds no phantom space.
  const hasChannelWarnings = channelWarningItems.length > 0;
  const channelWarningsNode = (
    <div style={hasChannelWarnings ? { marginTop: 16, marginBottom: -8 } : undefined}>
      <AnimatedBannerList items={channelWarningItems} />
    </div>
  );
  // Below the tab bar, above the cards — the "new claims — Refresh" banner.
  const topAlertsNode = (
    <div>
      <AnimatedBannerList items={refreshItems} />
    </div>
  );

  return (
    <AccountSuspendedContext.Provider value={(realClaims ?? []).some((c) => !!c.accountSuspended)}>
    <ClaimsConfigContext.Provider value={{ pendingTimeoutDays }}>
      {/* Not connected (first-connect) state: banner + add-channels panel.
          The wrapper adds the standard top gap below the tinted "Claims" header
          band so the banner clears it instead of overlapping (the banner's own
          -12 then just tightens that gap). The panel is shown inline (always
          open) right under the banner — self-contained, no header toggle/arrow. */}
      {hasChannelTiers && isNotConnected && (
        <div style={{ marginTop: 24 }}>
          <ConnectChannelBanner multiple={(channelCap ?? 0) > 1} hideArrow />
          <AnimatedChannelsPanel open empty onManageSubscription={() => setChannelManageOpen(true)} onChannelChange={() => { pollClaimsForReChecks(); onChannelsChanged?.(); }} onChannelDisconnected={handleChannelDisconnected} isPastDue={isPastDue} accountSuspended={(realClaims ?? []).some((c) => !!c.accountSuspended)} />
        </div>
      )}

      {accountType === AccountType.FREE && <FreeView planModalOpen={freePlanModalOpen} onClosePlanModal={onCloseFreePlanModal} realClaims={realClaims} onRefresh={refreshClaims} tabBarRightSlot={tabBarRightSlot} />}
      {accountType === AccountType.SUBSCRIPTION && isConnected && <PremiumView realClaims={realClaims === null ? null : realClaims.filter((c) => !c.channelId || !removedChannelIds.has(c.channelId))} onRefresh={refreshClaims} onChannelLimitHit={refreshChannelCredits} tabBarRightSlot={tabBarRightSlot} topAlerts={topAlertsNode} channelWarnings={channelWarningsNode} isPastDue={isPastDue} onChannelChange={() => { pollClaimsForReChecks(); onChannelsChanged?.(); }} onChannelDisconnected={handleChannelDisconnected} onManageSubscription={() => setChannelManageOpen(true)} />}
      {accountType === AccountType.MUSIC_CREATOR && isConnected && <MusicCreatorConnectedView />}
      {accountType === AccountType.MUSIC_CREATOR && isNotConnected && <MusicCreatorNotConnectedView />}

      <ManageAddonsModal
        open={channelManageOpen}
        onClose={() => setChannelManageOpen(false)}
        currentChannels={2}
        currentClaims={15}
        billingPeriod="monthly"
        daysLeftInPeriod={15}
        periodEndDate="2026-05-10"
      />

    </ClaimsConfigContext.Provider>
    </AccountSuspendedContext.Provider>
  );
};

export default LicensesTab;
